// ==============================
// Part 1 / 2
// ==============================
/* eslint-disable react/function-component-definition */
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import {
  Modal,
  Box,
  Menu,
  Select,
  MenuItem,
  Typography,
  Button,
  TextField,
  useTheme,
  useMediaQuery,
  Checkbox,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import LoadingScreen from "../loading/loadingscreen";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useTallysheetData, { parseNumber, formatNumber } from "./data/TallySheetData";
import Swal from "sweetalert2";
import api from "api/api";
import PropTypes from "prop-types";
import { API_BASE_URL } from "config";
import ExcelJS from "exceljs";
import DownloadIcon from "@mui/icons-material/Download";
import PreviewOverlay from "utils/PreviewOverlay";

// 읽기 전용 메모 칸: 클릭(포커스)해도 기본 테두리 유지
const READ_ONLY_NOTE_SX = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d2d6da",
    borderWidth: 1,
  },
};

const SWAL_LOADING_CUSTOM_CLASS = {
  popup: "tally-swal-loading-popup",
  confirmButton: "tally-swal-hidden-confirm",
};

const ensureSwalLoadingStyle = () => {
  if (typeof document === "undefined" || document.getElementById("tally-swal-loading-style")) return;
  const style = document.createElement("style");
  style.id = "tally-swal-loading-style";
  style.textContent = `
    .tally-swal-loading-popup .tally-swal-hidden-confirm {
      display: none !important;
    }
    .tally-swal-loading-popup .swal2-loader {
      display: flex !important;
    }
  `;
  document.head.appendChild(style);
};

const showSwalLoadingWithoutConfirm = () => {
  ensureSwalLoadingStyle();
  Swal.showLoading(Swal.getConfirmButton());
};

/**
 * ✅✅ IMPORTANT
 * - type=1000: 현재 구성(법인카드) 기능 그대로 유지
 * - type=1008: "현금/카드" + (현금이면 "현금영수증 유형") 포함 모달 추가, 저장/조회 endpoint는 별도
 * - type != 1 인 셀: 테이블에서 직접 입력(contentEditable) 불가, 모달로만 입력/수정
 *
 * ✅ type=1008 endpoint는 아래 상수만 실제 서버에 맞게 바꿔주세요.
 */

// ======================== ✅ type=1008 전용 endpoint (프로젝트에 맞게 교체) ========================
const ENDPOINT_CASH_SAVE = "/receipt-scanV4"; // TODO: 실제 저장 endpoint로 변경
const ENDPOINT_CASH_LIST = "/Account/AccountPurchaseTallyPaymentList"; // TODO: 실제 목록 조회 endpoint로 변경

// ======================== ✅ 기타 type(1000/1008/1~4 제외) 공통 endpoint ========================
// - type 1002/1003 제외 나머지: ENDPOINT_OTHER_SAVE(저장), ENDPOINT_OTHER_LIST(목록조회)
const ENDPOINT_OTHER_SAVE = "/receipt-scan";
const ENDPOINT_OTHER_LIST = "/Account/AccountPurchaseTallyPaymentList";

// ======================== ✅ type=1002, 1003 전용 endpoint ========================
// - 조회: /Account/HeadOfficeCorporateCardPaymentList
// - 저장: /Corporate/receipt-scan
const ENDPOINT_CORP_CARD_SAVE = "/Corporate/receipt-scan";
const ENDPOINT_CORP_CARD_LIST = "/Account/HeadOfficeCorporateCardPaymentList";

const isPdfFileLike = (value) => {
  if (!value) return false;
  if (typeof value === "object") {
    const type = String(value.type || "").toLowerCase();
    const name = String(value.name || "").toLowerCase();
    return type.includes("pdf") || name.endsWith(".pdf");
  }
  const s = String(value).toLowerCase();
  return s.startsWith("data:application/pdf") || /\.pdf($|[?#&])/i.test(s);
};

const extractDisplayFileName = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value.name || "");
  const raw = String(value).split("?")[0].split("#")[0];
  const idx = raw.lastIndexOf("/");
  return decodeURIComponent(idx >= 0 ? raw.slice(idx + 1) : raw);
};

const normalizeStoredPreviewPath = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(raw)) return raw;

  const normalized = raw.replace(/\\/g, "/");
  const lower = normalized.toLowerCase();
  const imageIdx = lower.indexOf("/image/");
  if (imageIdx >= 0) return normalized.slice(imageIdx);
  if (lower.startsWith("image/")) return `/${normalized}`;
  return normalized;
};

const extractCorpCardSaleIdFromPath = (value) => {
  const normalized = normalizeStoredPreviewPath(value);
  const matched = normalized.match(/\/image\/acnCorporate\/([^/]+)/i);
  return matched?.[1] || "";
};

const getCorpCardSaleId = (row, originRow = {}) => (
  String(
    row?.sale_id ||
    row?.saleId ||
    originRow?.sale_id ||
    originRow?.saleId ||
    extractCorpCardSaleIdFromPath(row?.receipt_image || row?.receiptImage) ||
    extractCorpCardSaleIdFromPath(originRow?.receipt_image || originRow?.receiptImage) ||
    ""
  )
);

const getPreviewFileKind = (value) => {
  if (isPdfFileLike(value)) return "pdf";
  return "image";
};

// ======================== ✅ 상단 예산/사용/비율 표시 바 ========================
function BudgetSummaryBar({ budget, used, suppliesBudget = 0, suppliesUsed = 0, title = "예산 현황", monthText }) {
  const safeBudget = parseNumber(budget);
  const safeUsed = parseNumber(used);
  const safeSuppliesBudget = parseNumber(suppliesBudget);
  const safeSuppliesUsed = parseNumber(suppliesUsed);

  const foodRatio = useMemo(() => {
    if (!safeBudget || safeBudget <= 0) return 0;
    return (safeUsed / safeBudget) * 100;
  }, [safeBudget, safeUsed]);

  const suppliesRatio = useMemo(() => {
    if (!safeSuppliesBudget || safeSuppliesBudget <= 0) return 0;
    return (safeSuppliesUsed / safeSuppliesBudget) * 100;
  }, [safeSuppliesBudget, safeSuppliesUsed]);

  const foodRatioColor = foodRatio >= 100 ? "#f44336" : foodRatio >= 90 ? "#ff9800" : undefined;
  const suppliesRatioColor = suppliesRatio >= 100 ? "#f44336" : suppliesRatio >= 90 ? "#ff9800" : undefined;

  const monthLabel = monthText ?? dayjs().format("MM월");

  const renderRow = (cells, borderTop = false) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        borderTop: borderTop ? "1px solid #111" : "none",
      }}
    >
      {cells.map((it) => (
        <Box
          key={it.label}
          sx={{
            px: 0.5,
            py: 0.5,
            borderRight: "1px solid #111",
            "&:last-of-type": { borderRight: "none" },
            bgcolor: it.bg || "#fff",
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: it.color || "#444" }}>
            {it.label} : {it.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", border: "1px solid #111", borderRadius: 1, overflow: "hidden" }}>
      {/* 헤더 */}
      <Box
        sx={{
          px: 0.5,
          py: 0.5,
          bgcolor: "#288ebe",
          borderBottom: "1px solid #111",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 900, color: "#fff" }}>{title}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{monthLabel}</Typography>
      </Box>

      {/* 식자재 행 */}
      {renderRow([
        { label: "식자재 예산", value: formatNumber(safeBudget) },
        { label: "식자재 사용금액", value: formatNumber(safeUsed) },
        { label: "예산대비", value: `${foodRatio.toFixed(2)}%`, bg: foodRatioColor, color: foodRatioColor ? "#fff" : "#444" },
      ])}

      {renderRow([
        { label: "소모품 예산", value: formatNumber(safeSuppliesBudget) },
        { label: "소모품 사용금액", value: formatNumber(safeSuppliesUsed) },
        { label: "예산대비", value: `${suppliesRatio.toFixed(2)}%`, bg: suppliesRatioColor, color: suppliesRatioColor ? "#fff" : "#444" },
      ], true)}
    </Box>
  );
}

BudgetSummaryBar.propTypes = {
  budget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  suppliesBudget: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  suppliesUsed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  monthText: PropTypes.string,
};

// ======================== 은행/포맷 유틸 ========================
const KOREAN_BANKS = [
  "KB국민은행",
  "신한은행",
  "우리은행",
  "하나은행",
  "IBK기업은행",
  "NH농협은행",
  "수협은행",
  "KDB산업은행",
  "SC제일은행",
  "씨티은행",
  "카카오뱅크",
  "토스뱅크",
  "케이뱅크",
  "우체국",
  "새마을금고",
  "신협",
  "저축은행",
  "부산은행",
  "대구은행",
  "광주은행",
  "전북은행",
  "경남은행",
  "제주은행",
  "기타(직접입력)",
];

const onlyDigits = (v = "") => String(v).replace(/\D/g, "");

const formatByGroups = (digits, groups) => {
  let idx = 0;
  const parts = [];
  for (const g of groups) {
    if (digits.length <= idx) break;
    parts.push(digits.slice(idx, idx + g));
    idx += g;
  }
  if (digits.length > idx) parts.push(digits.slice(idx));
  return parts.filter(Boolean).join("-");
};

const BANK_MASKS_BY_NAME = {
  KB국민은행: [
    [3, 2, 6],
    [3, 3, 6],
  ],
  신한은행: [
    [3, 3, 6],
    [3, 2, 6],
  ],
  우리은행: [
    [4, 3, 6],
    [3, 3, 6],
  ],
  하나은행: [
    [3, 6, 5],
    [3, 3, 6],
  ],
  IBK기업은행: [
    [3, 6, 2, 3],
    [3, 3, 6],
  ],
  NH농협은행: [
    [3, 4, 4, 2],
    [3, 3, 6],
  ],
  카카오뱅크: [
    [4, 2, 7],
    [3, 3, 6],
  ],
  토스뱅크: [
    [3, 3, 6],
    [4, 3, 6],
  ],
  케이뱅크: [
    [3, 3, 6],
    [4, 2, 7],
  ],
  우체국: [
    [4, 4, 4],
    [3, 3, 6],
  ],
};

const pickBestMask = (bankName, len) => {
  const masks = BANK_MASKS_BY_NAME[bankName] || [];
  if (!masks.length) return null;

  let best = masks[0];
  let bestScore = Infinity;
  for (const m of masks) {
    const sum = m.reduce((a, b) => a + b, 0);
    const score = Math.abs(sum - len);
    if (score < bestScore) {
      bestScore = score;
      best = m;
    }
  }
  return best;
};

const formatAccountNumber = (bankName, value) => {
  const digits = onlyDigits(value).slice(0, 16);
  const mask = pickBestMask(bankName, digits.length);

  if (mask) return formatByGroups(digits, mask);

  if (digits.length <= 9) return formatByGroups(digits, [3, 3, 3]);
  if (digits.length <= 12) return formatByGroups(digits, [3, 3, 6]);
  return formatByGroups(digits, [4, 4, 4, 4]);
};

const formatBizNo = (value) => {
  const digits = onlyDigits(value).slice(0, 10);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 5);
  const c = digits.slice(5, 10);
  if (digits.length <= 3) return a;
  if (digits.length <= 5) return `${a}-${b}`;
  return `${a}-${b}-${c}`;
};

const formatPhone = (value) => {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.startsWith("02")) {
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  if (digits.startsWith("0505")) {
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

// ======================== 선택 테이블 컴포넌트 ========================
function YourSelectableTable({ data, selected, setSelected, enableSorting = false }) {
  // 거래처 연결 후보 목록의 정렬 기준과 방향을 관리한다.
  const [sortConfig, setSortConfig] = useState({ key: "type", direction: "asc" });

  const sortedData = useMemo(() => {
    const source = Array.isArray(data) ? data : [];
    if (!enableSorting) return source;

    return source
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const left = String(a.row?.[sortConfig.key] ?? "");
        const right = String(b.row?.[sortConfig.key] ?? "");
        const compared = left.localeCompare(right, "ko", { numeric: true, sensitivity: "base" });
        if (compared === 0) return a.index - b.index;
        return sortConfig.direction === "asc" ? compared : -compared;
      })
      .map(({ row }) => row);
  }, [data, enableSorting, sortConfig]);

  const getSortDirectionMark = (key) => {
    if (!enableSorting || sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { ...prev, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const toggleSelect = (item) => {
    const index = selected.findIndex((i) => JSON.stringify(i) === JSON.stringify(item));
    if (index !== -1) setSelected(selected.filter((_, idx) => idx !== index));
    else setSelected([...selected, item]);
  };

  const isSelected = (item) => selected.some((i) => JSON.stringify(i) === JSON.stringify(item));

  const tableSx = {
    maxHeight: "550px",
    overflow: "auto",
    "& table": { borderCollapse: "collapse", width: "100%", minWidth: "100%", borderSpacing: 0 },
    "& th, & td": {
      border: "1px solid #686D76",
      textAlign: "center",
      padding: "4px",
      whiteSpace: "nowrap",
      fontSize: "12px",
    },
    "& th": { backgroundColor: "#f0f0f0", position: "sticky", top: 0, zIndex: 2 },
  };

  return (
    <Box sx={tableSx}>
      <table>
        <thead>
          <tr>
            <th>선택</th>
            <th
              style={enableSorting ? { cursor: "pointer", userSelect: "none" } : undefined}
              onClick={() => enableSorting && toggleSort("name")}
            >
              {`이름${getSortDirectionMark("name")}`}
            </th>
            <th
              style={enableSorting ? { cursor: "pointer", userSelect: "none" } : undefined}
              onClick={() => enableSorting && toggleSort("type")}
            >
              {`타입${getSortDirectionMark("type")}`}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => (
            <tr
              key={idx}
              style={{
                background: isSelected(row) ? "#d3f0ff" : row.del_yn === "Y" ? "#E0E0E0" : "white",
              }}
            >
              <td>
                <input
                  type="checkbox"
                  checked={isSelected(row)}
                  onChange={() => toggleSelect(row)}
                />
              </td>
              <td>{row.name}</td>
              <td>{row.type}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

YourSelectableTable.propTypes = {
  data: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  setSelected: PropTypes.func.isRequired,
  enableSorting: PropTypes.bool,
};

// ======================== 메인 집계표 컴포넌트 ========================
function TallySheet() {
  const localAccountId = useMemo(() => localStorage.getItem("account_id") || "", []);
  const localUserId = useMemo(() => localStorage.getItem("user_id") || "", []);
  // ✅ 우클릭 "입력가능/불가" 메뉴 권한:
  //    부서(0:대표, 2:회계팀, 6:개발팀) 또는 직책(0:대표, 1:팀장) 중 하나라도 맞으면 허용(OR)
  const localDepartmentCode = useMemo(() => {
    const rawDepartment = localStorage.getItem("department");
    if (rawDepartment == null || String(rawDepartment).trim() === "") return null;
    const parsedDepartment = Number(rawDepartment);
    return Number.isFinite(parsedDepartment) ? parsedDepartment : null;
  }, []);
  const localPositionCode = useMemo(() => {
    const rawPosition = localStorage.getItem("position");
    if (rawPosition == null || String(rawPosition).trim() === "") return null;
    const parsedPosition = Number(rawPosition);
    return Number.isFinite(parsedPosition) ? parsedPosition : null;
  }, []);
  const canManageUseInputState = useMemo(
    () => [0, 2, 6].includes(localDepartmentCode) || [0, 1].includes(localPositionCode),
    [localDepartmentCode, localPositionCode]
  );
  // ✅ 집계표 메모 편집 권한:
  //    부서(2:회계팀, 4:영업팀, 5:운영팀, 6:개발팀) 또는 직책(0:대표, 1:팀장) 중 하나라도 맞으면 허용(OR)
  const canEditTallyNote = useMemo(
    () => [2, 4, 5, 6].includes(localDepartmentCode) || [0, 1].includes(localPositionCode),
    [localDepartmentCode, localPositionCode]
  );
  const isAccountLocked = useMemo(() => !!localAccountId, [localAccountId]);
  const [selectedAccountId, setSelectedAccountId] = useState(() => localAccountId || "");
  const [accountInput, setAccountInput] = useState("");
  const [viewLoading, setViewLoading] = useState(true);
  const loadingStartedRef = useRef(false);
  const accountListCheckedRef = useRef(false);
  const accountListEffectRanRef = useRef(false);

  const [originalRows, setOriginalRows] = useState([]);
  const [original2Rows, setOriginal2Rows] = useState([]);
  const today = dayjs();
  const [year, setYear] = useState(today.year());
  const [month, setMonth] = useState(today.month() + 1);

  const [images, setImages] = useState(Array(31).fill(null));
  const [receiptType, setReceiptType] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isMobileOrTablet = isMobile || isTablet;

  const [tabValue, setTabValue] = useState(0);
  const tallyNoteInputRef = useRef(null);
  const [tallyNoteInputKey, setTallyNoteInputKey] = useState(0);
  const [tallyNoteOrigin, setTallyNoteOrigin] = useState("");
  const [isTallyNoteDirty, setIsTallyNoteDirty] = useState(false);
  const [loadingTallyNote, setLoadingTallyNote] = useState(false);
  const [savingTallyNote, setSavingTallyNote] = useState(false);
  // 현장에서 작성한 현장 메모(본사 화면에서는 읽기 전용)
  const [tallyFieldNote, setTallyFieldNote] = useState("");

  const beginListLoading = useCallback(() => {
    setViewLoading(true);
    loadingStartedRef.current = false;
  }, []);

  const setSelectedAccountWithLoading = useCallback(
    (nextAccountId) => {
      const nextId = String(nextAccountId ?? "");
      if (nextId === String(selectedAccountId ?? "")) return;
      beginListLoading();
      setSelectedAccountId(nextId);
    },
    [beginListLoading, selectedAccountId]
  );

  const hook = useTallysheetData(selectedAccountId, year, month);

  const {
    dataRows,
    setDataRows,
    data2Rows,
    setData2Rows,
    accountList,
    pointList,
    fetchPointList, // ✅ 추가
    useList,
    fetchUseList, // ✅ 추가
    countMonth,
    count2Month,
    loading,
    fetchDataRows,
    fetchData2Rows,
    prevYear: hookPrevYear,
    prevMonth: hookPrevMonth,
    budgetGrant = 0,
    budget2Grant = 0,
    suppliesBudget = 0,
    suppliesUsed = 0,
    supplies2Budget = 0,
    supplies2Used = 0,
    fetchBudgetGrant = async () => { },
    fetchBudget2Grant = async () => { },
  } = hook || {};

  const prevYm = useMemo(() => {
    const base = dayjs(`${year}-${String(month).padStart(2, "0")}-01`);
    return base.subtract(1, "month");
  }, [year, month]);

  const prevYear = useMemo(() => hookPrevYear ?? prevYm.year(), [hookPrevYear, prevYm]);
  const prevMonth = useMemo(() => hookPrevMonth ?? prevYm.month() + 1, [hookPrevMonth, prevYm]);
  const activeNoteYear = useMemo(() => (tabValue === 0 ? year : prevYear), [tabValue, year, prevYear]);
  const activeNoteMonth = useMemo(
    () => (tabValue === 0 ? month : prevMonth),
    [tabValue, month, prevMonth]
  );

  // ✅ 해당 월의 실제 일수
  const daysInMonthNow = useMemo(() => {
    return dayjs(`${year}-${String(month).padStart(2, "0")}-01`).daysInMonth();
  }, [year, month]);

  const daysInMonthPrev = useMemo(() => {
    return dayjs(`${prevYear}-${String(prevMonth).padStart(2, "0")}-01`).daysInMonth();
  }, [prevYear, prevMonth]);

  useEffect(() => {
    if (!localAccountId) return;
    if (String(selectedAccountId) !== String(localAccountId)) {
      setSelectedAccountWithLoading(localAccountId);
    }
  }, [localAccountId, selectedAccountId, setSelectedAccountWithLoading]);

  const filteredAccountList = useMemo(() => {
    if (!localAccountId) return accountList || [];
    return (accountList || []).filter((row) => String(row.account_id) === String(localAccountId));
  }, [accountList, localAccountId]);

  useEffect(() => {
    if (!accountListEffectRanRef.current) {
      accountListEffectRanRef.current = true;
      return;
    }
    accountListCheckedRef.current = true;
  }, [accountList]);

  const selectedAccountOption = useMemo(() => {
    if (!selectedAccountId) return null;
    return (
      (filteredAccountList || []).find((a) => String(a.account_id) === String(selectedAccountId)) ||
      null
    );
  }, [filteredAccountList, selectedAccountId]);

  useEffect(() => {
    if (!selectedAccountId) return;
    fetchUseList?.(selectedAccountId, year, month);
  }, [selectedAccountId, year, month, fetchUseList]);

  const fetchTallyNote = useCallback(
    async (overrideAccountId, overrideYear, overrideMonth) => {
      const a = overrideAccountId ?? selectedAccountId;
      const y = overrideYear ?? activeNoteYear;
      const m = overrideMonth ?? activeNoteMonth;

      if (!a || !y || !m) {
        setTallyNoteOrigin("");
        setTallyFieldNote("");
        setIsTallyNoteDirty(false);
        setTallyNoteInputKey((k) => k + 1);
        return "";
      }

      setLoadingTallyNote(true);
      try {
        const res = await api.get("/Operate/TallySheetNote", {
          params: { account_id: a, year: y, month: m },
        });
        const loadedNote = String(res.data?.note ?? "");
        setTallyNoteOrigin(loadedNote);
        setTallyFieldNote(String(res.data?.note2 ?? ""));
        setIsTallyNoteDirty(false);
        setTallyNoteInputKey((k) => k + 1);
        return loadedNote;
      } catch (err) {
        console.error("집계표 메모 조회 실패:", err);
        setTallyNoteOrigin("");
        setTallyFieldNote("");
        setIsTallyNoteDirty(false);
        setTallyNoteInputKey((k) => k + 1);
        return "";
      } finally {
        setLoadingTallyNote(false);
      }
    },
    [selectedAccountId, activeNoteYear, activeNoteMonth]
  );

  useEffect(() => {
    fetchTallyNote(selectedAccountId, activeNoteYear, activeNoteMonth);
  }, [fetchTallyNote, selectedAccountId, activeNoteYear, activeNoteMonth]);

  const handleTallyNoteInputChange = useCallback(
    (e) => {
      const nextValue = String(e.target.value ?? "");
      const dirty = nextValue !== String(tallyNoteOrigin ?? "");
      setIsTallyNoteDirty((prev) => (prev === dirty ? prev : dirty));
    },
    [tallyNoteOrigin]
  );

  const handleSaveTallyNote = useCallback(async () => {
    if (!canEditTallyNote) return;
    if (!selectedAccountId || !activeNoteYear || !activeNoteMonth) return;

    const nextNote = String(tallyNoteInputRef.current?.value ?? "");
    if (nextNote === String(tallyNoteOrigin ?? "")) return;

    try {
      setSavingTallyNote(true);
      const payload = {
        account_id: selectedAccountId,
        year: activeNoteYear,
        month: activeNoteMonth,
        note: nextNote,
        user_id: localUserId,
      };
      const res = await api.post("/Operate/TallySheetNoteSave", payload);

      if (res.data?.code === 200) {
        setTallyNoteOrigin(nextNote);
        setIsTallyNoteDirty(false);
        Swal.fire("저장", "집계표 메모가 저장되었습니다.", "success");
      } else {
        Swal.fire("실패", res.data?.message || "집계표 메모 저장 실패", "error");
      }
    } catch (err) {
      Swal.fire("실패", err.message || "집계표 메모 저장 중 오류 발생", "error");
    } finally {
      setSavingTallyNote(false);
    }
  }, [
    canEditTallyNote,
    selectedAccountId,
    activeNoteYear,
    activeNoteMonth,
    tallyNoteOrigin,
    localUserId,
  ]);

  // ✅ 검색: 엔터 입력 시 텍스트로 거래처 선택 (정확 일치 우선, 없으면 부분 일치)
  const selectAccountByInput = () => {
    if (isAccountLocked) return;
    const q = String(accountInput || "").trim();
    if (!q) return;
    const list = filteredAccountList || [];
    const qLower = q.toLowerCase();
    const exact = list.find((a) => String(a?.account_name || "").toLowerCase() === qLower);
    const partial =
      exact ||
      list.find((a) =>
        String(a?.account_name || "")
          .toLowerCase()
          .includes(qLower)
      );
    if (partial) {
      setSelectedAccountWithLoading(partial.account_id);
      setAccountInput(partial.account_name || q);
    }
  };

  useEffect(() => {
    setDataRows?.([]);
    setData2Rows?.([]);
    setOriginalRows([]);
    setOriginal2Rows([]);

    // ✅ 현재 선택 월 기준으로 길이 맞춤
    setImages(Array(daysInMonthNow).fill(null));
    setReceiptType(Array(daysInMonthNow).fill("")); // 타입도 같이 맞추는게 안전
  }, [selectedAccountId, year, month, setDataRows, setData2Rows, daysInMonthNow]);

  useEffect(() => {
    if ((dataRows || []).length > 0 && originalRows.length === 0) {
      setOriginalRows((dataRows || []).map((r) => ({ ...r })));
    }
  }, [dataRows, originalRows.length]);

  useEffect(() => {
    if ((data2Rows || []).length > 0 && original2Rows.length === 0) {
      setOriginal2Rows((data2Rows || []).map((r) => ({ ...r })));
    }
  }, [data2Rows, original2Rows.length]);

  useEffect(() => {
    if (localAccountId) {
      setSelectedAccountWithLoading(localAccountId);
      return;
    }
    if ((accountList || []).length > 0 && !selectedAccountId) {
      setSelectedAccountWithLoading(accountList[0].account_id);
    }
  }, [accountList, selectedAccountId, localAccountId, setSelectedAccountWithLoading]);

  useEffect(() => {
    if (!viewLoading) return;
    if (!selectedAccountId) {
      if (accountListCheckedRef.current && (filteredAccountList || []).length === 0) {
        setViewLoading(false);
      }
      return;
    }

    if (loading) {
      loadingStartedRef.current = true;
      return;
    }

    if (loadingStartedRef.current) {
      setViewLoading(false);
      loadingStartedRef.current = false;
      return;
    }
  }, [loading, viewLoading, selectedAccountId, filteredAccountList]);

  // ======================== ✅ Floating Preview 상태 ========================
  const [floatingPreview, setFloatingPreview] = useState({
    open: false,
    files: [],
    currentIndex: 0,
    revokeUrls: [],
  });
  const floatingPreviewRef = useRef(floatingPreview);

  const releasePreviewBlobUrls = useCallback((previewState) => {
    (previewState?.revokeUrls || []).forEach((previewUrl) => {
      if (String(previewUrl || "").startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    });
  }, []);

  // ======================== ✅ "클릭된 셀/행" 하이라이트 상태 (요청 반영) ========================
  const [activeCell, setActiveCell] = useState({
    isSecond: false,
    rowIndex: null,
    colKey: null,
  });

  // ======================== 공통 util ========================
  const toAbsolutePreviewUrl = useCallback((value) => {
    const raw = normalizeStoredPreviewPath(value);
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;

    const base = String(API_BASE_URL || "").replace(/\/$/, "");
    const params = new URLSearchParams();
    params.set("file_path", raw.startsWith("/") ? raw : `/${raw}`);
    params.set("_ts", String(Date.now()));
    return `${base}/Account/AccountStoredFileView?${params.toString()}`;
  }, []);

  const toPreviewUrl = useCallback((path) => {
    if (!path) return null;
    const s = normalizeStoredPreviewPath(path);
    if (!s) return null;
    if (s.startsWith("blob:")) return s;
    if (s.startsWith("http")) return s;
    if (s.startsWith("data:")) return s;
    // 이미 미리보기 API URL로 변환된 값(상대경로 /api 포함)은 그대로 사용
    if (s.includes("/Account/AccountStoredFileView")) return s;
    return toAbsolutePreviewUrl(s);
  }, [toAbsolutePreviewUrl]);

  const openFloatingPreview = useCallback(
    (src, title = "미리보기") => {
      if (!src) return;

      setFloatingPreview((prev) => {
        releasePreviewBlobUrls(prev);
        const revokeUrls = [];
        // 단일 파일/경로와 다중 파일 목록 모두 공통 포맷으로 변환한다.
        const sourceList = Array.isArray(src) ? src : [src];
        const files = sourceList
          .map((item, idx) => {
            if (!item) return null;

            const previewUrl =
              typeof item === "object"
                ? (() => {
                  const nextBlobUrl = URL.createObjectURL(item);
                  revokeUrls.push(nextBlobUrl);
                  return nextBlobUrl;
                })()
                : toPreviewUrl(item) || String(item);

            return {
              url: previewUrl,
              name:
                sourceList.length > 1
                  ? `${title || "미리보기"} ${idx + 1}`
                  : title || extractDisplayFileName(item) || "미리보기",
              kind: getPreviewFileKind(item),
            };
          })
          .filter(Boolean);

        if (!files.length) {
          return { ...prev, open: false, files: [], currentIndex: 0, revokeUrls: [] };
        }

        return {
          open: true,
          files,
          currentIndex: 0,
          revokeUrls,
        };
      });
    },
    [releasePreviewBlobUrls, toPreviewUrl]
  );

  const closeFloatingPreview = useCallback(() => {
    setFloatingPreview((prev) => {
      releasePreviewBlobUrls(prev);
      return { ...prev, open: false, files: [], currentIndex: 0, revokeUrls: [] };
    });
  }, [releasePreviewBlobUrls]);

  const closeModalWithPreview = useCallback(
    (setModalOpen) => {
      closeFloatingPreview();
      setModalOpen(false);
    },
    [closeFloatingPreview]
  );

  const handleFloatingPreviewIndexChange = useCallback((nextIndex) => {
    setFloatingPreview((prev) => ({
      ...prev,
      currentIndex:
        typeof nextIndex === "function" ? nextIndex(prev.currentIndex) : Number(nextIndex) || 0,
    }));
  }, []);

  useEffect(() => {
    floatingPreviewRef.current = floatingPreview;
  }, [floatingPreview]);

  useEffect(
    () => () => releasePreviewBlobUrls(floatingPreviewRef.current),
    [releasePreviewBlobUrls]
  );

  // ✅✅ FIX: 날짜는 항상 dayjs로 확정해서 YYYY-MM-DD 만들기
  const buildCellDate = useCallback((y, m, dayIdx) => {
    const dd = String((dayIdx ?? 0) + 1).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const yy = String(y);
    return dayjs(`${yy}-${mm}-${dd}`).format("YYYY-MM-DD");
  }, []);

  const maskCardNo = (no) => {
    const s = String(no ?? "").replace(/\s+/g, "");
    if (!s) return "";
    const last4 = s.slice(-4);
    return `****-****-****-${last4}`;
  };

  const maskCardNoFull = (no) => {
    const s = String(no ?? "").replace(/[-\s]/g, "");
    if (!s) return "";
    const first4 = s.slice(0, 4).padEnd(4, "0");
    const last4 = s.slice(-4).padStart(4, "0");
    return `${first4}-****-****-${last4}`;
  };

  // ======================== ✅ 법인카드(1000) 모달 플로우 (기존 유지 + 목록에서 바로 수정/저장) ========================
  const [cardChoiceOpen, setCardChoiceOpen] = useState(false);
  const [cardCreateOpen, setCardCreateOpen] = useState(false);
  const [cardListOpen, setCardListOpen] = useState(false);
  const cardFileRef = useRef(null);

  // ✅ (1000) 목록 전체 편집용
  const [cardOrigRowsForList, setCardOrigRowsForList] = useState([]);

  // 문자열 비교용(차량관리 탭과 동일 컨셉)
  const normalizeStr = (v) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : v);

  // 변경 셀 스타일
  const getCardCellStyle = (rowIndex, key, value) => {
    const orig = cardOrigRowsForList?.[rowIndex]?.[key];

    // total 같은 숫자계열
    if (key === "total") {
      return parseNumber(orig) !== parseNumber(value) ? { color: "red" } : { color: "black" };
    }

    if (typeof orig === "string" && typeof value === "string") {
      return normalizeStr(orig) !== normalizeStr(value) ? { color: "red" } : { color: "black" };
    }
    return orig !== value ? { color: "red" } : { color: "black" };
  };

  // 행 변경 여부(필드 + 파일)
  const isCardRowChanged = (row, orig) => {
    if (!row || !orig) return false;
    const fields = ["use_name", "total", "card_idx", "receipt_type"];
    const fieldChanged = fields.some((k) => {
      if (k === "total") return parseNumber(row[k]) !== parseNumber(orig[k]);
      return normalizeStr(row[k]) !== normalizeStr(orig[k]);
    });
    const fileChanged = !!row._file; // 새 파일 선택하면 변경으로 취급
    return fieldChanged || fileChanged;
  };

  // 행 업데이트 헬퍼
  const updateCardRow = (rowIndex, patch) => {
    setCardEditRows((prev) => prev.map((r, i) => (i === rowIndex ? { ...r, ...patch } : r)));
  };

  // 파일 선택(행마다)
  const handleCardRowFileChange = (rowIndex, file) => {
    if (!file) return;

    closeFloatingPreview();
    setCardEditRows((prev) =>
      prev.map((r, i) => {
        if (i !== rowIndex) return r;
        if (r._preview && String(r._preview).startsWith("blob:")) URL.revokeObjectURL(r._preview);
        return { ...r, _file: file, _preview: URL.createObjectURL(file) };
      })
    );
  };

  const [cardContext, setCardContext] = useState({
    isSecond: false,
    rowIndex: null,
    colKey: null,
    dayIndex: null,
    dateStr: "",
    cellValue: 0,
  });

  const [cardRows, setCardRows] = useState([]);
  const [cardSelectedRow, setCardSelectedRow] = useState(null);
  const [cardSelectedKey, setCardSelectedKey] = useState(null);

  const [corpCardList, setCorpCardList] = useState([]);
  const [corpCardLoading, setCorpCardLoading] = useState(false);

  // ======================== ✅ (1000) List: 전체 행 편집용 상태 ========================
  const [cardEditRows, setCardEditRows] = useState([]);
  const [cardOrigRowsForDiff, setCardOrigRowsForDiff] = useState([]);
  const [cardRowFiles, setCardRowFiles] = useState({}); // { [rowKey]: {file, previewUrl} }

  const [cardForm, setCardForm] = useState({
    id: null,
    use_name: "",
    total: "",
    receipt_image: null,
    card_idx: "",
    receipt_type: "UNKNOWN",
    card_brand: "",
    card_no: "",
    sale_id: "",
    account_id: "",
  });

  const [cardReceiptPreview, setCardReceiptPreview] = useState(null);

  const getCorpCardByIdx = useCallback(
    (idx) => {
      const key = String(idx ?? "");
      if (!key) return null;
      return (corpCardList || []).find((c) => String(c.idx) === key) || null;
    },
    [corpCardList]
  );

  const fetchAccountCorporateCardList = useCallback(async (accountId) => {
    if (!accountId) {
      setCorpCardList([]);
      return [];
    }

    setCorpCardLoading(true);
    try {
      const res = await api.get("/Account/AccountCorporateCardList", {
        params: { account_id: accountId },
        validateStatus: () => true,
      });

      if (res.status !== 200) throw new Error(res.data?.message || "법인카드 목록 조회 실패");

      const list = res.data?.data || res.data || [];
      setCorpCardList(Array.isArray(list) ? list : []);
      return Array.isArray(list) ? list : [];
    } finally {
      setCorpCardLoading(false);
    }
  }, []);

  // ✅ 목록 열릴 때도 카드목록 필요(선택 셀렉트 편집)
  useEffect(() => {
    if (!selectedAccountId) return;
    if (cardCreateOpen || cardListOpen) {
      fetchAccountCorporateCardList(selectedAccountId).catch((e) => {
        Swal.fire("오류", e.message || "법인카드 목록 조회 중 오류", "error");
      });
    }
  }, [selectedAccountId, cardCreateOpen, cardListOpen, fetchAccountCorporateCardList]);

  useEffect(() => {
    if (!cardSelectedRow) return;
    setCardReceiptPreview(toPreviewUrl(cardSelectedRow.receipt_image));
  }, [cardSelectedRow, toPreviewUrl]);

  const handleCardReceiptFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.currentTarget.value = "";

    closeFloatingPreview();
    if (cardReceiptPreview && String(cardReceiptPreview).startsWith("blob:")) {
      URL.revokeObjectURL(cardReceiptPreview);
    }

    setCardForm((p) => ({ ...p, receipt_image: file }));
    const url = URL.createObjectURL(file);
    setCardReceiptPreview(url);
  };

  useEffect(() => {
    return () => {
      if (cardReceiptPreview && String(cardReceiptPreview).startsWith("blob:")) {
        URL.revokeObjectURL(cardReceiptPreview);
      }
    };
  }, [cardReceiptPreview]);

  const fetchCorpCardList = async (accountId, dateStr) => {
    const res = await api.get("/Account/AccountCorporateCardPaymentList", {
      params: { account_id: accountId, payment_dt: dateStr },
      validateStatus: () => true,
    });

    if (res.status !== 200) throw new Error(res.data?.message || "목록 조회 실패");
    return res.data || [];
  };

  const saveCorpCardPayment = async (mode) => {
    const fd = new FormData();

    fd.append("user_id", localUserId);

    const submitAccountId =
      mode === "edit"
        ? String(cardForm.account_id || selectedAccountId)
        : String(selectedAccountId);

    fd.append("account_id", submitAccountId);

    // ✅✅ FIX: cell_date는 항상 컨텍스트(클릭한 셀) 기준 dayjs로 확정
    const y = cardContext.isSecond ? prevYear : year;
    const m = cardContext.isSecond ? prevMonth : month;
    const fixedCellDate = buildCellDate(y, m, cardContext.dayIndex ?? 0);

    fd.append("cell_day", String((cardContext.dayIndex ?? 0) + 1));
    fd.append("cell_date", fixedCellDate);

    fd.append("receipt_type", cardForm.receipt_type || "UNKNOWN");
    fd.append("type", 1000);
    fd.append("saveType", "cor");

    if (cardForm.card_idx) fd.append("card_idx", cardForm.card_idx);

    const picked = getCorpCardByIdx(cardForm.card_idx);
    const brand = cardForm.card_brand || picked?.card_brand || "";
    const no = cardForm.card_no || picked?.card_no || "";

    fd.append("card_brand", brand);
    fd.append("card_no", no);
    fd.append("total", parseNumber(cardForm.total));

    if (cardForm.receipt_image) fd.append("file", cardForm.receipt_image);

    if (mode !== "edit" && !cardForm.receipt_image) {
      Swal.fire("안내", "영수증을 등록해주세요.", "warning");
      return false;
    }

    if (mode === "edit") {
      if (cardForm.id != null) fd.append("id", cardForm.id);
      if (cardForm.sale_id) fd.append("sale_id", String(cardForm.sale_id));
      fd.append("row_account_id", submitAccountId);
    }

    try {
      Swal.fire({
        title: "영수증 확인 중 입니다.",
        text: "잠시만 기다려 주세요...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      // ✅ "등록할 때 endpoint" 그대로 사용
      const res = await api.post("/receipt-scanV3", fd, {
        headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
        validateStatus: () => true,
      });

      Swal.close();

      if (res.status === 200) {
        Swal.fire("완료", mode === "edit" ? "수정되었습니다." : "등록되었습니다.", "success");
        // 소모품 예산 누계 갱신 (type 1002 소모품 포함, 백그라운드 호출)

        await fetchDataRows?.(selectedAccountId, year, month);
        await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
        await fetchBudgetGrant?.(selectedAccountId, year, month);
        await fetchBudget2Grant?.(selectedAccountId, year, month);

        setOriginalRows([]);
        setOriginal2Rows([]);

        if (cardFileRef.current) cardFileRef.current.value = "";
        setCardForm((p) => ({ ...p, receipt_image: null }));
        setCardReceiptPreview(null);
        return true;
      }

      if (res.status === 400) {
        Swal.fire("실패", res.data?.message || "저장에 실패했습니다.", "error");
        return false;
      }

      Swal.fire(
        "오류",
        res.data?.message || `예상치 못한 오류가 발생했습니다. (code: ${res.status})`,
        "error"
      );
      return false;
    } catch (err) {
      Swal.close();
      Swal.fire("오류", err.message || "저장 중 문제가 발생했습니다.", "error");
      return false;
    }
  };

  const saveCorpCardPaymentsBulk = async () => {
    if (!Array.isArray(cardEditRows) || cardEditRows.length === 0) {
      Swal.fire("안내", "저장할 데이터가 없습니다.", "info");
      return false;
    }

    const changedIndexes = cardEditRows
      .map((r, i) => (isCardRowChanged(r, cardOrigRowsForList?.[i]) ? i : -1))
      .filter((i) => i >= 0);

    if (changedIndexes.length === 0) {
      Swal.fire("안내", "변경된 내용이 없습니다.", "info");
      return false;
    }

    try {
      Swal.fire({
        title: "저장 중 입니다.",
        text: `0 / ${changedIndexes.length}`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_LOADING_CUSTOM_CLASS,
        willOpen: ensureSwalLoadingStyle,
        didOpen: showSwalLoadingWithoutConfirm,
      });

      for (let step = 0; step < changedIndexes.length; step++) {
        const idx = changedIndexes[step];
        const row = cardEditRows[idx];

        Swal.update({
          text: `${step + 1} / ${changedIndexes.length}`,
        });

        const fd = new FormData();
        fd.append("user_id", localUserId);

        const submitAccountId = String(row.account_id || selectedAccountId);
        fd.append("account_id", submitAccountId);

        // ✅ 클릭한 셀 날짜 기준(기존 로직 유지)
        const y = cardContext.isSecond ? prevYear : year;
        const m = cardContext.isSecond ? prevMonth : month;
        const fixedCellDate = buildCellDate(y, m, cardContext.dayIndex ?? 0);

        fd.append("cell_day", String((cardContext.dayIndex ?? 0) + 1));
        fd.append("cell_date", fixedCellDate);

        fd.append("receipt_type", row.receipt_type || "UNKNOWN");
        fd.append("type", 1000);
        fd.append("saveType", "cor");

        if (row.card_idx) fd.append("card_idx", String(row.card_idx));

        const picked = getCorpCardByIdx(row.card_idx);
        const brand = row.card_brand || picked?.card_brand || "";
        const no = row.card_no || picked?.card_no || "";
        fd.append("card_brand", brand);
        fd.append("card_no", no);

        fd.append("use_name", row.use_name || "");
        fd.append("total", parseNumber(row.total));

        // ✅ 파일 선택한 경우만
        if (row._file) fd.append("file", row._file);

        // ✅ edit 필수키
        if (row.id != null) fd.append("id", row.id);
        if (row.sale_id) fd.append("sale_id", String(row.sale_id));
        fd.append("row_account_id", submitAccountId);

        const res = await api.post("/receipt-scanV3", fd, {
          headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
          validateStatus: () => true,
        });

        if (res.status !== 200) {
          throw new Error(res.data?.message || `저장 실패 (code: ${res.status})`);
        }
      }

      Swal.close();
      Swal.fire("완료", "저장되었습니다.", "success");
      // 소모품 예산 누계 갱신 (백그라운드 호출)

      await fetchDataRows?.(selectedAccountId, year, month);
      await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
      await fetchBudgetGrant?.(selectedAccountId, year, month);
      await fetchBudget2Grant?.(selectedAccountId, year, month);

      setOriginalRows([]);
      setOriginal2Rows([]);

      return true;
    } catch (e) {
      Swal.close();
      Swal.fire("오류", e.message || "저장 중 문제가 발생했습니다.", "error");
      return false;
    }
  };

  const handleCorpCardCellClick = async (rowOriginal, rIdx, colKey, isSecond = false) => {
    if (!rowOriginal || rowOriginal.name === "총합") return;
    if (colKey === "name" || colKey === "total") return;
    if (String(rowOriginal.type) !== "1000") return;

    const rows = isSecond ? data2Rows : dataRows;
    const cellVal = parseNumber(rows?.[rIdx]?.[colKey]);

    const dayIndex = Number(String(colKey).replace("day_", "")) - 1;
    if (Number.isNaN(dayIndex) || dayIndex < 0) return;

    const y = isSecond ? prevYear : year;
    const m = isSecond ? prevMonth : month;
    const dateStr = buildCellDate(y, m, dayIndex);

    setCardContext({
      isSecond,
      rowIndex: rIdx,
      colKey,
      dayIndex,
      dateStr,
      cellValue: cellVal,
    });
    try {
      Swal.fire({
        title: "확인 중",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const list = await fetchCorpCardList(selectedAccountId, dateStr);
      Swal.close();

      const safe = Array.isArray(list) ? list : [];
      if (safe.length > 0) {
        // 이미 등록됨 -> 선택 모달(등록/수정)
        setCardChoiceOpen(true);
        return;
      }
    } catch (e) {
      Swal.close();
      Swal.fire("오류", e.message || "목록 조회 중 오류", "error");
    }

    // 목록이 없으면 신규 등록
    setCardForm((p) => ({
      ...p,
      id: null,
      use_name: "",
      total: "",
      receipt_image: null,
      card_idx: "",
      receipt_type: "UNKNOWN",
      card_brand: "",
      card_no: "",
      sale_id: "",
      account_id: String(selectedAccountId || ""),
    }));
    setCardReceiptPreview(null);
    setCardCreateOpen(true);
  };

  const openCreateFromChoice = () => {
    setCardChoiceOpen(false);
    setCardForm((p) => ({
      ...p,
      id: null,
      use_name: "",
      total: "",
      receipt_image: null,
      card_idx: "",
      receipt_type: "UNKNOWN",
      card_brand: "",
      card_no: "",
      sale_id: "",
      account_id: String(selectedAccountId || ""),
    }));
    setCardReceiptPreview(null);
    setCardCreateOpen(true);
  };

  const openListFromChoice = async () => {
    setCardChoiceOpen(false);

    try {
      Swal.fire({
        title: "불러오는 중",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const list = await fetchCorpCardList(selectedAccountId, cardContext.dateStr);
      Swal.close();

      const safe = Array.isArray(list) ? list : [];
      setCardRows(safe);

      const deep = safe.map((r) => ({ ...r }));
      setCardEditRows(deep);
      setCardOrigRowsForDiff(JSON.parse(JSON.stringify(deep)));
      setCardRowFiles({});

      setCardListOpen(true);
    } catch (e) {
      Swal.close();
      Swal.fire("오류", e.message || "목록 조회 중 오류", "error");
    }
  };

  // ✅ 목록에서 행 클릭하면 곧바로 편집 상태(cardForm)로 바뀌게
  const selectCardRowForInlineEdit = (rowObj, rowKey) => {
    setCardSelectedRow(rowObj);
    setCardSelectedKey(rowKey);

    setCardForm((p) => ({
      ...p,
      id: rowObj.id,
      use_name: rowObj.use_name || "",
      total: String(rowObj.total ?? ""),
      receipt_image: null,
      card_idx: String(rowObj.card_idx ?? rowObj.corp_card_idx ?? rowObj.idx ?? ""),
      receipt_type: rowObj.receipt_type || p.receipt_type || "UNKNOWN",
      card_brand: rowObj.card_brand || p.card_brand || "",
      card_no: rowObj.card_no || p.card_no || "",
      sale_id: String(rowObj.sale_id ?? ""),
      account_id: String(rowObj.account_id ?? selectedAccountId ?? ""),
    }));

    setCardReceiptPreview(toPreviewUrl(rowObj.receipt_image));
  };

  useEffect(() => {
    if (!cardListOpen) return;
    if (cardSelectedRow) return; // 이미 선택되어 있으면 패스
    if (!Array.isArray(cardRows) || cardRows.length === 0) return;

    const first = cardRows[0];
    const rowKey = String(first.id ?? first.sale_id ?? 0);
    selectCardRowForInlineEdit(first, rowKey);
  }, [cardListOpen, cardRows, cardSelectedRow, selectCardRowForInlineEdit]);

  // ======================== ✅ 1008 모달 플로우 (목록에서 바로 수정/저장) ========================
  const [cashChoiceOpen, setCashChoiceOpen] = useState(false);
  const [cashCreateOpen, setCashCreateOpen] = useState(false);
  const [cashListOpen, setCashListOpen] = useState(false);
  const cashFileRef = useRef(null);

  const [cashContext, setCashContext] = useState({
    isSecond: false,
    rowIndex: null,
    colKey: null,
    dayIndex: null,
    dateStr: "",
    cellValue: 0,
  });

  const [cashRows, setCashRows] = useState([]);
  const [cashSelectedRow, setCashSelectedRow] = useState(null);
  const [cashSelectedKey, setCashSelectedKey] = useState(null);

  // ======================== ✅ (1008) List: 전체 행 편집용 상태 ========================
  const [cashEditRows, setCashEditRows] = useState([]);
  const [cashOrigRowsForDiff, setCashOrigRowsForDiff] = useState([]);
  const [cashRowFiles, setCashRowFiles] = useState({}); // { [rowKey]: {file, previewUrl} }

  const [cashForm, setCashForm] = useState({
    id: null,
    use_name: "",
    total: "",
    payType: "1",
    cash_receipt_type: "3",
    receipt_image: null,
    receipt_type: "UNKNOWN",
    buyer: "",
    sale_id: "",
    account_id: "",
  });

  const [cashReceiptPreview, setCashReceiptPreview] = useState(null);

  const handleCashReceiptFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.currentTarget.value = "";

    closeFloatingPreview();
    if (cashReceiptPreview && String(cashReceiptPreview).startsWith("blob:")) {
      URL.revokeObjectURL(cashReceiptPreview);
    }

    setCashForm((p) => ({ ...p, receipt_image: file }));
    const url = URL.createObjectURL(file);
    setCashReceiptPreview(url);
  };

  useEffect(() => {
    return () => {
      if (cashReceiptPreview && String(cashReceiptPreview).startsWith("blob:")) {
        URL.revokeObjectURL(cashReceiptPreview);
      }
    };
  }, [cashReceiptPreview]);

  const fetchCashPaymentList = async (accountId, dateStr) => {
    const res = await api.get(ENDPOINT_CASH_LIST, {
      params: { account_id: accountId, saleDate: dateStr, type: 1008 },
      validateStatus: () => true,
    });
    if (res.status !== 200) throw new Error(res.data?.message || "목록 조회 실패");
    return res.data || [];
  };

  const saveCashPayment = async (mode) => {
    const fd = new FormData();

    fd.append("user_id", localUserId);

    const submitAccountId =
      mode === "edit"
        ? String(cashForm.account_id || selectedAccountId)
        : String(selectedAccountId);

    fd.append("account_id", submitAccountId);

    const y = cashContext.isSecond ? prevYear : year;
    const m = cashContext.isSecond ? prevMonth : month;
    const fixedCellDate = buildCellDate(y, m, cashContext.dayIndex ?? 0);

    const cellDay = String((cashContext.dayIndex ?? 0) + 1);
    fd.append("cell_day", cellDay);
    fd.append("cell_date", fixedCellDate);

    fd.append("type", 1008);

    fd.append("payType", String(cashForm.payType || "1"));
    fd.append("cash_receipt_type", String(cashForm.cash_receipt_type || "3"));
    fd.append("receipt_type", cashForm.receipt_type || "UNKNOWN");
    fd.append("use_name", cashForm.use_name || "");
    fd.append("buyer", cashForm.buyer || "");
    fd.append("total", parseNumber(cashForm.total));

    if (cashForm.receipt_image) fd.append("file", cashForm.receipt_image);

    if (mode !== "edit" && !cashForm.receipt_image) {
      Swal.fire("안내", "영수증을 등록해주세요.", "warning");
      return false;
    }

    if (mode === "edit") {
      if (cashForm.id != null) fd.append("id", cashForm.id);
      if (cashForm.sale_id) fd.append("sale_id", String(cashForm.sale_id));
      fd.append("row_account_id", submitAccountId);
    }

    try {
      Swal.fire({
        title: "저장 중 입니다.",
        text: "잠시만 기다려 주세요...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_LOADING_CUSTOM_CLASS,
        willOpen: ensureSwalLoadingStyle,
        didOpen: showSwalLoadingWithoutConfirm,
      });

      // ✅ 등록 때 endpoint 그대로
      const res = await api.post(ENDPOINT_CASH_SAVE, fd, {
        headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
        validateStatus: () => true,
      });

      Swal.close();

      if (res.status === 200) {
        const desiredPayType = String(cashForm.payType || "1");
        const desiredCashReceiptType = String(cashForm.cash_receipt_type || "3");
        const savedPayType = String(res.data?.payType ?? res.data?.pay_type ?? "");
        const savedCashReceiptType = String(
          res.data?.cashReceiptType ?? res.data?.cash_receipt_type ?? ""
        );

        // ✅ OCR이 payType을 카드로 덮어쓴 경우, 사용자 선택(현금)을 다시 반영
        if (
          desiredPayType === "1" &&
          (savedPayType === "2" || savedCashReceiptType !== desiredCashReceiptType)
        ) {
          const purchase = res.data || {};
          const saleId = String(purchase.sale_id || cashForm.sale_id || "");
          const fixAccountId = String(purchase.account_id || submitAccountId || "");
          const fixSaleDate = String(purchase.saleDate || cashContext.dateStr || "");

          if (saleId && fixAccountId) {
            const fdFix = new FormData();
            fdFix.append("user_id", localUserId);
            fdFix.append("account_id", fixAccountId);
            fdFix.append("row_account_id", fixAccountId);
            fdFix.append("saleDate", fixSaleDate);
            fdFix.append("type", 1008);
            fdFix.append("payType", "1");
            fdFix.append("cash_receipt_type", desiredCashReceiptType);
            fdFix.append("use_name", purchase.use_name || cashForm.use_name || "");
            fdFix.append("buyer", purchase.buyer || cashForm.buyer || "");
            fdFix.append(
              "receipt_type",
              purchase.receipt_type || cashForm.receipt_type || "UNKNOWN"
            );
            fdFix.append("total", parseNumber(purchase.total ?? cashForm.total ?? 0));
            fdFix.append("vat", parseNumber(purchase.vat ?? 0));
            fdFix.append("taxFree", parseNumber(purchase.taxFree ?? 0));
            fdFix.append("tax", parseNumber(purchase.tax ?? 0));
            fdFix.append("totalCash", parseNumber(purchase.total ?? cashForm.total ?? 0));
            fdFix.append("totalCard", 0);
            fdFix.append("cardNo", "");
            fdFix.append("cardBrand", "");
            if (purchase.bizNo) fdFix.append("bizNo", purchase.bizNo);
            if (purchase.note) fdFix.append("note", purchase.note);
            if (purchase.receipt_image) fdFix.append("receipt_image", purchase.receipt_image);
            fdFix.append("sale_id", saleId);
            if (purchase.id != null) fdFix.append("id", purchase.id);

            const resFix = await api.post("/Account/AccountTallyToPurchaseSave", fdFix, {
              headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
              validateStatus: () => true,
            });

            if (resFix.status !== 200) {
              throw new Error(resFix.data?.message || `결제수단 보정 실패(code: ${resFix.status})`);
            }
          }
        }

        Swal.fire("완료", mode === "edit" ? "수정되었습니다." : "등록되었습니다.", "success");
        // 소모품 예산 누계 갱신 (type 1002 소모품 포함, 백그라운드 호출)

        await fetchDataRows?.(selectedAccountId, year, month);
        await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
        await fetchBudgetGrant?.(selectedAccountId, year, month);
        await fetchBudget2Grant?.(selectedAccountId, year, month);

        setOriginalRows([]);
        setOriginal2Rows([]);

        if (cashFileRef.current) cashFileRef.current.value = "";
        setCashForm((p) => ({ ...p, receipt_image: null }));
        setCashReceiptPreview(null);
        return true;
      }

      if (res.status === 400) {
        Swal.fire("실패", res.data?.message || "저장에 실패했습니다.", "error");
        return false;
      }

      Swal.fire(
        "오류",
        res.data?.message || `예상치 못한 오류가 발생했습니다. (code: ${res.status})`,
        "error"
      );
      return false;
    } catch (err) {
      Swal.close();
      Swal.fire("오류", err.message || "저장 중 문제가 발생했습니다.", "error");
      return false;
    }
  };

  const handleCashCellClick = async (rowOriginal, rIdx, colKey, isSecond = false) => {
    if (!rowOriginal || rowOriginal.name === "총합") return;
    if (colKey === "name" || colKey === "total") return;
    if (String(rowOriginal.type) !== "1008") return;

    const rows = isSecond ? data2Rows : dataRows;
    const cellVal = parseNumber(rows?.[rIdx]?.[colKey]);

    const dayIndex = Number(String(colKey).replace("day_", "")) - 1;
    if (Number.isNaN(dayIndex) || dayIndex < 0) return;

    const y = isSecond ? prevYear : year;
    const m = isSecond ? prevMonth : month;
    const dateStr = buildCellDate(y, m, dayIndex);

    setCashContext({
      isSecond,
      rowIndex: rIdx,
      colKey,
      dayIndex,
      dateStr,
      cellValue: cellVal,
    });
    try {
      Swal.fire({
        title: "확인 중",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const list = await fetchCashPaymentList(selectedAccountId, dateStr);
      Swal.close();

      const safe = Array.isArray(list) ? list : [];
      if (safe.length > 0) {
        // 이미 등록됨 -> 선택 모달(등록/수정)
        setCashChoiceOpen(true);
        return;
      }
    } catch (e) {
      Swal.close();
      Swal.fire("오류", e.message || "목록 조회 중 오류", "error");
    }

    // 목록이 없으면 신규 등록
    setCashForm({
      id: null,
      use_name: "",
      total: "",
      payType: "1",
      cash_receipt_type: "3",
      receipt_image: null,
      receipt_type: "UNKNOWN",
      buyer: "",
      sale_id: "",
      account_id: String(selectedAccountId || ""),
    });
    setCashReceiptPreview(null);
    setCashCreateOpen(true);
  };

  const openCashCreateFromChoice = () => {
    setCashChoiceOpen(false);
    setCashForm({
      id: null,
      use_name: "",
      total: "",
      payType: "1",
      cash_receipt_type: "3",
      receipt_image: null,
      receipt_type: "UNKNOWN",
      buyer: "",
      sale_id: "",
      account_id: String(selectedAccountId || ""),
    });
    setCashReceiptPreview(null);
    setCashCreateOpen(true);
  };

  const openCashListFromChoice = async () => {
    setCashChoiceOpen(false);

    try {
      Swal.fire({
        title: "불러오는 중",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const list = await fetchCashPaymentList(selectedAccountId, cashContext.dateStr);
      Swal.close();

      const safe = Array.isArray(list) ? list : [];
      const normalized = safe.map((r) => ({
        ...r,
        cash_receipt_type: String(r.cash_receipt_type ?? r.cashReceiptType ?? "3"),
      }));
      setCashRows(normalized);

      const deep = normalized.map((r) => ({ ...r }));
      setCashEditRows(deep);
      setCashOrigRowsForDiff(JSON.parse(JSON.stringify(deep)));
      setCashRowFiles({});

      setCashListOpen(true);
    } catch (e) {
      Swal.close();
      Swal.fire("오류", e.message || "목록 조회 중 오류", "error");
    }
  };

  const selectCashRowForInlineEdit = (rowObj, rowKey) => {
    setCashSelectedRow(rowObj);
    setCashSelectedKey(rowKey);

    setCashForm({
      id: rowObj.id,
      use_name: rowObj.use_name || "",
      total: String(rowObj.total ?? ""),
      payType: String(rowObj.payType ?? "1"),
      cash_receipt_type: String(rowObj.cash_receipt_type ?? rowObj.cashReceiptType ?? "3"),
      receipt_image: null,
      receipt_type: rowObj.receipt_type || "UNKNOWN",
      buyer: rowObj.buyer || "",
      sale_id: String(rowObj.sale_id ?? ""),
      account_id: String(rowObj.account_id ?? selectedAccountId ?? ""),
    });

    setCashReceiptPreview(toPreviewUrl(rowObj.receipt_image));
  };

  useEffect(() => {
    if (!cashListOpen) return;
    if (cashSelectedRow) return;
    if (!Array.isArray(cashRows) || cashRows.length === 0) return;

    const first = cashRows[0];
    const rowKey = String(first.id ?? first.sale_id ?? 0);
    selectCashRowForInlineEdit(first, rowKey);
  }, [cashListOpen, cashRows, cashSelectedRow]);

  // ======================== ✅ 기타 type 공통 모달 플로우 (목록에서 바로 수정/저장) ========================
  const [otherChoiceOpen, setOtherChoiceOpen] = useState(false);
  const [otherCreateOpen, setOtherCreateOpen] = useState(false);
  const [otherListOpen, setOtherListOpen] = useState(false);
  const otherFileRef = useRef(null);

  const [headOfficeCardList, setHeadOfficeCardList] = useState([]);
  const [headOfficeCardLoading, setHeadOfficeCardLoading] = useState(false);

  const fetchHeadOfficeCardList = useCallback(async () => {
    setHeadOfficeCardLoading(true);
    try {
      const res = await api.get("/Account/HeadOfficeCorporateCardList", { validateStatus: () => true });
      if (res.status !== 200) throw new Error(res.data?.message || "본사 법인카드 목록 조회 실패");
      const raw = res.data;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const list = Array.isArray(parsed) ? parsed : parsed?.data || [];
      const result = Array.isArray(list) ? list : [];
      setHeadOfficeCardList(result);
      return result;
    } catch (e) {
      setHeadOfficeCardList([]);
      return [];
    } finally {
      setHeadOfficeCardLoading(false);
    }
  }, []);

  const [otherContext, setOtherContext] = useState({
    isSecond: false,
    rowIndex: null,
    colKey: null,
    dayIndex: null,
    dateStr: "",
    cellValue: 0,
    type: "",
    rowName: "",
  });

  const [otherRows, setOtherRows] = useState([]);
  const [otherSelectedRow, setOtherSelectedRow] = useState(null);
  const [otherSelectedKey, setOtherSelectedKey] = useState(null);

  // ======================== ✅ (기타 type) List: 전체 행 편집용 상태 ========================
  const [otherEditRows, setOtherEditRows] = useState([]);
  const [otherOrigRowsForDiff, setOtherOrigRowsForDiff] = useState([]);
  const [otherRowFiles, setOtherRowFiles] = useState({}); // { [rowKey]: {file, previewUrl} }

  const [otherForm, setOtherForm] = useState({
    id: null,
    use_name: "",
    total: "",
    receipt_image: null,
    receipt_image2: "",
    receipt_image3: "",
    receipt_files: [],
    receipt_type: "UNKNOWN",
    sale_id: "",
    account_id: "",
    type: "",
    card_idx: "",
  });

  const [otherReceiptPreview, setOtherReceiptPreview] = useState(null);
  const [otherReceiptPreviewList, setOtherReceiptPreviewList] = useState([]);

  const handleOtherReceiptFileChange = (e) => {
    const isCorpCard =
      String(otherForm.type ?? otherContext.type ?? "") === "1002" ||
      String(otherForm.type ?? otherContext.type ?? "") === "1003";
    const files = Array.from(e.target.files || [])
      .filter(Boolean)
      .sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "ko", {
          numeric: true,
          sensitivity: "base",
        })
      )
      .slice(0, isCorpCard ? 1 : 3);
    if (!files.length) return;
    e.currentTarget.value = "";

    closeFloatingPreview();
    (otherReceiptPreviewList || []).forEach((url) => {
      if (url && String(url).startsWith("blob:")) URL.revokeObjectURL(url);
    });

    const previewList = files.map((file) => URL.createObjectURL(file));
    setOtherForm((p) => ({
      ...p,
      receipt_image: files[0] || null,
      receipt_files: files,
      receipt_image2: files.length >= 2 ? "" : p.receipt_image2,
      receipt_image3: files.length >= 3 ? "" : p.receipt_image3,
    }));
    setOtherReceiptPreview(previewList[0] || null);
    setOtherReceiptPreviewList(previewList);
  };

  useEffect(() => {
    return () => {
      (otherReceiptPreviewList || []).forEach((previewUrl) => {
        if (previewUrl && String(previewUrl).startsWith("blob:")) URL.revokeObjectURL(previewUrl);
      });
    };
  }, [otherReceiptPreviewList]);

  // ======================== ✅ 집계표 결제 목록 조회 (type별 분기) ========================
  const fetchOtherPurchaseList = async (accountId, dateStr, typeValue) => {
    const t = String(typeValue ?? "");

    // ✅ type 1002(기타-소모품) / 1003(기타-식자재): tb_headoffice_corporate_card_payment_list 조회
    // - SQL이 year/month 기준 조회 → dateStr(YYYY-MM-DD)에서 분리해서 전달
    // - 해당 day 건만 프론트에서 필터링 (SQL에 day 조건 없음)
    // - 응답이 JSON 문자열로 오므로 파싱 처리
    if (t === "1002" || t === "1003") {
      const [year, month, day] = String(dateStr).split("-");
      const res = await api.get(ENDPOINT_CORP_CARD_LIST, {
        params: { account_id: accountId, year, month, type: t },
        validateStatus: () => true,
      });
      if (res.status !== 200) throw new Error(res.data?.message || "목록 조회 실패");
      const raw = res.data;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      const list = Array.isArray(parsed) ? parsed : [];
      // detail 합계(detail_total)가 있으면 total로 사용
      return list
        .filter((r) => String(r.payment_dt || "").slice(8, 10) === day)
        .map((r) => ({
          ...r,
          total: r.detail_total != null && Number(r.detail_total) !== 0
            ? Number(r.detail_total)
            : Number(r.total ?? 0),
        }));
    }

    // ✅ 나머지 type: tb_account_purchase_tally 조회 (saleDate + type 기준)
    const res = await api.get(ENDPOINT_OTHER_LIST, {
      params: {
        account_id: accountId,
        saleDate: dateStr,
        type: typeValue,
      },
      validateStatus: () => true,
    });
    if (res.status !== 200) throw new Error(res.data?.message || "목록 조회 실패");
    return res.data || [];
  };

  const saveOtherPayment = async (mode) => {
    if (otherContext.dayIndex == null) {
      Swal.fire("오류", "날짜 정보가 없습니다. (셀을 다시 클릭 후 저장하세요)", "error");
      return false;
    }

    const fd = new FormData();

    fd.append("user_id", localUserId);

    const submitAccountId =
      mode === "edit"
        ? String(otherForm.account_id || selectedAccountId)
        : String(selectedAccountId);

    fd.append("account_id", submitAccountId);

    const y = otherContext.isSecond ? prevYear : year;
    const m = otherContext.isSecond ? prevMonth : month;
    const fixedCellDate = buildCellDate(y, m, otherContext.dayIndex ?? 0);

    const cellDay = String((otherContext.dayIndex ?? 0) + 1);
    fd.append("cell_day", cellDay);

    const t = String(otherForm.type ?? otherContext.type ?? "");
    const isCorpCard = t === "1002" || t === "1003";
    const otherReceiptType = String(otherForm.receipt_type || "UNKNOWN");

    if (isCorpCard) {
      const noCard = !otherForm.card_idx;
      const noType = otherReceiptType === "UNKNOWN";
      if (noCard && noType) {
        Swal.fire("안내", "영수증 타입과 카드를 선택해주세요.", "warning");
        return false;
      }
      if (noCard) {
        Swal.fire("안내", "카드를 선택해주세요.", "warning");
        return false;
      }
      if (noType) {
        Swal.fire("안내", "영수증 타입을 선택해주세요.", "warning");
        return false;
      }
    } else {
      if (otherReceiptType === "UNKNOWN") {
        Swal.fire("안내", "영수증 타입을 선택해주세요.", "warning");
        return false;
      }
    }

    // ✅ type 1002/1003: 파일 1장만 / 나머지 type: 최대 3장
    const selectedReceiptFiles = Array.isArray(otherForm.receipt_files)
      ? otherForm.receipt_files.filter((f) => f instanceof File).slice(0, isCorpCard ? 1 : 3)
      : [];
    const uploadFiles =
      selectedReceiptFiles.length > 0
        ? selectedReceiptFiles
        : otherForm.receipt_image instanceof File
          ? [otherForm.receipt_image]
          : [];

    if (isCorpCard) {
      const pickedCard = (headOfficeCardList || []).find((c) => String(c.idx) === String(otherForm.card_idx || ""));
      if (uploadFiles[0]) fd.append("file", uploadFiles[0]);
      fd.append("receiptType", otherReceiptType);
      fd.append("objectValue", submitAccountId);
      fd.append("folderValue", "acnCorporate");
      fd.append("cardNo", pickedCard?.card_no || "");
      fd.append("cardBrand", pickedCard?.card_brand || "");
      fd.append("saveType", "headoffice");
      fd.append("type", otherReceiptType);
      fd.append("tallyType", t);
      fd.append("receipt_type", otherReceiptType);
      fd.append("use_name", otherForm.use_name || "");
      fd.append("total", String(parseNumber(otherForm.total)));
      fd.append("cell_date", fixedCellDate);
      if (otherForm.sale_id) fd.append("sale_id", String(otherForm.sale_id));
    } else {
      fd.append("cell_date", fixedCellDate);
      fd.append("type", otherForm.type);
      fd.append("receipt_type", otherForm.receipt_type || "UNKNOWN");
      fd.append("use_name", otherForm.use_name || "");
      fd.append("total", parseNumber(otherForm.total));
      uploadFiles.forEach((f) => fd.append("files", f));
    }

    if (mode !== "edit" && uploadFiles.length === 0) {
      Swal.fire("안내", "영수증 1장을 등록해주세요.", "warning");
      return false;
    }

    if (mode === "edit") {
      if (otherForm.id != null) fd.append("id", otherForm.id);
      if (otherForm.sale_id) fd.append("sale_id", String(otherForm.sale_id));
      fd.append("row_account_id", submitAccountId);
    }

    try {
      Swal.fire({
        title: "저장 중 입니다.",
        text: "잠시만 기다려 주세요...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_LOADING_CUSTOM_CLASS,
        willOpen: ensureSwalLoadingStyle,
        didOpen: showSwalLoadingWithoutConfirm,
      });

      // ✅ type 1002/1003: corporatecardsheet.js와 동일하게 /Corporate/receipt-scan
      // ✅ 나머지 type: /receipt-scan
      const endpoint = isCorpCard ? ENDPOINT_CORP_CARD_SAVE : ENDPOINT_OTHER_SAVE;
      const res = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
        validateStatus: () => true,
      });

      Swal.close();

      if (res.status === 200) {
        Swal.fire("완료", mode === "edit" ? "수정되었습니다." : "등록되었습니다.", "success");
        // 저장 API 내부에서 손익 집계와 소모품 예산 누계를 함께 갱신

        await fetchDataRows?.(selectedAccountId, year, month);
        await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
        await fetchBudgetGrant?.(selectedAccountId, year, month);
        await fetchBudget2Grant?.(selectedAccountId, year, month);

        setOriginalRows([]);
        setOriginal2Rows([]);

        if (otherFileRef.current) otherFileRef.current.value = "";
        setOtherForm((p) => ({
          ...p,
          receipt_image: null,
          receipt_image2: "",
          receipt_image3: "",
          receipt_files: [],
        }));
        setOtherReceiptPreview(null);
        setOtherReceiptPreviewList([]);
        return true;
      }

      if (res.status === 400) {
        Swal.fire("실패", res.data?.message || "저장에 실패했습니다.", "error");
        return false;
      }

      Swal.fire(
        "오류",
        res.data?.message || `예상치 못한 오류가 발생했습니다. (code: ${res.status})`,
        "error"
      );
      return false;
    } catch (err) {
      Swal.close();
      Swal.fire("오류", err.message || "저장 중 문제가 발생했습니다.", "error");
      return false;
    }
  };

  const handleOtherCellClick = async (rowOriginal, rIdx, colKey, isSecond = false) => {
    if (!rowOriginal || rowOriginal.name === "총합") return;
    if (colKey === "name" || colKey === "total") return;

    const t = String(rowOriginal.type ?? "");
    if (!t) return;

    const rows = isSecond ? data2Rows : dataRows;
    const cellVal = parseNumber(rows?.[rIdx]?.[colKey]);

    const dayIndex = Number(String(colKey).replace("day_", "")) - 1;
    if (Number.isNaN(dayIndex) || dayIndex < 0) return;

    const y = isSecond ? prevYear : year;
    const m = isSecond ? prevMonth : month;
    const dateStr = buildCellDate(y, m, dayIndex);

    setOtherContext({
      isSecond,
      rowIndex: rIdx,
      colKey,
      dayIndex,
      dateStr,
      cellValue: cellVal,
      type: t,
      rowName: String(rowOriginal?.name ?? ""),
    });
    try {
      Swal.fire({
        title: "확인 중",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const list = await fetchOtherPurchaseList(selectedAccountId, dateStr, t);
      Swal.close();

      const safe = Array.isArray(list) ? list : [];
      if (safe.length > 0) {
        // 이미 등록됨 -> 선택 모달(등록/수정)
        setOtherChoiceOpen(true);
        return;
      }
    } catch (e) {
      Swal.close();
      Swal.fire("오류", e.message || "목록 조회 중 오류", "error");
    }

    // 목록이 없으면 신규 등록
    if (t === "1002" || t === "1003") fetchHeadOfficeCardList();
    setOtherForm({
      id: null,
      use_name: "",
      total: "",
      receipt_image: null,
      receipt_image2: "",
      receipt_image3: "",
      receipt_files: [],
      receipt_type: "UNKNOWN",
      sale_id: "",
      account_id: String(selectedAccountId || ""),
      type: t,
      card_idx: "",
    });
    setOtherReceiptPreview(null);
    setOtherReceiptPreviewList([]);
    setOtherCreateOpen(true);
  };

  const openOtherCreateFromChoice = () => {
    const t = String(otherContext.type || "");
    setOtherChoiceOpen(false);
    if (t === "1002" || t === "1003") fetchHeadOfficeCardList();
    setOtherForm({
      id: null,
      use_name: "",
      total: "",
      receipt_image: null,
      receipt_image2: "",
      receipt_image3: "",
      receipt_files: [],
      receipt_type: "UNKNOWN",
      sale_id: "",
      account_id: String(selectedAccountId || ""),
      type: t,
      card_idx: "",
    });
    setOtherReceiptPreview(null);
    setOtherReceiptPreviewList([]);
    setOtherCreateOpen(true);
  };

  const openOtherListFromChoice = async () => {
    setOtherChoiceOpen(false);

    try {
      Swal.fire({
        title: "불러오는 중",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const list = await fetchOtherPurchaseList(
        selectedAccountId,
        otherContext.dateStr,
        otherContext.type
      );
      Swal.close();

      const safe = Array.isArray(list) ? list : [];
      setOtherRows(safe);

      const t = String(otherContext.type || "");
      let cardList = headOfficeCardList;
      if (t === "1002" || t === "1003") {
        cardList = await fetchHeadOfficeCardList();
      }

      const deep = safe.map((r) => {
        if (t !== "1002" && t !== "1003") return { ...r };
        const cardNo = String(r.cardNo || r.card_no || "").replace(/[-\s]/g, "");
        const matched = cardList.find((c) => String(c.card_no || "").replace(/[-\s]/g, "") === cardNo);
        return { ...r, card_idx: matched ? String(matched.idx) : "" };
      });
      setOtherEditRows(deep);
      setOtherOrigRowsForDiff(JSON.parse(JSON.stringify(deep)));
      setOtherRowFiles({});

      setOtherListOpen(true);
    } catch (e) {
      Swal.close();
      Swal.fire("오류", e.message || "목록 조회 중 오류", "error");
    }
  };

  const selectOtherRowForInlineEdit = (rowObj, rowKey) => {
    setOtherSelectedRow(rowObj);
    setOtherSelectedKey(rowKey);

    setOtherForm({
      id: rowObj.id ?? null,
      use_name: rowObj.use_name || "",
      total: String(rowObj.total ?? ""),
      receipt_image: null,
      receipt_image2: String(rowObj.receipt_image2 || ""),
      receipt_image3: String(rowObj.receipt_image3 || ""),
      receipt_files: [],
      receipt_type: rowObj.receipt_type || "UNKNOWN",
      sale_id: String(rowObj.sale_id ?? ""),
      account_id: String(rowObj.account_id ?? selectedAccountId ?? ""),
      type: String(otherContext.type || rowObj.type || ""),
    });

    setOtherReceiptPreview(toPreviewUrl(rowObj.receipt_image));
    setOtherReceiptPreviewList([]);
  };

  useEffect(() => {
    if (!otherListOpen) return;
    if (otherSelectedRow) return;
    if (!Array.isArray(otherRows) || otherRows.length === 0) return;

    const first = otherRows[0];
    const rowKey = String(first.id ?? first.sale_id ?? 0);
    selectOtherRowForInlineEdit(first, rowKey);
  }, [otherListOpen, otherRows, otherSelectedRow]);

  // ✅ 직접 입력은 type=1만 허용
  // ✅ 직접 입력 허용 타입 (1~4, 1042)
  const INLINE_EDIT_TYPES = useMemo(() => new Set(["1", "2", "3", "4", "1042"]), []);

  // ======================== ✅ 직접 입력이 아닌 타입별 모달 라우팅 ========================
  const handleSpecialCellClick = useCallback(
    (rowOriginal, rIdx, colKey, isSecond) => {
      if (!rowOriginal || rowOriginal.name === "총합") return;
      if (colKey === "name" || colKey === "total") return;

      setActiveCell({ isSecond: !!isSecond, rowIndex: rIdx, colKey });

      const t = String(rowOriginal.type ?? "");
      if (!t) return;

      // ✅ type 1~4 는 직접 입력 대상이므로 모달/기타 클릭로직 타지 않게 종료
      if (INLINE_EDIT_TYPES.has(t)) return;

      if (t === "1000") {
        handleCorpCardCellClick(rowOriginal, rIdx, colKey, isSecond);
        return;
      }

      if (t === "1008") {
        handleCashCellClick(rowOriginal, rIdx, colKey, isSecond);
        return;
      }

      handleOtherCellClick(rowOriginal, rIdx, colKey, isSecond);
    },
    [
      INLINE_EDIT_TYPES,
      handleCorpCardCellClick,
      handleCashCellClick,
      handleOtherCellClick,
    ]
  );

  // ======================== 컬럼 구성 ========================
  const buildColumns = useCallback((daysCount) => {
    const dayColumns = Array.from({ length: daysCount }, (_, i) => ({
      header: `${i + 1}일`,
      accessorKey: `day_${i + 1}`,
      size: 100,
    }));

    return [
      { header: "구분", accessorKey: "name", size: 100 },
      ...dayColumns,
      { header: "합계", accessorKey: "total", size: 100 },
    ];
  }, []);

  const columnsNow = useMemo(() => buildColumns(daysInMonthNow), [buildColumns, daysInMonthNow]);
  const columnsPrev = useMemo(() => buildColumns(daysInMonthPrev), [buildColumns, daysInMonthPrev]);

  const makeTableData = (rows, daysCount) => {
    if (!rows || rows.length === 0) return [];

    const calculatedRows = rows.map((r) => {
      const total = Array.from({ length: daysCount }, (_, i) =>
        parseNumber(r[`day_${i + 1}`])
      ).reduce((sum, val) => sum + val, 0);

      return { ...r, total };
    });

    const totals = {};
    for (let i = 1; i <= daysCount; i++) {
      totals[`day_${i}`] = calculatedRows.reduce((sum, r) => sum + parseNumber(r[`day_${i}`]), 0);
    }

    const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

    return [...calculatedRows, { name: "총합", ...totals, total: grandTotal }];
  };

  // ----------- ✅ 행(row) type 노출/숨김 규칙 (이 목록만 수정하면 됨) -----------
  // mode: "onlyMonth"     → 지정된 year/month 에만 노출, 그 외 월은 숨김
  // mode: "hiddenFromMonth" → 지정된 year/month부터(이후 포함) 숨김, 그 이전은 계속 노출
  // accountId를 지정하면 해당 거래처에만 규칙이 적용됨 (미지정 시 전체 거래처 공통 적용)
  const ROW_TYPE_VISIBILITY_RULES = [
    { mode: "onlyMonth", types: ["1030", "1031"], year: 2026, month: 7 },
    { mode: "onlyMonth", types: ["1010"], year: 2026, month: 1 },
    { mode: "hiddenFromMonth", types: ["3", "4"], year: 2026, month: 5, accountId: "20250819193630" }, // 강남(웰스토리)
  ];

  const filterRowsByTypeVisibility = (rows, targetYear, targetMonth) => {
    const y = Number(targetYear);
    const m = Number(targetMonth);
    const accountId = String(selectedAccountId).trim();
    return (rows || []).filter((r) => {
      const t = String(r.type);
      return ROW_TYPE_VISIBILITY_RULES.every((rule) => {
        if (!rule.types.includes(t)) return true;
        if (rule.accountId && rule.accountId !== accountId) return true;
        if (rule.mode === "onlyMonth") return y === rule.year && m === rule.month;
        if (rule.mode === "hiddenFromMonth") {
          const isAfterCutoff = y > rule.year || (y === rule.year && m >= rule.month);
          return !isAfterCutoff;
        }
        return true;
      });
    });
  };
  // ----------------------------------------------------------------------

  const tableData = useMemo(
    () => makeTableData(filterRowsByTypeVisibility(dataRows, year, month), daysInMonthNow),
    [dataRows, daysInMonthNow, selectedAccountId, year, month]
  );
  const table2Data = useMemo(
    () => makeTableData(filterRowsByTypeVisibility(data2Rows, prevYear, prevMonth), daysInMonthPrev),
    [data2Rows, daysInMonthPrev, selectedAccountId, prevYear, prevMonth]
  );

  const table = useReactTable({
    data: tableData,
    columns: columnsNow,
    getCoreRowModel: getCoreRowModel(),
  });
  const table2 = useReactTable({
    data: table2Data,
    columns: columnsPrev,
    getCoreRowModel: getCoreRowModel(),
  });

  // type 1002(소모품)는 소모품 바에서 따로 표시 → 식자재 합계에서 제외
  const usedTotalNow = useMemo(() => {
    return filterRowsByTypeVisibility(dataRows, year, month)
      .filter((r) => String(r?.type) !== "1002")
      .reduce((sum, r) => {
        return sum + Array.from({ length: daysInMonthNow }, (_, i) =>
          parseNumber(r[`day_${i + 1}`])
        ).reduce((a, b) => a + b, 0);
      }, 0);
  }, [dataRows, daysInMonthNow, selectedAccountId, year, month]);

  const usedTotalPrev = useMemo(() => {
    return filterRowsByTypeVisibility(data2Rows, prevYear, prevMonth)
      .filter((r) => String(r?.type) !== "1002")
      .reduce((sum, r) => {
        return sum + Array.from({ length: daysInMonthPrev }, (_, i) =>
          parseNumber(r[`day_${i + 1}`])
        ).reduce((a, b) => a + b, 0);
      }, 0);
  }, [data2Rows, daysInMonthPrev, selectedAccountId, prevYear, prevMonth]);

  const budgetForTab = tabValue === 1 ? budget2Grant : budgetGrant;
  const usedForTab = tabValue === 1 ? usedTotalPrev : usedTotalNow;
  const suppliesBudgetForTab = tabValue === 1 ? supplies2Budget : suppliesBudget;
  const suppliesUsedForTab = tabValue === 1 ? supplies2Used : suppliesUsed;

  // ✅ 직접 입력은 type=1~4, 1042 허용
  const handleCellChange = (rowIndex, colKey, value, isSecond = false) => {
    const rows = isSecond ? data2Rows : dataRows;
    const row = rows?.[rowIndex];
    if (!row || row.name === "총합" || colKey === "name" || colKey === "total") return;

    // ✅ FIX: y, m 정의 (현재월/전월 기준)
    const y = isSecond ? prevYear : year;
    const m = isSecond ? prevMonth : month;

    // ✅ 추가: input_yn=1이면 입력 차단
    if (isTypeLocked(y, m, String(row.type ?? ""))) return;

    if (!INLINE_EDIT_TYPES.has(String(row.type ?? ""))) return;

    const setter = isSecond ? setData2Rows : setDataRows;
    const newValue = parseNumber(value);
    setter?.(rows.map((r, i) => (i === rowIndex ? { ...r, [colKey]: newValue } : r)));
  };

  const handleImageUpload = async (e, dayIndex) => {
    if (dayIndex >= daysInMonthNow) {
      return Swal.fire("경고", "해당 월의 날짜 범위를 초과했습니다.", "info");
    }

    const typeForDay = receiptType[dayIndex];
    if (!typeForDay) return Swal.fire("경고", "영수증 유형을 선택하세요.", "info");

    const file = e.target.files?.[0];
    if (!file) return;

    setImages((prev) => {
      const newImages = [...prev];
      newImages[dayIndex] = file;
      return newImages;
    });

    const day = dayIndex + 1;
    const selectedDate = dayjs(
      `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    );

    const formData = new FormData();
    formData.append("user_id", localUserId);
    formData.append("file", file);
    formData.append("type", typeForDay);
    formData.append("account_id", selectedAccountId);
    formData.append("cell_day", String(day));
    formData.append("cell_date", selectedDate.format("YYYY-MM-DD"));

    try {
      Swal.fire({
        title: "영수증 확인 중 입니다.",
        text: "잠시만 기다려 주세요...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      const res = await api.post("/receipt-scan", formData, {
        headers: { "Content-Type": "multipart/form-data", Accept: "application/json" },
        validateStatus: () => true,
      });

      Swal.close();

      if (res.status === 200) {
        Swal.fire("완료", "영수증 확인이 완료되었습니다.", "success");

        await fetchDataRows?.(selectedAccountId, year, month);
        await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
        await fetchBudgetGrant?.(selectedAccountId, year, month);
        await fetchBudget2Grant?.(selectedAccountId, year, month);

        setOriginalRows([]);
        setOriginal2Rows([]);
      } else if (res.status === 400) {
        Swal.fire("실패", res.data?.message || "영수증 인식에 실패했습니다.", "error");
      } else {
        Swal.fire(
          "오류",
          res.data?.message || `예상치 못한 오류가 발생했습니다. (code: ${res.status})`,
          "error"
        );
      }
    } catch (err) {
      Swal.close();
      Swal.fire("오류", err.message || "영수증 확인 중 문제가 발생했습니다.", "error");
    } finally {
      e.target.value = "";
    }
  };

  const isSavingRef = useRef(false);
  const handleSave = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    const getChangedRows = (curr, orig) =>
      (curr || [])
        .map((row, idx) => {
          const changed = {};
          let hasChange = false;

          Object.keys(row || {}).forEach((k) => {
            if (["name", "total"].includes(k) || row.name === "총합") return;
            if (parseNumber(row[k]) !== parseNumber(orig?.[idx]?.[k])) {
              changed[k] = parseNumber(row[k]);
              hasChange = true;
            }
          });

          return hasChange ? { ...row, ...changed } : null;
        })
        .filter(Boolean);

    const changedNow = getChangedRows(dataRows, originalRows);
    const changedBefore = getChangedRows(data2Rows, original2Rows);

    if (!changedNow.length && !changedBefore.length) {
      return Swal.fire("정보", "변경된 내용이 없습니다.", "info");
    }

    // ✅ 여기서 row마다 user_id 주입
    const nowList = changedNow.map((r) => ({ ...r, user_id: localUserId }));
    const beforeList = changedBefore.map((r) => ({ ...r, user_id: localUserId }));

    try {
      const payload = { user_id: localUserId, nowList: nowList, beforeList: beforeList };
      const res = await api.post("/Operate/TallySheetSave", payload);

      if (res.data?.code === 200) {
        Swal.fire({
          title: "저장",
          text: "저장되었습니다.",
          icon: "success",
          confirmButtonColor: "#d33",
          confirmButtonText: "확인",
        }).then(async (result) => {
          if (result.isConfirmed) {
            await fetchDataRows?.(selectedAccountId, year, month);
            await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
            await fetchBudgetGrant?.(selectedAccountId, year, month);
            await fetchBudget2Grant?.(selectedAccountId, year, month);
            setOriginalRows([]);
            setOriginal2Rows([]);
          }
        });
      } else {
        Swal.fire("실패", res.data?.message || "저장 실패", "error");
      }
    } catch (e) {
      Swal.fire("실패", e.message || "저장 중 오류 발생", "error");
    } finally {
      isSavingRef.current = false;
    }
  };

  // ✅ 새로고침: 선택된 거래처 기준으로 데이터 재조회
  const handleRefresh = async () => {
    if (!selectedAccountId) return;
    try {
      await fetchDataRows?.(selectedAccountId, year, month);
      await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
      await fetchBudgetGrant?.(selectedAccountId, year, month);
      await fetchBudget2Grant?.(selectedAccountId, year, month);
      // ✅ 추가
      await fetchUseList?.(selectedAccountId, year, month);
      await fetchTallyNote?.(selectedAccountId, activeNoteYear, activeNoteMonth);
      setOriginalRows([]);
      setOriginal2Rows([]);
      setImages(Array(daysInMonthNow).fill(null));
      setReceiptType(Array(daysInMonthNow).fill(""));
    } catch (e) {
      Swal.fire("실패", e.message || "새로고침 중 오류가 발생했습니다.", "error");
    }
  };

  const ratioDataNow = useMemo(() => {
    return Array.from(
      { length: daysInMonthNow },
      (_, i) => (((i + 1) / daysInMonthNow) * 100).toFixed(2) + "%"
    );
  }, [daysInMonthNow]);

  const ratioDataPrev = useMemo(() => {
    return Array.from(
      { length: daysInMonthPrev },
      (_, i) => (((i + 1) / daysInMonthPrev) * 100).toFixed(2) + "%"
    );
  }, [daysInMonthPrev]);

  // ✅ 엑셀 컬럼명(A, B, ... AA) 변환
  const toExcelColName = useCallback((index) => {
    let col = "";
    let n = Number(index);
    while (n > 0) {
      const rem = (n - 1) % 26;
      col = String.fromCharCode(65 + rem) + col;
      n = Math.floor((n - 1) / 26);
    }
    return col || "A";
  }, []);

  // ✅ 집계표 시트 생성(현재월/전월 공용)
  const buildTallyExcelSheet = useCallback(
    (
      workbook,
      // targetSheet: 기존 시트에 이어 붙일 때 전달 (없으면 새 시트 생성)
      // rowOffset: 기존 시트에 이어 쓸 때 시작 행 오프셋 (0 = 첫 행부터)
      { sheetName, accountName, reportDateLabel, budget, used, ratioData, tableRows, daysCount,
        targetSheet = null, rowOffset = 0 }
    ) => {
      // targetSheet가 있으면 재사용, 없으면 새 시트 생성
      const ws = targetSheet || workbook.addWorksheet(sheetName);
      const totalCols = daysCount + 2; // 구분 + 일자들 + 합계

      const borderThin = {
        top: { style: "thin", color: { argb: "FF686D76" } },
        left: { style: "thin", color: { argb: "FF686D76" } },
        bottom: { style: "thin", color: { argb: "FF686D76" } },
        right: { style: "thin", color: { argb: "FF686D76" } },
      };

      const budgetNum = parseNumber(budget);
      const usedNum = parseNumber(used);
      const ratioNum = budgetNum > 0 ? (usedNum / budgetNum) * 100 : 0;
      const ratioText = `${ratioNum.toFixed(2)}%`;

      // 행 번호: rowOffset 기준 상대 위치
      const titleRowNo = rowOffset + 1;
      const summaryRowNo = rowOffset + 2;
      // rowOffset + 3 은 공백행
      const ratioRowNo = rowOffset + 4;
      const headerRowNo = rowOffset + 5;
      const dataStartRowNo = rowOffset + 6;

      // 제목: ■ {업장명} / {조회날짜}
      ws.getCell(`A${titleRowNo}`).value = `■ ${accountName} / ${reportDateLabel}`;
      ws.mergeCells(titleRowNo, 1, titleRowNo, totalCols);
      ws.getCell(`A${titleRowNo}`).font = { bold: true, size: 12 };
      ws.getCell(`A${titleRowNo}`).alignment = { vertical: "middle", horizontal: "left" };
      ws.getRow(titleRowNo).height = 24;

      // 요약행: 식자재 월 예산 / 사용 금액 / 예산대비 퍼센트
      ws.getRow(summaryRowNo).values = ["식자재 월 예산", budgetNum, "사용 금액", usedNum, "예산대비 퍼센트", ratioText];
      for (let c = 1; c <= Math.min(6, totalCols); c++) {
        const cell = ws.getCell(summaryRowNo, c);
        cell.border = borderThin;
        cell.alignment = { vertical: "middle", horizontal: [2, 4, 6].includes(c) ? "right" : "center" };
        if ([1, 3, 5].includes(c)) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } };
          cell.font = { bold: true };
        }
      }
      ws.getCell(summaryRowNo, 2).numFmt = "#,##0";
      ws.getCell(summaryRowNo, 4).numFmt = "#,##0";

      // 일 사용기준 % 행
      ws.getCell(ratioRowNo, 1).value = "일 사용기준 %";
      ratioData.forEach((val, idx) => {
        ws.getCell(ratioRowNo, idx + 2).value = val;
      });
      ws.getCell(ratioRowNo, totalCols).value = "";

      // 헤더 행 (구분 + 1일~N일 + 합계)
      ws.getCell(headerRowNo, 1).value = "구분";
      for (let d = 1; d <= daysCount; d++) {
        ws.getCell(headerRowNo, d + 1).value = `${d}일`;
      }
      ws.getCell(headerRowNo, totalCols).value = "합계";

      // 본문 데이터 행
      (tableRows || []).forEach((row, rowIdx) => {
        const r = dataStartRowNo + rowIdx;
        ws.getCell(r, 1).value = row?.name ?? "";
        for (let d = 1; d <= daysCount; d++) {
          const cell = ws.getCell(r, d + 1);
          cell.value = parseNumber(row?.[`day_${d}`]);
          cell.numFmt = "#,##0";
        }
        const totalCell = ws.getCell(r, totalCols);
        totalCell.value = parseNumber(row?.total);
        totalCell.numFmt = "#,##0";
      });

      const dataEndRowNo = dataStartRowNo + Math.max((tableRows || []).length - 1, 0);

      // 공통 테두리/정렬 (비율행 ~ 데이터 마지막 행)
      for (let r = ratioRowNo; r <= dataEndRowNo; r++) {
        for (let c = 1; c <= totalCols; c++) {
          const cell = ws.getCell(r, c);
          cell.border = borderThin;
          cell.alignment = { vertical: "middle", horizontal: "center" };
        }
      }

      // 일 사용기준% / 헤더 강조
      for (let c = 1; c <= totalCols; c++) {
        ws.getCell(ratioRowNo, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFE3A9" } };
        const headerCell = ws.getCell(headerRowNo, c);
        headerCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } };
        headerCell.font = { bold: true };
      }

      // 총합 행 강조
      const totalRowIdx = (tableRows || []).findIndex((r) => String(r?.name) === "총합");
      if (totalRowIdx >= 0) {
        const totalRowNo = dataStartRowNo + totalRowIdx;
        for (let c = 1; c <= totalCols; c++) {
          const cell = ws.getCell(totalRowNo, c);
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFE3A9" } };
          cell.font = { bold: true };
        }
      }

      // 컬럼 폭은 새 시트(첫 블록)에서만 설정 (targetSheet 없을 때 or rowOffset=0 일 때)
      if (!targetSheet || rowOffset === 0) {
        ws.getColumn(1).width = 19;
        for (let c = 2; c <= totalCols - 1; c++) ws.getColumn(c).width = 11;
        ws.getColumn(totalCols).width = 12;
        if (totalCols >= 5) ws.getColumn(5).width = 18;
        if (totalCols >= 2) ws.getColumn(2).width = 12;
        if (totalCols >= 4) ws.getColumn(4).width = 12;
      }

      // 이 블록이 차지한 마지막 행 번호 반환 (다음 블록 시작 위치 계산용)
      return dataEndRowNo;
    },
    [toExcelColName]
  );

  // ✅ 집계표 엑셀 다운로드 (현재월/전월 시트 동시 생성)
  const handleExcelDownload = useCallback(async () => {
    // 거래처 미선택 시 안내
    if (!selectedAccountId) {
      Swal.fire("안내", "거래처를 먼저 선택하세요.", "info");
      return;
    }

    // 현재 선택된 거래처명 (모달 옵션 라벨로 사용)
    const currentAccountName = String(selectedAccountOption?.account_name || selectedAccountId || "업장");

    // 다운로드 방식 선택 모달: "거래처 전체" 또는 현재 거래처
    const { value: mode } = await Swal.fire({
      title: "전체 다운로드 옵션",
      text: "다운로드 방식을 선택하세요.",
      input: "radio",
      inputOptions: {
        all: "거래처 전체",
        current: currentAccountName,
      },
      inputValidator: (v) => (!v ? "옵션을 선택해주세요." : undefined),
      confirmButtonText: "다운로드",
      showCancelButton: true,
      cancelButtonText: "취소",
    });

    // 취소 시 중단
    if (!mode) return;

    // 엑셀 시트 헤더에 표시할 조회 날짜 레이블
    const nowReportDateLabel = `${year}-${String(month).padStart(2, "0")}`;
    const prevReportDateLabel = `${prevYear}-${String(prevMonth).padStart(2, "0")}`;

    // ─── 현재 거래처만 다운로드 ───────────────────────────────────────────────
    if (mode === "current") {
      // 화면에 이미 로드된 데이터 사용 (현재월 / 전월)
      const hasNow = Array.isArray(tableData) && tableData.length > 0;
      const hasPrev = Array.isArray(table2Data) && table2Data.length > 0;
      if (!hasNow && !hasPrev) {
        Swal.fire("안내", "다운로드할 데이터가 없습니다.", "info");
        return;
      }

      try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "tallysheet";
        workbook.created = new Date();

        // 현재월 시트 생성
        if (hasNow) {
          buildTallyExcelSheet(workbook, {
            sheetName: `현재월(${nowReportDateLabel})`,
            accountName: currentAccountName,
            reportDateLabel: nowReportDateLabel,
            budget: budgetGrant,
            used: usedTotalNow,
            ratioData: ratioDataNow,
            tableRows: tableData,
            daysCount: daysInMonthNow,
          });
        }

        // 전월 시트 생성
        if (hasPrev) {
          buildTallyExcelSheet(workbook, {
            sheetName: `전월(${prevReportDateLabel})`,
            accountName: currentAccountName,
            reportDateLabel: prevReportDateLabel,
            budget: budget2Grant,
            used: usedTotalPrev,
            ratioData: ratioDataPrev,
            tableRows: table2Data,
            daysCount: daysInMonthPrev,
          });
        }

        // 파일명에서 특수문자 제거 후 다운로드
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const safeAccountName = currentAccountName.replace(/[\\/:*?"<>|]/g, "_");
        a.href = url;
        a.download = `집계표_${safeAccountName}_${year}-${String(month).padStart(2, "0")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        Swal.fire("엑셀 다운로드 실패", err?.message || "오류가 발생했습니다.", "error");
      }
      return;
    }

    // ─── 거래처 전체 다운로드 ─────────────────────────────────────────────────
    // 로딩 모달 표시 (API 응답 대기 중)
    Swal.fire({
      title: "다운로드 중...",
      text: "전체 업장 데이터를 불러오는 중입니다.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      // 현재월 / 전월 전체 업장 데이터를 동시에 요청 (2회 API 호출)
      // - /Operate/TallySheetAllList: account_name 오름차순 + budget_grant 포함하여 반환
      const [nowRes, prevRes] = await Promise.all([
        api.get("/Operate/TallySheetAllList", { params: { year, month } }),
        api.get("/Operate/TallySheetAllList", { params: { year: prevYear, month: prevMonth } }),
      ]);

      const nowAllRows = Array.isArray(nowRes.data) ? nowRes.data : [];
      const prevAllRows = Array.isArray(prevRes.data) ? prevRes.data : [];

      if (nowAllRows.length === 0 && prevAllRows.length === 0) {
        Swal.close();
        Swal.fire("안내", "다운로드할 데이터가 없습니다.", "info");
        return;
      }

      // 응답 rows를 account_id 기준으로 Map에 묶음
      // Map 키: account_id, 값: { account_name, rows[] }
      const groupByAccountId = (rows) => {
        const map = new Map();
        rows.forEach((r) => {
          const aid = String(r?.account_id ?? "");
          if (!aid) return;
          if (!map.has(aid)) map.set(aid, { account_name: String(r?.account_name ?? ""), rows: [] });
          map.get(aid).rows.push(r);
        });
        return map;
      };

      const nowByAccount = groupByAccountId(nowAllRows);
      const prevByAccount = groupByAccountId(prevAllRows);

      // accountList(드롭다운 기준)를 account_name 오름차순으로 정렬한 것을 기준으로 사용
      // → API 응답에 없는 업장(데이터 0건)도 포맷은 출력됨
      const allAccountIds = [...(accountList || [])]
        .sort((a, b) =>
          String(a.account_name ?? "").localeCompare(String(b.account_name ?? ""), "ko")
        )
        .map((a) => String(a.account_id));

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "tallysheet";
      workbook.created = new Date();

      // 일 사용기준 % 배열 (현재월 / 전월 일수 기준)
      const ratioNow = Array.from(
        { length: daysInMonthNow },
        (_, i) => (((i + 1) / daysInMonthNow) * 100).toFixed(2) + "%"
      );
      const ratioPrev = Array.from(
        { length: daysInMonthPrev },
        (_, i) => (((i + 1) / daysInMonthPrev) * 100).toFixed(2) + "%"
      );

      // 시트 2개 생성: 현재월 / 전월
      // 각 시트에 전체 업장 블록을 account_name 오름차순으로 이어서 작성
      const nowSheet = workbook.addWorksheet(`현재월(${nowReportDateLabel})`);
      const prevSheet = workbook.addWorksheet(`전월(${prevReportDateLabel})`);

      // 각 시트의 현재 마지막 행 위치 (다음 업장 블록 시작 rowOffset 계산용)
      let nowRowOffset = 0;
      let prevRowOffset = 0;
      const BLOCK_GAP = 1; // 업장 블록 사이 공백 행 수

      // API 응답 rows 배열 → day 컬럼에 parseNumber 적용하는 공통 변환
      const parseGroupRows = (rows) =>
        rows.map((item) => {
          const row = { account_id: item.account_id, name: item.name, type: item.type };
          for (let i = 1; i <= 31; i++) row[`day_${i}`] = parseNumber(item[`day_${i}`]);
          return row;
        });

      // 예산: 같은 업장 rows 중 budget_grant 값이 있는 첫 번째 항목에서 추출
      const pickBudget = (rows) =>
        parseNumber((rows.find((x) => x?.budget_grant != null) || rows[0])?.budget_grant);

      // accountList Map: account_id → account_name (accName fallback용)
      const accountNameMap = new Map(
        (accountList || []).map((a) => [String(a.account_id), String(a.account_name ?? "")])
      );

      for (const accId of allAccountIds) {
        const nowGroup = nowByAccount.get(accId);
        const prevGroup = prevByAccount.get(accId);
        // account_name은 accountList 기준 우선, 없으면 API 응답에서 폴백
        const accName =
          accountNameMap.get(accId) ??
          nowGroup?.account_name ??
          prevGroup?.account_name ??
          accId;

        // 현재월 시트: 데이터 없는 업장도 포맷(제목/헤더)은 출력
        {
          const rawRows = nowGroup ? parseGroupRows(nowGroup.rows) : [];
          const nowBudget = nowGroup ? pickBudget(nowGroup.rows) : 0;
          const nowTableRows = makeTableData(rawRows, daysInMonthNow);
          const nowUsed = parseNumber((nowTableRows || []).find((r) => r?.name === "총합")?.total);

          const lastRow = buildTallyExcelSheet(workbook, {
            sheetName: `현재월(${nowReportDateLabel})`,
            accountName: accName,
            reportDateLabel: nowReportDateLabel,
            budget: nowBudget,
            used: nowUsed,
            ratioData: ratioNow,
            tableRows: nowTableRows,
            daysCount: daysInMonthNow,
            targetSheet: nowSheet,
            rowOffset: nowRowOffset,
          });
          // 다음 업장 시작 위치 = 이번 블록 마지막 행 + 공백 1행
          nowRowOffset = lastRow + BLOCK_GAP;
        }

        // 전월 시트: 데이터 없는 업장도 포맷(제목/헤더)은 출력
        {
          const rawRows = prevGroup ? parseGroupRows(prevGroup.rows) : [];
          const prevBudget = prevGroup ? pickBudget(prevGroup.rows) : 0;
          const prevTableRows = makeTableData(rawRows, daysInMonthPrev);
          const prevUsed = parseNumber((prevTableRows || []).find((r) => r?.name === "총합")?.total);

          const lastRow = buildTallyExcelSheet(workbook, {
            sheetName: `전월(${prevReportDateLabel})`,
            accountName: accName,
            reportDateLabel: prevReportDateLabel,
            budget: prevBudget,
            used: prevUsed,
            ratioData: ratioPrev,
            tableRows: prevTableRows,
            daysCount: daysInMonthPrev,
            targetSheet: prevSheet,
            rowOffset: prevRowOffset,
          });
          prevRowOffset = lastRow + BLOCK_GAP;
        }
      }

      Swal.close();

      if (workbook.worksheets.length === 0) {
        Swal.fire("안내", "다운로드할 데이터가 없습니다.", "info");
        return;
      }

      // 엑셀 파일 생성 후 브라우저 다운로드
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `집계표_전체_${year}-${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Swal.close();
      Swal.fire("엑셀 다운로드 실패", err?.message || "오류가 발생했습니다.", "error");
    }
  }, [
    selectedAccountId,
    selectedAccountOption,
    accountList,
    tableData,
    table2Data,
    year,
    month,
    prevYear,
    prevMonth,
    budgetGrant,
    budget2Grant,
    usedTotalNow,
    usedTotalPrev,
    ratioDataNow,
    ratioDataPrev,
    daysInMonthNow,
    daysInMonthPrev,
    buildTallyExcelSheet,
  ]);

  // ======================== 거래처 연결/등록 ========================
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  // 거래처 등록 저장 진행 상태
  const [savingAccountRegister, setSavingAccountRegister] = useState(false);
  // 거래처 등록 저장 중복 요청 차단 값
  const savingAccountRegisterRef = useRef(false);
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState([]);
  const [selectedRight, setSelectedRight] = useState([]);

  const handleModalOpen = async () => {
    setOpen(true);
    setSelectedLeft([]);
    setSelectedRight([]);
    try {
      const leftRes = await api.get("/Operate/AccountMappingList");
      setLeftItems(leftRes.data || []);

      if (selectedAccountId) {
        const rightRes = await api.get("/Operate/AccountMappingV2List", {
          params: { account_id: selectedAccountId },
        });
        setRightItems(rightRes.data || []);
      } else {
        setRightItems([]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      Swal.fire({ title: "오류", text: "거래처 목록을 불러오지 못했습니다.", icon: "error" });
    }
  };

  const moveRight = () => {
    const duplicates = selectedLeft.filter((item) =>
      (rightItems || []).some((r) => r.type === item.type && r.del_yn === "N")
    );
    if (duplicates.length > 0) {
      Swal.fire({ title: "중복", text: "이미 등록되어 있는 항목입니다.", icon: "warning" });
      return;
    }

    const updatedRightItems = [
      ...(rightItems || []),
      ...selectedLeft.map((item) => ({ ...item, account_id: selectedAccountId, del_yn: "N" })),
    ];
    setRightItems(updatedRightItems);
    setSelectedLeft([]);
  };

  const moveLeft = () => {
    const updatedRightItems = (rightItems || []).map((item) =>
      selectedRight.includes(item) ? { ...item, del_yn: "Y" } : item
    );
    setRightItems(updatedRightItems);
    setSelectedRight([]);
  };

  const handleSubmit = async () => {
    if (!selectedAccountId) {
      return Swal.fire({ title: "계정 선택", text: "계정을 먼저 선택하세요.", icon: "warning" });
    }

    try {
      const payload = (rightItems || []).map((r) => ({ ...r, user_id: localUserId }));
      const response = await api.post("/Operate/AccountMappingSave", payload);

      if (response.data?.code === 200) {
        Swal.fire({ title: "저장", text: "저장되었습니다.", icon: "success" });
        closeModalWithPreview(setOpen);

        await fetchDataRows?.(selectedAccountId, year, month);
        await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
        setOriginalRows([]);
        setOriginal2Rows([]);
      } else {
        Swal.fire({ title: "오류", text: response.data?.message || "저장 실패", icon: "error" });
      }
    } catch (err) {
      Swal.fire({ title: "오류", text: err.message || "저장 실패", icon: "error" });
    }
  };

  // ======================= 거래처 등록 기본값 =======================
  const initialForm = {
    name: "",
    biz_no: "",
    ceo_name: "",
    tel: "",
    bank_name: "",
    bank_no: "",
    bank_image: null,
    biz_image: null,
    add_yn: "N",
    add_name: "",
    // ✅ 필수값 검사(!formData[key])에 걸리지 않도록 문자열로 보관 ("0"|"1"|"2")
    account_type: "0",
  };

  const [formData, setFormData] = useState(initialForm);
  const [imagePreviews, setImagePreviews] = useState({ bank_image: null, biz_image: null });
  // 거래처 등록 입력 라벨 공통 속성
  const accountRegisterInputLabelProps = {
    shrink: true,
    style: { fontSize: "0.75rem", lineHeight: 1.2 },
  };
  // 거래처 등록 텍스트 입력 높이 공통 스타일
  const accountRegisterTextFieldSx = {
    mt: 1,
    "& .MuiOutlinedInput-root": {
      height: 40,
      alignItems: "center",
    },
    "& .MuiInputBase-input": {
      height: "40px",
      boxSizing: "border-box",
      py: 0,
    },
    "& .MuiInputLabel-shrink": {
      backgroundColor: "#fff",
      px: 0.5,
    },
  };
  // 거래처 등록 선택 입력 높이 공통 스타일
  const accountRegisterSelectSx = {
    fontSize: "0.85rem",
    height: 40,
    "& .MuiSelect-select": {
      height: "40px",
      minHeight: "0 !important",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      py: 0,
    },
  };
  const handleImagePreviewOpen = (src, title = "파일 미리보기") => {
    openFloatingPreview(src, title);
  };

  const handleModalOpen2 = async () => setOpen2(true);
  const handleModalClose2 = async () => {
    // 저장 요청 중 거래처 등록 모달 닫힘 차단
    if (savingAccountRegisterRef.current) return;
    closeModalWithPreview(setOpen2);
  };

  const handleChange2 = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handleAddYnChange = (e) => {
    const checked = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      add_yn: checked ? "Y" : "N",
      add_name: checked ? prev.add_name || "" : "",
    }));
  };

  const handleBankSelect = (e) => {
    const bankName = e.target.value;

    setFormData((prev) => {
      if (bankName === "기타(직접입력)") {
        return {
          ...prev,
          bank_name: prev.bank_name || "",
          bank_no: formatAccountNumber(prev.bank_name || "", prev.bank_no || ""),
        };
      }
      return {
        ...prev,
        bank_name: bankName,
        bank_no: formatAccountNumber(bankName, prev.bank_no || ""),
      };
    });
  };

  const handleBankNoChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, bank_no: formatAccountNumber(prev.bank_name || "", value) }));
  };

  const handleBizNoChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, biz_no: formatBizNo(value) }));
  };

  const handleTelChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, tel: formatPhone(value) }));
  };

  const uploadAccountFileImmediately = useCallback(
    async (field, file) => {
      const formDataToSend = new FormData();
      formDataToSend.append("user_id", localUserId);
      formDataToSend.append("file", file);
      formDataToSend.append("type", "account");
      formDataToSend.append("gubun", field);
      formDataToSend.append("folder", selectedAccountId || "common");

      const res = await api.post("/Operate/OperateImgUpload", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.code === 200) return res.data.image_path;
      throw new Error(res.data?.message || "파일 업로드 실패");
    },
    [localUserId, selectedAccountId]
  );

  const handleImageUploadPreview = async (e) => {
    const { name, files } = e.target;
    const file = files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setImagePreviews((prev) => {
      const oldPreview = prev?.[name];
      if (oldPreview && String(oldPreview).startsWith("blob:")) URL.revokeObjectURL(oldPreview);
      return { ...prev, [name]: previewUrl };
    });
    setFormData((prev) => ({ ...prev, [name]: file }));

    try {
      const uploadedPath = await uploadAccountFileImmediately(name, file);
      setFormData((prev) => ({ ...prev, [name]: uploadedPath }));
      setImagePreviews((prev) => {
        const oldPreview = prev?.[name];
        if (oldPreview && String(oldPreview).startsWith("blob:")) URL.revokeObjectURL(oldPreview);
        return { ...prev, [name]: toPreviewUrl(uploadedPath) || uploadedPath };
      });
    } catch (err) {
      Swal.fire("업로드 실패", err.message || "파일 업로드 중 오류가 발생했습니다.", "error");
    } finally {
      e.target.value = "";
    }
  };

  const handleSubmit2 = async () => {
    // 거래처 등록 저장 중복 요청 차단
    if (savingAccountRegisterRef.current) return;

    const requiredFields = [
      "name",
      "biz_no",
      "ceo_name",
      "tel",
      "bank_name",
      "bank_no",
      "bank_image",
      "biz_image",
      "account_type",
    ];

    const missing = requiredFields.filter((key) => !formData[key]);
    if (missing.length > 0) {
      return Swal.fire({
        title: "경고",
        text: "필수항목을 모두 입력하세요.",
        icon: "error",
        confirmButtonColor: "#d33",
        confirmButtonText: "확인",
      });
    }

    savingAccountRegisterRef.current = true;
    setSavingAccountRegister(true);

    try {
      const imageFields = ["bank_image", "biz_image"];
      const uploadPromises = imageFields.map(async (field) => {
        const file = formData[field];
        if (!file || typeof file === "string") return file;

        const formDataToSend = new FormData();
        formDataToSend.append("user_id", localUserId);
        formDataToSend.append("file", file);
        formDataToSend.append("type", "account");
        formDataToSend.append("gubun", field);
        formDataToSend.append("folder", selectedAccountId);

        const res = await api.post("/Operate/OperateImgUpload", formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data?.code === 200) return res.data.image_path;
        throw new Error(res.data?.message || "이미지 업로드 실패");
      });

      const [bankPath, bizPath] = await Promise.all(uploadPromises);

      const payload = {
        ...formData,
        // 거래처 유형은 저장 시점에 숫자로 변환
        account_type: Number(formData.account_type),
        bank_image: bankPath,
        biz_image: bizPath,
        del_yn: "N",
        user_id: localUserId,
      };

      const response = await api.post("/Operate/AccountRetailBusinessSave", payload);
      if (response.data?.code === 200) {
        Swal.fire({
          title: "성공",
          text: "거래처가 등록되었습니다.",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "확인",
        });
        closeModalWithPreview(setOpen2);
        setFormData(initialForm);
        setImagePreviews({ bank_image: null, biz_image: null });
      } else {
        Swal.fire("실패", response.data?.message || "저장 중 오류 발생", "error");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      Swal.fire("에러", err.message || "저장 중 문제가 발생했습니다.", "error");
    } finally {
      savingAccountRegisterRef.current = false;
      setSavingAccountRegister(false);
    }
  };

  const handleTypeChange = (e, index) => {
    const newTypes = [...receiptType];
    newTypes[index] = e.target.value;
    setReceiptType(newTypes);
  };

  const stopRowClick = (e) => {
    e.stopPropagation();
  };
  // ======================== ✅ List 모달 공통(변경감지/스타일) ========================
  const normalizeText = (v) =>
    String(v ?? "")
      .replace(/\s+/g, " ")
      .trim();

  const isDiff = (a, b) => {
    // 숫자처럼 생기면 숫자 비교
    const an = parseNumber(a);
    const bn = parseNumber(b);

    // 둘 다 숫자 비교가 의미있는 케이스(하나라도 숫자 입력이 있으면)
    const aHas = normalizeText(a) !== "";
    const bHas = normalizeText(b) !== "";

    // payType 같은 문자열 숫자도 여기로 들어오니,
    // 숫자 비교가 맞는 필드에서는 호출부에서 String 비교를 쓰는 게 안전함.
    // 여기서는 기본만 제공.
    if (aHas || bHas) {
      // 숫자 필드로 쓰는 곳이면 parseNumber 비교가 유리
      if (!Number.isNaN(an) || !Number.isNaN(bn)) {
        return an !== bn;
      }
    }

    // 기본: 문자열 정규화 비교
    return normalizeText(a) !== normalizeText(b);
  };

  const getCellStyleByCompare = (origVal, newVal) =>
    isDiff(origVal, newVal) ? { color: "red" } : { color: "black" };

  // ======================== ✅ 우클릭 포인트(색상) 기능 ========================
  const POINT_GUBUN = [
    { gubun: 1, color: "#FF5F00", label: "날짜오기입" },
    { gubun: 2, color: "#0046FF", label: "금액오기입" },
    { gubun: 3, color: "#f8fbfe", label: "완료" },
  ];

  // ✅ 거래처 검색 왼쪽에 표시할 범례(동그라미 + 의미)
  const PointLegend = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        rowGap: 0.5,
        mr: isMobileOrTablet ? 0 : 1,
        width: isMobileOrTablet ? "100%" : "auto",
        maxWidth: "100%",
        minWidth: 0,
        userSelect: "none",
      }}
    >
      {POINT_GUBUN.map((it) => (
        <Box
          key={it.gubun}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: 0.8,
            py: 0.3,
            borderRadius: 999,
            bgcolor: "rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.08)",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: it.color,
              border: "1px solid rgba(0,0,0,0.25)",
              boxShadow: "0 0 0 2px rgba(255,255,255,0.9) inset",
            }}
          />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "#333",
              position: "relative",
              top: "1px",
              whiteSpace: "nowrap",
              lineHeight: 1,
            }}
          >
            {it.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const getPointColorByGubun = (g) => {
    const n = Number(g);
    return POINT_GUBUN.find((x) => x.gubun === n)?.color || null;
  };

  const buildPointKey = (y, m, type, day) =>
    `${Number(y)}-${Number(m)}-${String(type)}-${Number(day)}`;

  // ======================== ✅ useList 기반 "입력불가(input_yn=1)" 처리 ========================
  const buildUseKey = (y, m, type) => `${Number(y)}-${Number(m)}-${String(type)}`;

  const parseInputYn = (value) => {
    const s = String(value ?? "")
      .trim()
      .toUpperCase();
    if (!s) return 0;
    if (s === "1" || s === "Y" || s === "YES" || s === "TRUE") return 1;
    if (s === "0" || s === "N" || s === "NO" || s === "FALSE") return 0;
    const n = Number(s);
    return Number.isFinite(n) && n === 1 ? 1 : 0;
  };

  const parseUseVersion = (row) => {
    const idx = Number(row?.idx ?? 0);
    if (Number.isFinite(idx) && idx > 0) return idx;
    const modTs = Date.parse(String(row?.mod_dt ?? "").trim());
    if (Number.isFinite(modTs)) return modTs;
    const regTs = Date.parse(String(row?.reg_dt ?? "").trim());
    if (Number.isFinite(regTs)) return regTs;
    return 0;
  };

  // useList -> (year,month,type) 단위로 input_yn lookup 맵
  const useLockMap = useMemo(() => {
    const map = new Map();
    (useList || []).forEach((u) => {
      const rowAccountId = String(u.account_id ?? u.row_account_id ?? "").trim();
      if (selectedAccountId && rowAccountId && rowAccountId !== String(selectedAccountId)) return;

      const y = Number(u.count_year ?? u.year);
      const m = Number(u.count_month ?? u.month);
      const t = String(u.type ?? u.use_type ?? u.gubun ?? "").trim();
      if (!y || !m || !t) return;

      const key = buildUseKey(y, m, t);
      const lock = parseInputYn(u.input_yn ?? u.use_yn ?? u.lock_yn);
      const version = parseUseVersion(u);
      const prev = map.get(key);

      if (!prev || version >= prev.version) {
        map.set(key, { lock, version }); // 최신 버전만 유지
      }
    });
    return map;
  }, [useList, selectedAccountId]);

  const isTypeLocked = useCallback(
    (y, m, type) => (useLockMap.get(buildUseKey(y, m, type))?.lock ?? 0) === 1,
    [useLockMap]
  );

  // ✅ 낙관적 업데이트용 로컬 override 맵 (서버 재조회 완료 전까지 즉시 반영)
  const [pointOverrideMap, setPointOverrideMap] = useState(new Map());

  // ✅ pointList -> (year,month,type,day) 단위로 색상 lookup 맵
  const pointColorMap = useMemo(() => {
    const map = new Map();
    (pointList || []).forEach((p) => {
      // account_id 매칭(선택 거래처 기준)
      if (selectedAccountId && String(p.account_id) !== String(selectedAccountId)) return;

      const y = Number(p.count_year ?? p.year);
      const m = Number(p.count_month ?? p.month);
      const d = Number(p.count_date ?? p.date);
      const t = String(p.type ?? "");
      if (!y || !m || !d || !t) return;

      const color = getPointColorByGubun(p.gubun);
      if (!color) return;

      map.set(buildPointKey(y, m, t, d), color);
    });
    // override 적용 (저장 직후 낙관적 반영)
    pointOverrideMap.forEach((color, key) => {
      if (color === null) map.delete(key);
      else map.set(key, color);
    });
    return map;
  }, [pointList, selectedAccountId, pointOverrideMap]);

  // ✅ 우클릭 메뉴 상태(마우스 좌표 + 타겟 셀 정보)
  const [pointMenu, setPointMenu] = useState({
    open: false,
    mouseX: null,
    mouseY: null,
    target: null, // { year, month, day, type }
  });

  const closePointMenu = useCallback(() => {
    setPointMenu({ open: false, mouseX: null, mouseY: null, target: null });
  }, []);

  // ✅ 특정 월(= 저장한 year/month)의 포인트를 다시 조회해서 pointList 갱신
  // 현재 fetchPointList가 "전월 기준 조회"라서, (targetYear,targetMonth)의 포인트를 가져오려면
  // base = (targetYear,targetMonth) + 1개월을 넣어서 prevYearMonth가 target이 되도록 만듭니다.
  const refetchPointForExactMonth = useCallback(
    async (targetYear, targetMonth) => {
      if (!fetchPointList) return;
      const base = dayjs(`${targetYear}-${String(targetMonth).padStart(2, "0")}-01`).add(
        1,
        "month"
      );

      await fetchPointList(selectedAccountId, base.year(), base.month() + 1);
    },
    [fetchPointList, selectedAccountId]
  );

  // ✅ 우클릭 메뉴 오픈
  const openPointMenu = useCallback(
    (e, rowOriginal, colKey, isSecond) => {
      if (!selectedAccountId) {
        Swal.fire("안내", "거래처를 먼저 선택하세요.", "info");
        return;
      }

      // day_# 컬럼만 대상
      const dayNo = Number(String(colKey).replace("day_", ""));
      if (!dayNo || Number.isNaN(dayNo)) return;

      const t = String(rowOriginal?.type ?? "");
      if (!t) return;

      const y = isSecond ? prevYear : year;
      const m = isSecond ? prevMonth : month;

      setPointMenu({
        open: true,
        mouseX: e.clientX - 2,
        mouseY: e.clientY - 4,
        target: { year: y, month: m, day: dayNo, type: t },
      });
    },
    [selectedAccountId, year, month, prevYear, prevMonth]
  );

  // ✅ 저장(즉시 저장)
  const savePointGubun = useCallback(
    async (gubun) => {
      const t = pointMenu.target;
      if (!t) return;

      const localUserId = localStorage.getItem("user_id");

      const payload = {
        account_id: selectedAccountId,
        year: t.year,
        month: t.month,
        type: t.type,
        gubun: Number(gubun),
        user_id: localUserId,

        // 서버에서 어떤 키를 받는지 불확실할 수 있어서 안전하게 둘 다 보냅니다.
        count_date: Number(t.day),
        date: Number(t.day),
      };

      // ✅ 낙관적 업데이트: 저장 전 즉시 색상 반영
      const overrideKey = buildPointKey(t.year, t.month, t.type, t.day);
      const optimisticColor = getPointColorByGubun(gubun);
      setPointOverrideMap((prev) => {
        const next = new Map(prev);
        if (optimisticColor) next.set(overrideKey, optimisticColor);
        else next.set(overrideKey, null);
        return next;
      });

      try {
        const res = await api.post("/Operate/TallySheetPointSave", payload, {
          validateStatus: () => true,
        });

        const ok = res?.data?.code === 200 || res?.status === 200;
        if (!ok) {
          throw new Error(res?.data?.message || `저장 실패 (code: ${res?.status})`);
        }

        closePointMenu();

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "포인트 저장 완료",
          showConfirmButton: false,
          timer: 900,
        });

        // ✅ 저장한 월 기준으로 pointList 재조회 후 override 초기화
        await refetchPointForExactMonth(t.year, t.month);
        setPointOverrideMap((prev) => {
          const next = new Map(prev);
          next.delete(overrideKey);
          return next;
        });
      } catch (err) {
        // 실패 시 override 롤백
        setPointOverrideMap((prev) => {
          const next = new Map(prev);
          next.delete(overrideKey);
          return next;
        });
        closePointMenu();
        Swal.fire("오류", err.message || "포인트 저장 중 오류", "error");
      }
    },
    [pointMenu.target, selectedAccountId, closePointMenu, refetchPointForExactMonth]
  );

  // ======================== ✅ "구분(업체명)" 우클릭 입력가능/불가 메뉴 ========================
  const [useMenu, setUseMenu] = useState({
    open: false,
    mouseX: null,
    mouseY: null,
    target: null, // { year, month, type }
  });

  const closeUseMenu = useCallback(() => {
    setUseMenu({ open: false, mouseX: null, mouseY: null, target: null });
  }, []);

  const openUseMenu = useCallback(
    (e, rowOriginal, isSecond) => {
      if (!canManageUseInputState) return;
      if (!selectedAccountId) {
        Swal.fire("안내", "거래처를 먼저 선택하세요.", "info");
        return;
      }
      const t = String(rowOriginal?.type ?? "");
      if (!t) return;
      if (rowOriginal?.name === "총합") return;

      const y = isSecond ? prevYear : year;
      const m = isSecond ? prevMonth : month;

      setUseMenu({
        open: true,
        mouseX: e.clientX - 2,
        mouseY: e.clientY - 4,
        target: { year: y, month: m, type: t },
      });
    },
    [canManageUseInputState, selectedAccountId, year, month, prevYear, prevMonth]
  );

  const saveUseInputYn = useCallback(
    async (inputYn) => {
      if (!canManageUseInputState) return;
      const t = useMenu.target;
      if (!t) return;

      const user_id = localStorage.getItem("user_id");
      const rawMatchedIdx =
        (useList || []).find((u) => {
          const rowYear = Number(u?.count_year ?? u?.year);
          const rowMonth = Number(u?.count_month ?? u?.month);
          const rowType = String(u?.type ?? u?.use_type ?? u?.gubun ?? "").trim();
          const rowAccountId = String(u?.account_id ?? u?.row_account_id ?? "").trim();
          const accountMatch = !rowAccountId || rowAccountId === String(selectedAccountId ?? "");

          return (
            rowYear === Number(t.year) &&
            rowMonth === Number(t.month) &&
            rowType === String(t.type) &&
            accountMatch
          );
        })?.idx ?? null;
      const matchedIdxNum = Number(rawMatchedIdx);
      const safeIdx =
        Number.isInteger(matchedIdxNum) && matchedIdxNum > 0 ? matchedIdxNum : null;

      const payload = {
        idx: safeIdx,
        year: Number(t.year),
        month: Number(t.month),
        account_id: selectedAccountId,
        type: String(t.type),
        input_yn: Number(inputYn), // 0 or 1
        user_id: user_id,
      };

      try {
        const res = await api.post("/Operate/TallySheetUseSave", payload, {
          validateStatus: () => true,
        });

        const responseCode = Number(res?.data?.code);
        const ok = responseCode === 200;
        if (!ok) {
          throw new Error(
            res?.data?.message || res?.data?.msg || `저장 실패 (code: ${res?.data?.code ?? "N/A"})`
          );
        }

        // ✅ 저장한 월/년 기준으로 다시 조회 + 값 검증
        const refreshed = (await fetchUseList?.(selectedAccountId, year, month)) || [];
        const matched = refreshed.find((r) => {
          const rowYear = Number(r?.count_year ?? r?.year);
          const rowMonth = Number(r?.count_month ?? r?.month);
          const rowType = String(r?.type ?? r?.use_type ?? r?.gubun ?? "").trim();
          const rowAccountId = String(r?.account_id ?? r?.row_account_id ?? "").trim();
          const accountMatch = !rowAccountId || rowAccountId === String(selectedAccountId ?? "");

          return (
            rowYear === Number(t.year) &&
            rowMonth === Number(t.month) &&
            rowType === String(t.type) &&
            accountMatch
          );
        });
        const nextInputYn = matched ? parseInputYn(matched.input_yn ?? matched.use_yn) : 0;
        if (nextInputYn !== Number(inputYn)) {
          throw new Error("저장 후 재조회 값이 반영되지 않았습니다.");
        }

        closeUseMenu();
        if (Number(inputYn) === 1) {
          setActiveCell({ isSecond: false, rowIndex: null, colKey: null });
        }
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "입력상태 저장 완료",
          showConfirmButton: false,
          timer: 900,
        });
      } catch (err) {
        closeUseMenu();
        Swal.fire("오류", err.message || "입력상태 저장 중 오류", "error");
      }
    },
    [canManageUseInputState, useMenu.target, selectedAccountId, closeUseMenu, fetchUseList, useList]
  );

  if (loading || viewLoading) return <LoadingScreen />;

  const renderTable = (
    tableInstance,
    originalData,
    handleChange,
    dataState,
    ratioData,
    isSecond = false
  ) => (
    <MDBox
      pt={0}
      sx={{
        overflowX: "auto",
        "& table": {
          borderCollapse: "separate",
          width: "max-content",
          minWidth: "50%",
          borderSpacing: 0,
        },
        "& th, & td": {
          border: "1px solid #686D76",
          textAlign: "center",
          whiteSpace: "nowrap",
          fontSize: "12px",
          padding: "4px",
        },
        "& th": {
          backgroundColor: "#f0f0f0",
          position: "sticky",
          top: 0,
          zIndex: isMobileOrTablet ? 1 : 2,
        },
        "& td:first-of-type, & th:first-of-type": {
          position: "sticky",
          left: 0,
          background: "#f0f0f0",
          zIndex: isMobileOrTablet ? 2 : 3,
        },
        "& .total-row": { backgroundColor: "#FFE3A9", fontWeight: "bold" },
      }}
    >
      <table>
        <thead>
          <tr style={{ backgroundColor: "#FFE3A9" }}>
            <td>일 사용기준 %</td>
            {ratioData.map((val, idx) => (
              <td key={idx}>{val}</td>
            ))}
            <td />
          </tr>
          {tableInstance.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th key={h.id}>{flexRender(h.column.columnDef.header, h.getContext())}</th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {tableInstance.getRowModel().rows.map((row, rIdx) => (
            <tr key={row.id} className={row.original.name === "총합" ? "total-row" : ""}>
              {row.getVisibleCells().map((cell) => {
                const colKey = cell.column.columnDef.accessorKey;

                const isTotalRow = row.original.name === "총합";
                const isBaseCell = colKey !== "name" && colKey !== "total" && !isTotalRow;

                const rowType = String(row.original.type ?? "");

                const yForRow = isSecond ? prevYear : year;
                const mForRow = isSecond ? prevMonth : month;
                const rowLocked = !isTotalRow && isTypeLocked(yForRow, mForRow, rowType);

                // ✅ 포인트 색상 계산(여기서!)
                const dayNo = isBaseCell ? Number(String(colKey).replace("day_", "")) : null;
                const cellYear = isSecond ? prevYear : year;
                const cellMonth = isSecond ? prevMonth : month;

                const pointColor =
                  isBaseCell && dayNo
                    ? pointColorMap.get(buildPointKey(cellYear, cellMonth, rowType, dayNo))
                    : null;

                const canInlineEdit = INLINE_EDIT_TYPES.has(rowType) && !rowLocked;
                const isEditable = isBaseCell && canInlineEdit;

                const currVal = parseNumber(dataState?.[rIdx]?.[colKey]);
                const origVal = parseNumber(originalData?.[rIdx]?.[colKey]);
                const isChanged = isEditable && currVal !== origVal;

                // ✅ 클릭된 행/셀 하이라이트(요청 반영)
                const isActiveRow =
                  !isTotalRow &&
                  activeCell.rowIndex != null &&
                  activeCell.rowIndex === rIdx &&
                  activeCell.isSecond === !!isSecond;

                const isActiveThisCell = isActiveRow && activeCell.colKey === colKey;

                const baseBg = "";

                const nameLockedBg = colKey === "name" && rowLocked ? "#E9E9E9" : "";

                const activeRowBg = isActiveRow ? "#FFF4B3" : "";
                const activeCellBg = isActiveThisCell ? "#FFD54F" : "";
                const lockedRowBg = rowLocked ? "#E4E4E4" : "";
                const isLightPoint =
                  pointColor &&
                  ["#f8fbfe", "#fff", "#ffffff"].includes(String(pointColor).toLowerCase());

                // 파란색/빨간색만 노란 activeRow보다 우선 (흰색은 노랑이 덮음)
                const isStrongPoint = pointColor && !isLightPoint;

                // ✅ 우선순위: locked > 파/빨 pointColor > activeCellBg > activeRowBg > 흰 pointColor > 기본
                const mergedBg = (() => {
                  if (lockedRowBg) return lockedRowBg;
                  if (isStrongPoint) return pointColor;
                  if (activeCellBg) return activeCellBg;
                  if (activeRowBg) return activeRowBg;
                  if (pointColor) return pointColor; // 흰색은 activeRow 없을 때만
                  return nameLockedBg || baseBg || "";
                })();

                return (
                  <td
                    key={cell.id}
                    contentEditable={isEditable}
                    suppressContentEditableWarning
                    style={{
                      color: isChanged
                        ? "#d32f2f"
                        : lockedRowBg
                          ? "black"
                          : pointColor
                            ? isLightPoint
                              ? "black"
                              : "#fff"
                            : "black",
                      width: "80px",
                      cursor: !isBaseCell
                        ? "default"
                        : rowLocked
                          ? "not-allowed"
                          : canInlineEdit
                            ? "text"
                            : "pointer",
                      // ✅ 우선순위: 활성셀 > 포인트색 > 활성행 > 기본BG
                      background: mergedBg,
                      outline: isActiveThisCell ? "2px solid rgba(255, 152, 0, 0.9)" : "none",
                      outlineOffset: isActiveThisCell ? "-2px" : "0px",
                    }}
                    onContextMenu={(e) => {
                      // ✅ 1) 구분(업체명) 우클릭 -> 입력가능/불가 메뉴
                      if (colKey === "name" && !isTotalRow) {
                        if (!canManageUseInputState) return;
                        e.preventDefault();
                        e.stopPropagation();
                        openUseMenu(e, row.original, isSecond);
                        return;
                      }

                      // ✅ 2) day 셀 우클릭 -> 포인트 메뉴(기존)
                      if (!isBaseCell || isTotalRow) return;
                      e.preventDefault();
                      e.stopPropagation();
                      openPointMenu(e, row.original, colKey, isSecond);
                    }}
                    onMouseDown={
                      isEditable
                        ? undefined
                        : (e) => {
                          if (e.button !== 0) return;
                          if (rowLocked) return; // ✅ 추가: 입력불가면 클릭 무시

                          e.preventDefault();
                          handleSpecialCellClick(row.original, rIdx, colKey, isSecond);
                        }
                    }
                    onClick={
                      isEditable
                        ? (e) => {
                          if (e.button !== 0) return;
                          if (rowLocked) return; // ✅ 추가

                          handleSpecialCellClick(row.original, rIdx, colKey, isSecond);
                        }
                        : undefined
                    }
                    onBlur={
                      isEditable
                        ? (e) => {
                          if (rowLocked) return; // ✅ 안전장치
                          handleChange(rIdx, colKey, e.currentTarget.innerText, isSecond);
                        }
                        : undefined
                    }
                  >
                    {colKey === "name" ? row.original[colKey] : formatNumber(row.original[colKey])}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </MDBox>
  );

  const filterAndActionArea = (
    <MDBox
      pt={1}
      pb={1}
      sx={{
        px: 2,
        display: "flex",
        flexWrap: isMobileOrTablet ? "wrap" : "nowrap",
        justifyContent: isMobileOrTablet ? "flex-start" : "flex-end",
        alignItems: "center",
        gap: isMobileOrTablet ? 1 : 2,
        backgroundColor: "#ffffff",
        borderBottom: "1px solid #eee",
      }}
    >
      {/* ✅ 범례 + 거래처검색을 한 덩어리로 묶어서 "거래처 검색" 왼쪽에 범례 표시 */}
      <Box
        sx={{
          display: "flex",
          alignItems: isMobileOrTablet ? "stretch" : "center",
          gap: 1,
          flexWrap: isMobileOrTablet ? "nowrap" : "wrap",
          flexDirection: isMobileOrTablet ? "column" : "row",
          width: isMobileOrTablet ? "100%" : "auto",
          minWidth: 0,
        }}
      >
        <PointLegend />

        <Autocomplete
          size="small"
          sx={{
            width: isMobileOrTablet ? "100%" : "auto",
            maxWidth: "100%",
            minWidth: isMobileOrTablet ? 0 : 200,
            flex: isMobileOrTablet ? "1 1 auto" : "0 0 auto",
          }}
          options={filteredAccountList || []}
          value={selectedAccountOption}
          onChange={(_, newValue) => {
            if (isAccountLocked) return;
            if (!newValue) return;
            setSelectedAccountWithLoading(newValue.account_id);
            setAccountInput(newValue?.account_name || "");
          }}
          inputValue={accountInput}
          onInputChange={(_, newValue) => {
            if (isAccountLocked) return;
            setAccountInput(newValue);
          }}
          getOptionLabel={(opt) => (opt?.account_name ? String(opt.account_name) : "")}
          isOptionEqualToValue={(opt, val) => String(opt?.account_id) === String(val?.account_id)}
          disableClearable={isAccountLocked}
          disabled={isAccountLocked}
          renderInput={(params) => (
            <TextField
              {...params}
              label="거래처 검색"
              placeholder="거래처명을 입력"
              // ✅ 검색: 엔터로 바로 선택되도록 처리
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  selectAccountByInput();
                }
              }}
              sx={{
                "& .MuiInputBase-root": { height: 35, fontSize: 12 },
                "& .MuiInputLabel-root": { fontSize: 12 },
                "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
              }}
            />
          )}
        />
      </Box>
      <TextField
        select
        size="small"
        value={year}
        onChange={(e) => {
          beginListLoading();
          setYear(Number(e.target.value));
        }}
        sx={{
          minWidth: isMobile ? 80 : 90,
          flex: isMobileOrTablet ? "1 1 80px" : "0 0 auto",
        }}
        SelectProps={{ native: true }}
      >
        {Array.from({ length: 10 }, (_, i) => today.year() - 5 + i).map((y) => (
          <option key={y} value={y}>
            {y}년
          </option>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        value={month}
        onChange={(e) => {
          beginListLoading();
          setMonth(Number(e.target.value));
          // '전월' 탭이어도 월을 변경하면 바꾸면 항상 '현재월' 탭으로 복귀
          setTabValue(0);
        }}
        sx={{
          minWidth: isMobile ? 70 : 80,
          flex: isMobileOrTablet ? "1 1 70px" : "0 0 auto",
        }}
        SelectProps={{ native: true }}
      >
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
          <option key={m} value={m}>
            {m}월
          </option>
        ))}
      </TextField>

      <MDButton
        variant="gradient"
        color="info"
        onClick={handleModalOpen2}
        sx={{
          fontSize: isMobile ? "11px" : "13px",
          minWidth: isMobile ? 90 : 110,
          px: isMobile ? 1 : 2,
        }}
      >
        거래처 등록
      </MDButton>

      <MDButton
        variant="gradient"
        color="info"
        onClick={handleModalOpen}
        sx={{
          fontSize: isMobile ? "11px" : "13px",
          minWidth: isMobile ? 90 : 110,
          px: isMobile ? 1 : 2,
        }}
      >
        거래처 연결
      </MDButton>

      <MDButton
        variant="gradient"
        color="info"
        onClick={handleRefresh}
        sx={{
          fontSize: isMobile ? "11px" : "13px",
          minWidth: isMobile ? 70 : 90,
          px: isMobile ? 1 : 2,
        }}
      >
        새로고침
      </MDButton>

      <MDButton
        variant="contained"
        color="success"
        startIcon={<DownloadIcon />}
        onClick={handleExcelDownload}
        sx={{
          fontSize: isMobile ? "11px" : "13px",
          minWidth: isMobile ? 70 : 90,
          px: isMobile ? 1 : 2,
        }}
      >
        엑셀다운로드
      </MDButton>

      <MDButton
        variant="gradient"
        color="info"
        onClick={handleSave}
        sx={{
          fontSize: isMobile ? "11px" : "13px",
          minWidth: isMobile ? 70 : 90,
          px: isMobile ? 1 : 2,
        }}
      >
        저장
      </MDButton>
    </MDBox>
  );

  // ==============================
  // Part 2 / 2  (Part 1 바로 아래에 이어붙이세요)
  // ==============================
  return (
    <DashboardLayout>
      <PreviewOverlay
        open={floatingPreview.open}
        files={floatingPreview.files}
        currentIndex={floatingPreview.currentIndex}
        onChangeIndex={handleFloatingPreviewIndexChange}
        onClose={closeFloatingPreview}
        anchorX={1 / 3}
      />

      {isMobileOrTablet ? (
        <>
          <MDBox
            sx={{
              position: "sticky",
              top: 0,
              zIndex: theme.zIndex.appBar + 1,
              isolation: "isolate",
              backgroundColor: "#ffffff",
            }}
          >
            <DashboardNavbar title="🧮 집계표" />
          </MDBox>
          {filterAndActionArea}
        </>
      ) : (
        <MDBox
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1200,
            isolation: "isolate",
            backgroundColor: "#ffffff",
          }}
        >
          <DashboardNavbar title="🧮 집계표" />
          {filterAndActionArea}
        </MDBox>
      )}

      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox
            mx={0}
            mt={-3}
            py={1}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <MDTypography variant="h6" color="white">
                집계표
              </MDTypography>

              <Box sx={{ width: "65%" }}>
                <BudgetSummaryBar
                  budget={budgetForTab}
                  used={usedForTab}
                  suppliesBudget={suppliesBudgetForTab}
                  suppliesUsed={suppliesUsedForTab}
                />
              </Box>

              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                textColor="inherit"
                indicatorColor="secondary"
                variant={isMobile ? "scrollable" : "standard"}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    color: "rgba(255,255,255,0.85)",
                    fontSize: 13,
                    fontWeight: 600,
                  },
                  "& .Mui-selected": { color: "#fff" },
                }}
              >
                <Tab label={countMonth ? `현재월 (${countMonth})` : "현재월"} />
                <Tab label={count2Month ? `전월 (${count2Month})` : "전월"} />
              </Tabs>
            </Box>
          </MDBox>

          <MDBox pt={1}>
            {tabValue === 0 &&
              renderTable(table, originalRows, handleCellChange, dataRows, ratioDataNow)}
            {tabValue === 1 &&
              renderTable(table2, original2Rows, handleCellChange, data2Rows, ratioDataPrev, true)}
          </MDBox>
        </Card>
      </MDBox>

      {/* 집계표 메모 영역: 왼쪽 본사 메모(편집), 오른쪽 현장 메모(읽기 전용) */}
      <MDBox pb={3}>
        <Card>
          <Grid container spacing={2} p={2}>
          {/* 본사 메모 입력 영역 */}
          <Grid item xs={12} md={6}>
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1,
                minHeight: 32,
              }}
            >
              {/* 본사 메모 제목(권한 없으면 바로 옆에 읽기 전용 표시) */}
              <MDBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MDTypography variant="h6">본사 메모</MDTypography>
                {!canEditTallyNote && (
                  <MDTypography variant="caption" color="text">
                    읽기 전용
                  </MDTypography>
                )}
              </MDBox>
              {canEditTallyNote && (
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  disabled={
                    loadingTallyNote ||
                    savingTallyNote ||
                    !selectedAccountId ||
                    !isTallyNoteDirty
                  }
                  onClick={handleSaveTallyNote}
                >
                  {savingTallyNote ? "저장중..." : "메모 저장"}
                </MDButton>
              )}
            </MDBox>

            <TextField
              key={tallyNoteInputKey}
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              defaultValue={tallyNoteOrigin}
              inputRef={tallyNoteInputRef}
              onChange={handleTallyNoteInputChange}
              placeholder="집계표 메모를 입력하세요."
              InputProps={{ readOnly: !canEditTallyNote }}
              disabled={loadingTallyNote || !selectedAccountId}
              sx={{
                ...(!canEditTallyNote ? READ_ONLY_NOTE_SX : {}),
                "& .MuiInputBase-inputMultiline": {
                  color: isTallyNoteDirty ? "#d32f2f" : "inherit",
                  fontWeight: isTallyNoteDirty ? 600 : 400,
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  borderColor: isTallyNoteDirty ? "#d32f2f" : undefined,
                },
                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isTallyNoteDirty ? "#d32f2f" : undefined,
                },
                "& .MuiFormHelperText-root": {
                  color: isTallyNoteDirty ? "#d32f2f" : undefined,
                  fontWeight: isTallyNoteDirty ? 700 : 400,
                },
              }}
              helperText={
                loadingTallyNote
                  ? "메모를 불러오는 중입니다."
                  : canEditTallyNote && isTallyNoteDirty
                    ? "수정됨 (저장 필요)"
                    : ""
              }
            />
          </Grid>

          {/* 현장 메모 표시 영역 */}
          <Grid item xs={12} md={6}>
            {/* 현장 메모 제목 및 읽기 전용 표시 */}
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                minHeight: 32,
              }}
            >
              <MDTypography variant="h6">현장 메모</MDTypography>
              <MDTypography variant="caption" color="text">
                읽기 전용
              </MDTypography>
            </MDBox>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              value={tallyFieldNote}
              InputProps={{ readOnly: true }}
              sx={READ_ONLY_NOTE_SX}
              disabled={loadingTallyNote || !selectedAccountId}
              helperText={loadingTallyNote ? "메모를 불러오는 중입니다." : ""}
            />
          </Grid>
          </Grid>
        </Card>
      </MDBox>

      {/* ======================== ✅ 우클릭 포인트 메뉴 ======================== */}
      <Menu
        open={pointMenu.open}
        onClose={closePointMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          pointMenu.open ? { top: pointMenu.mouseY, left: pointMenu.mouseX } : undefined
        }
      >
        {POINT_GUBUN.map((it) => (
          <MenuItem
            key={it.gubun}
            onClick={() => savePointGubun(it.gubun)}
            sx={{ fontSize: 13, display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%", // ✅ 동그라미로 변경
                bgcolor: it.color,
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
            {it.label}
          </MenuItem>
        ))}
      </Menu>

      {/* ======================== ✅ 우클릭 입력가능/불가 메뉴(구분 셀) ======================== */}
      {canManageUseInputState && (
        <Menu
          open={useMenu.open}
          onClose={closeUseMenu}
          anchorReference="anchorPosition"
          anchorPosition={useMenu.open ? { top: useMenu.mouseY, left: useMenu.mouseX } : undefined}
        >
          {(() => {
            const t = useMenu.target;
            const cur = t
              ? Number(useLockMap.get(buildUseKey(t.year, t.month, t.type))?.lock || 0)
              : 0;

            return (
              <>
                <MenuItem
                  selected={cur === 0}
                  onClick={() => saveUseInputYn(0)}
                  sx={{ fontSize: 13 }}
                >
                  입력가능 (0)
                </MenuItem>
                <MenuItem
                  selected={cur === 1}
                  onClick={() => saveUseInputYn(1)}
                  sx={{ fontSize: 13 }}
                >
                  입력불가 (1)
                </MenuItem>
              </>
            );
          })()}
        </Menu>
      )}

      {/* ================= 거래처 연결 모달(open) ================= */}
      <Modal
        open={open}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => closeModalWithPreview(setOpen)}
      >
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 1000,
            maxWidth: 1000,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            p: isMobileOrTablet ? 2 : 3,
          }}
        >
          <MDBox
            mx={0}
            mt={-2}
            py={1}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              거래처 연결
            </MDTypography>
          </MDBox>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <YourSelectableTable
                data={leftItems}
                selected={selectedLeft}
                setSelected={setSelectedLeft}
                enableSorting
              />
            </Grid>
            <Grid
              item
              xs={12}
              lg={1}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <MDButton variant="gradient" color="info" onClick={moveRight}>
                {">"}
              </MDButton>
              <MDButton variant="gradient" color="primary" onClick={moveLeft}>
                {"<"}
              </MDButton>
            </Grid>
            <Grid item xs={12} lg={5}>
              <YourSelectableTable
                data={rightItems}
                selected={selectedRight}
                setSelected={setSelectedRight}
              />
            </Grid>
          </Grid>

          <MDBox mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <MDButton
              variant="gradient"
              color="primary"
              onClick={() => closeModalWithPreview(setOpen)}
            >
              취소
            </MDButton>
            <MDButton variant="gradient" color="info" onClick={handleSubmit}>
              저장
            </MDButton>
          </MDBox>
        </MDBox>
      </Modal>

      {/* ================= 거래처 등록 모달(open2) ================= */}
      <Modal open={open2} disableEscapeKeyDown={floatingPreview.open} onClose={handleModalClose2}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 500,
            maxWidth: 500,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: isMobileOrTablet ? 2 : 5,
          }}
        >
          <Typography variant="h6" gutterBottom>
            거래처 등록
          </Typography>

          <TextField
            fullWidth
            required
            margin="normal"
            size="small"
            label="거래처명"
            InputLabelProps={accountRegisterInputLabelProps}
            name="name"
            value={formData.name || ""}
            onChange={handleChange2}
            sx={accountRegisterTextFieldSx}
          />

          <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Grid item xs={4} sm={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Checkbox
                  size="small"
                  checked={(formData.add_yn || "N") === "Y"}
                  onChange={handleAddYnChange}
                  sx={{ p: 0.5 }}
                />
                <Typography sx={{ fontSize: "0.8rem", lineHeight: 1, whiteSpace: "nowrap" }}>
                  약식사용
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={4} sm={4}>
              <TextField
                fullWidth
                margin="none"
                label="약식명"
                InputLabelProps={accountRegisterInputLabelProps}
                name="add_name"
                value={formData.add_name || ""}
                onChange={handleChange2}
                disabled={(formData.add_yn || "N") !== "Y"}
                placeholder="약식사용 체크 시 입력"
                size="small"
                sx={{ ...accountRegisterTextFieldSx, mt: 0 }}
              />
            </Grid>

            <Grid item xs={4} sm={2}>
              <Typography sx={{ fontSize: "0.8rem", mb: 0.5 }}>유형 (필수)</Typography>
            </Grid>
            <Grid item xs={4} sm={3}>
              <Select
                fullWidth
                size="small"
                value={formData.account_type ?? "0"} // 거래처 유형 기본 문자열 "0"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    // 필수값 로직 유지 위해 문자열로 저장
                    account_type: String(e.target.value),
                  }))
                }
                sx={accountRegisterSelectSx}
              >
                <MenuItem value={"0"}>식재료</MenuItem>
                <MenuItem value={"1"}>소모품</MenuItem>
                <MenuItem value={"2"}>복합</MenuItem>
              </Select>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            required
            margin="normal"
            size="small"
            label="사업자번호"
            InputLabelProps={accountRegisterInputLabelProps}
            name="biz_no"
            value={formData.biz_no || ""}
            onChange={handleBizNoChange}
            placeholder="예: 123-45-67890"
            inputProps={{ inputMode: "numeric" }}
            sx={accountRegisterTextFieldSx}
          />

          <TextField
            fullWidth
            required
            margin="normal"
            size="small"
            label="대표자명"
            InputLabelProps={accountRegisterInputLabelProps}
            name="ceo_name"
            value={formData.ceo_name || ""}
            onChange={handleChange2}
            sx={accountRegisterTextFieldSx}
          />

          <TextField
            fullWidth
            required
            margin="normal"
            size="small"
            label="연락처"
            InputLabelProps={accountRegisterInputLabelProps}
            name="tel"
            value={formData.tel || ""}
            onChange={handleTelChange}
            placeholder="예: 010-1234-5678"
            inputProps={{ inputMode: "numeric" }}
            sx={accountRegisterTextFieldSx}
          />

          <Box mt={0}>
            <Typography sx={{ fontSize: "0.8rem", mb: 0.5 }}>은행명 (필수)</Typography>
            <Select
              fullWidth
              size="small"
              value={
                KOREAN_BANKS.includes(formData.bank_name)
                  ? formData.bank_name
                  : formData.bank_name
                    ? "기타(직접입력)"
                    : ""
              }
              onChange={handleBankSelect}
              displayEmpty
              sx={{ ...accountRegisterSelectSx, mb: 1 }}
            >
              <MenuItem value="">
                <em>은행 선택</em>
              </MenuItem>
              {KOREAN_BANKS.map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>

            {(!KOREAN_BANKS.includes(formData.bank_name) ||
              formData.bank_name === "기타(직접입력)") && (
                <TextField
                  fullWidth
                  required
                  margin="normal"
                  size="small"
                  label="은행명 직접입력"
                  InputLabelProps={accountRegisterInputLabelProps}
                  name="bank_name"
                  value={formData.bank_name === "기타(직접입력)" ? "" : formData.bank_name || ""}
                  onChange={handleChange2}
                  sx={accountRegisterTextFieldSx}
                />
              )}
          </Box>

          <TextField
            fullWidth
            required
            margin="normal"
            size="small"
            label="계좌번호"
            InputLabelProps={accountRegisterInputLabelProps}
            name="bank_no"
            value={formData.bank_no || ""}
            onChange={handleBankNoChange}
            placeholder="숫자만 입력해도 자동으로 - 가 들어갑니다."
            inputProps={{ inputMode: "numeric" }}
            sx={accountRegisterTextFieldSx}
          />

          <Box mt={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.8rem", minWidth: "120px" }}>통장사본 (필수)</Typography>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Button
                variant="outlined"
                component="label"
                sx={{
                  color: "#e8a500",
                  borderColor: "#e8a500",
                  fontSize: "12px",
                  height: "32px",
                  "&:hover": { borderColor: "#e8a500", backgroundColor: "rgba(232, 165, 0, 0.1)" },
                }}
              >
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  name="bank_image"
                  onChange={handleImageUploadPreview}
                />
              </Button>

              {imagePreviews.bank_image && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  {isPdfFileLike(formData.bank_image || imagePreviews.bank_image) ? (
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 1,
                        border: "1px solid #ddd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#d32f2f",
                        cursor: "pointer",
                        bgcolor: "#fafafa",
                      }}
                      onClick={() =>
                        handleImagePreviewOpen(
                          formData.bank_image || imagePreviews.bank_image,
                          "통장사본 미리보기"
                        )
                      }
                    >
                      PDF
                    </Box>
                  ) : (
                    <img
                      src={imagePreviews.bank_image}
                      alt="bank_image"
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid #ddd",
                        cursor: "pointer",
                        transition: "transform 0.2s",
                      }}
                      onClick={() =>
                        handleImagePreviewOpen(
                          formData.bank_image || imagePreviews.bank_image,
                          "통장사본 미리보기"
                        )
                      }
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "11px", flex: 1, minWidth: 0, wordBreak: "break-all" }}
                  >
                    {extractDisplayFileName(formData.bank_image) || "업로드 완료"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box mt={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.8rem", minWidth: "120px" }}>
              사업자등록증 (필수)
            </Typography>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Button
                variant="outlined"
                component="label"
                sx={{
                  color: "#e8a500",
                  borderColor: "#e8a500",
                  fontSize: "12px",
                  height: "32px",
                  "&:hover": { borderColor: "#e8a500", backgroundColor: "rgba(232, 165, 0, 0.1)" },
                }}
              >
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  name="biz_image"
                  onChange={handleImageUploadPreview}
                />
              </Button>

              {imagePreviews.biz_image && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  {isPdfFileLike(formData.biz_image || imagePreviews.biz_image) ? (
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 1,
                        border: "1px solid #ddd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#d32f2f",
                        cursor: "pointer",
                        bgcolor: "#fafafa",
                      }}
                      onClick={() =>
                        handleImagePreviewOpen(
                          formData.biz_image || imagePreviews.biz_image,
                          "사업자등록증 미리보기"
                        )
                      }
                    >
                      PDF
                    </Box>
                  ) : (
                    <img
                      src={imagePreviews.biz_image}
                      alt="biz_image"
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid #ddd",
                        cursor: "pointer",
                        transition: "transform 0.2s",
                      }}
                      onClick={() =>
                        handleImagePreviewOpen(
                          formData.biz_image || imagePreviews.biz_image,
                          "사업자등록증 미리보기"
                        )
                      }
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "11px", flex: 1, minWidth: 0, wordBreak: "break-all" }}
                  >
                    {extractDisplayFileName(formData.biz_image) || "업로드 완료"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box mt={4} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              variant="contained"
              onClick={handleModalClose2}
              disabled={savingAccountRegister}
              sx={{
                bgcolor: "#e8a500",
                color: "#ffffff",
                "&:hover": { bgcolor: "#e8a500", color: "#ffffff" },
                "&.Mui-disabled": { bgcolor: "#e0e0e0", color: "#888888" },
              }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit2}
              disabled={savingAccountRegister}
              sx={{ color: "#ffffff" }}
            >
              {savingAccountRegister ? "저장 중" : "저장"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (1000) 법인카드 모달 3종 (Choice/Create/List) ======================== */}
      <Modal
        open={cardChoiceOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => closeModalWithPreview(setCardChoiceOpen)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 420,
            maxWidth: 420,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            (법인카드) 결제 입력
          </Typography>
          <Typography sx={{ fontSize: 13, mb: 2 }}>
            이미 입력된 금액이 있습니다.
            <br />
            등록 / 수정을 선택하세요.
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            거래처명: {selectedAccountOption?.account_name || "-"} / 날짜: {cardContext.dateStr}
          </Typography>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="contained" color="info" onClick={openCreateFromChoice}>
              등록
            </MDButton>
            <MDButton variant="contained" color="primary" onClick={openListFromChoice}>
              수정
            </MDButton>
            <MDButton variant="outlined" onClick={() => closeModalWithPreview(setCardChoiceOpen)}>
              취소
            </MDButton>
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (1000) 목록 모달: "선택" → "저장", 테이블에서 직접 수정 ======================== */}
      <Modal
        open={cardListOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => {
          // blob revoke
          Object.values(cardRowFiles || {}).forEach((v) => {
            if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
              URL.revokeObjectURL(v.previewUrl);
          });
          setCardRowFiles({});
          closeModalWithPreview(setCardListOpen);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 1200,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            fontSize: 12,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            법인카드 결제 목록 (전체 편집)
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#666", mb: 2 }}>
            날짜: {cardContext.dateStr}
          </Typography>

          <Box sx={{ maxHeight: 360, overflow: "auto", border: "1px solid #ddd" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f0f0f0" }}>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 160 }}>사용처</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 110 }}>금액</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 220 }}>카드</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 160 }}>분류</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 260 }}>영수증</th>
                </tr>
              </thead>

              <tbody>
                {(cardEditRows || []).map((r, idx) => {
                  const rowKey = String(r.id ?? r.sale_id ?? idx);

                  const orig = cardOrigRowsForDiff?.[idx] || {};
                  const fileInfo = cardRowFiles?.[rowKey];
                  const previewSrc = fileInfo?.previewUrl || toPreviewUrl(r.receipt_image);

                  return (
                    <tr key={rowKey} style={{ background: "#ffffff" }}>
                      <td style={{ border: "1px solid #ddd", padding: 6 }}>
                        <TextField
                          size="small"
                          value={r.use_name || ""}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) =>
                            setCardEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, use_name: e.target.value } : x
                              )
                            )
                          }
                          fullWidth
                          sx={{ "& input": getCellStyleByCompare(orig.use_name, r.use_name) }}
                        />
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "right" }}>
                        <TextField
                          size="small"
                          value={r.total ?? ""}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9-]/g, "");
                            setCardEditRows((prev) =>
                              prev.map((x, i) => (i === idx ? { ...x, total: v } : x))
                            );
                          }}
                          fullWidth
                          sx={{
                            "& input": getCellStyleByCompare(
                              parseNumber(orig.total),
                              parseNumber(r.total)
                            ),
                          }}
                        />
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Select
                          size="small"
                          value={String(r.card_idx ?? r.corp_card_idx ?? r.idx ?? "")}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) => {
                            const v = String(e.target.value || "");
                            const picked = getCorpCardByIdx(v);
                            setCardEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx
                                  ? {
                                    ...x,
                                    card_idx: v,
                                    card_brand: picked?.card_brand || x.card_brand || "",
                                    card_no: picked?.card_no || x.card_no || "",
                                  }
                                  : x
                              )
                            );
                          }}
                          fullWidth
                          displayEmpty
                          sx={{
                            height: "40px",
                            "& .MuiSelect-select": getCellStyleByCompare(
                              String(orig.card_idx ?? orig.corp_card_idx ?? orig.idx ?? ""),
                              String(r.card_idx ?? r.corp_card_idx ?? r.idx ?? "")
                            ),
                          }}
                        >
                          <MenuItem value="">
                            <em>카드 선택</em>
                          </MenuItem>
                          {(corpCardList || []).map((c) => (
                            <MenuItem key={String(c.idx)} value={String(c.idx)}>
                              {c.card_brand || "카드"} / {maskCardNo(c.card_no)} / idx=
                              {String(c.idx)}
                            </MenuItem>
                          ))}
                        </Select>
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Select
                          size="small"
                          value={r.receipt_type || "UNKNOWN"}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) =>
                            setCardEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, receipt_type: e.target.value } : x
                              )
                            )
                          }
                          fullWidth
                          sx={{
                            height: "40px",
                            "& .MuiSelect-select": getCellStyleByCompare(
                              orig.receipt_type,
                              r.receipt_type
                            ),
                          }}
                        >
                          <MenuItem value="UNKNOWN" disabled>
                            <em>영수증 타입 선택</em>
                          </MenuItem>
                          <MenuItem value="CARD_SLIP_GENERIC">카드전표</MenuItem>
                          <MenuItem value="MART_ITEMIZED">마트</MenuItem>
                          <MenuItem value="CONVENIENCE">편의점</MenuItem>
                          {/*<MenuItem value="COUPANG_CARD">쿠팡</MenuItem>*/}
                          <MenuItem value="COUPANG_APP">배달앱</MenuItem>
                        </Select>
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <MDButton
                            component="label"
                            variant="contained"
                            color="info"
                            size="small"
                            sx={{ color: "#fff !important" }}
                          >
                            <input
                              type="file"
                              hidden
                              multiple
                              accept="image/*,application/pdf"
                              onChange={(e) => {
                                const files = Array.from(e.target.files || [])
                                  .filter(Boolean)
                                  .sort((a, b) =>
                                    String(a?.name || "").localeCompare(String(b?.name || ""), "ko", {
                                      numeric: true,
                                      sensitivity: "base",
                                    })
                                  )
                                  .slice(0, 3);
                                if (!files.length) return;
                                setCardRowFiles((prev) => {
                                  // 기존 blob revoke
                                  const old = prev?.[rowKey];
                                  if (
                                    old?.previewUrl &&
                                    String(old.previewUrl).startsWith("blob:")
                                  ) {
                                    URL.revokeObjectURL(old.previewUrl);
                                  }
                                  return {
                                    ...prev,
                                    [rowKey]: {
                                      file: files[0],
                                      files,
                                      previewUrl: URL.createObjectURL(files[0]),
                                    },
                                  };
                                });
                                e.target.value = "";
                              }}
                            />
                            파일
                          </MDButton>

                          {previewSrc && (
                            <MDButton
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                const selectedFiles = Array.isArray(fileInfo?.files)
                                  ? fileInfo.files.filter(Boolean).slice(0, 3)
                                  : [];
                                openFloatingPreview(
                                  selectedFiles.length ? selectedFiles : fileInfo?.file || previewSrc,
                                  "(법인카드) 영수증 미리보기"
                                );
                              }}
                            >
                              미리보기
                            </MDButton>
                          )}
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={async () => {
                // ✅ 변경된 행만 찾아서 일괄 저장
                const changed = (cardEditRows || [])
                  .map((r, idx) => {
                    const orig = cardOrigRowsForDiff?.[idx] || {};
                    const rowKey = String(r.id ?? r.sale_id ?? idx);

                    const pickedCardIdx = String(r.card_idx ?? r.corp_card_idx ?? r.idx ?? "");
                    const origCardIdx = String(
                      orig.card_idx ?? orig.corp_card_idx ?? orig.idx ?? ""
                    );

                    const fieldChanged =
                      normalizeText(r.use_name) !== normalizeText(orig.use_name) ||
                      parseNumber(r.total) !== parseNumber(orig.total) ||
                      String(r.receipt_type || "UNKNOWN") !==
                      String(orig.receipt_type || "UNKNOWN") ||
                      pickedCardIdx !== origCardIdx;

                    const hasFile = Array.isArray(cardRowFiles?.[rowKey]?.files)
                      ? cardRowFiles[rowKey].files.length > 0
                      : !!cardRowFiles?.[rowKey]?.file;

                    return fieldChanged || hasFile ? { r, idx, rowKey } : null;
                  })
                  .filter(Boolean);

                if (!changed.length) {
                  Swal.fire("정보", "변경된 내용이 없습니다.", "info");
                  return;
                }

                try {
                  Swal.fire({
                    title: "저장 중 입니다.",
                    text: `0 / ${changed.length}`,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    customClass: SWAL_LOADING_CUSTOM_CLASS,
                    willOpen: ensureSwalLoadingStyle,
                    didOpen: showSwalLoadingWithoutConfirm,
                  });

                  for (let i = 0; i < changed.length; i++) {
                    const { r, rowKey, idx } = changed[i];
                    const orig = cardOrigRowsForDiff?.[idx] || {};

                    // ✅ 컨텍스트 날짜(클릭 셀 기준) 유지
                    const y = cardContext.isSecond ? prevYear : year;
                    const m = cardContext.isSecond ? prevMonth : month;
                    const fixedCellDate = buildCellDate(y, m, cardContext.dayIndex ?? 0);

                    const submitAccountId = String(
                      r.account_id ?? orig.account_id ?? selectedAccountId ?? ""
                    );
                    const cardIdx = String(
                      r.card_idx ??
                      r.corp_card_idx ??
                      r.idx ??
                      orig.card_idx ??
                      orig.corp_card_idx ??
                      orig.idx ??
                      ""
                    );
                    const picked = getCorpCardByIdx(cardIdx);
                    const cardBrand =
                      r.card_brand ||
                      r.cardBrand ||
                      orig.card_brand ||
                      orig.cardBrand ||
                      picked?.card_brand ||
                      "";
                    const cardNo =
                      r.card_no || r.cardNo || orig.card_no || orig.cardNo || picked?.card_no || "";
                    const paymentDt = r.payment_dt || orig.payment_dt || fixedCellDate;

                    const receiptImage = r.receipt_image || orig.receipt_image || "";
                    const bizNo = r.bizNo || orig.bizNo || "";
                    const note = r.note || orig.note || "";
                    const vat = parseNumber(r.vat === "" || r.vat == null ? orig.vat : r.vat);
                    const taxFree = parseNumber(
                      r.taxFree === "" || r.taxFree == null ? orig.taxFree : r.taxFree
                    );
                    const tax = parseNumber(r.tax === "" || r.tax == null ? orig.tax : r.tax);
                    const totalCard = parseNumber(
                      r.totalCard === "" || r.totalCard == null ? orig.totalCard : r.totalCard
                    );

                    const selectedFiles = Array.isArray(cardRowFiles?.[rowKey]?.files)
                      ? cardRowFiles[rowKey].files.filter((f) => f instanceof File).slice(0, 3)
                      : [];
                    const uploadFiles =
                      selectedFiles.length > 0
                        ? selectedFiles
                        : cardRowFiles?.[rowKey]?.file instanceof File
                          ? [cardRowFiles[rowKey].file]
                          : [];

                    if (uploadFiles.length > 0) {
                      // ✅ 파일 변경 시: OCR 저장 endpoint 사용 (receipt_image 갱신)
                      const fd = new FormData();
                      fd.append("user_id", localUserId);
                      fd.append("account_id", submitAccountId);
                      fd.append("row_account_id", submitAccountId);
                      fd.append("cell_day", String((cardContext.dayIndex ?? 0) + 1));
                      fd.append("cell_date", fixedCellDate);
                      fd.append("type", 1000);
                      fd.append("saveType", "cor");

                      if (r.id != null) fd.append("id", r.id);
                      if (r.sale_id) fd.append("sale_id", String(r.sale_id));
                      if (cardIdx) fd.append("card_idx", cardIdx);

                      fd.append("card_brand", cardBrand);
                      fd.append("card_no", cardNo);
                      fd.append("use_name", r.use_name || "");
                      fd.append("receipt_type", r.receipt_type || "UNKNOWN");
                      fd.append("total", parseNumber(r.total));
                      uploadFiles.forEach((f) => fd.append("files", f));

                      const res = await api.post("/receipt-scanV3", fd, {
                        headers: {
                          "Content-Type": "multipart/form-data",
                          Accept: "application/json",
                        },
                        validateStatus: () => true,
                      });

                      if (res.status !== 200) {
                        throw new Error(res.data?.message || `저장 실패(code: ${res.status})`);
                      }
                    } else {
                      // ✅ 파일 변경 없음: 기존 receipt_image 유지 + JSON 저장
                      const main = {
                        sale_id: r.sale_id || orig.sale_id || "",
                        account_id: submitAccountId,
                        payment_dt: paymentDt,
                        type: 1000,
                        use_name: r.use_name || orig.use_name || "",
                        bizNo,
                        total: parseNumber(
                          r.total === "" || r.total == null ? orig.total : r.total
                        ),
                        vat,
                        taxFree,
                        tax,
                        totalCard,
                        cardNo,
                        cardBrand,
                        receipt_image: receiptImage,
                        note,
                        user_id: localUserId,
                        receipt_type: r.receipt_type || orig.receipt_type || "UNKNOWN",
                        idx: cardIdx || r.idx || orig.idx || "",
                      };

                      const res = await api.post(
                        "/Account/AccountCorporateCardPaymentAllSave",
                        { main: [main], item: [] },
                        {
                          headers: { "Content-Type": "application/json" },
                          validateStatus: () => true,
                        }
                      );

                      if (!(res.data?.code === 200 || res.status === 200)) {
                        throw new Error(res.data?.message || `저장 실패(code: ${res.status})`);
                      }
                    }

                    Swal.update?.({ text: `${i + 1} / ${changed.length}` });
                  }

                  Swal.close();
                  Swal.fire("완료", "저장되었습니다.", "success");
                  // 소모품 예산 누계 갱신 (백그라운드 호출)

                  await fetchDataRows?.(selectedAccountId, year, month);
                  await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
                  await fetchBudgetGrant?.(selectedAccountId, year, month);
                  await fetchBudget2Grant?.(selectedAccountId, year, month);
                  setOriginalRows([]);
                  setOriginal2Rows([]);

                  // 파일 blob 정리
                  Object.values(cardRowFiles || {}).forEach((v) => {
                    if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
                      URL.revokeObjectURL(v.previewUrl);
                  });
                  setCardRowFiles({});
                  closeModalWithPreview(setCardListOpen);
                } catch (e) {
                  Swal.close();
                  Swal.fire("오류", e.message || "저장 중 오류", "error");
                }
              }}
            >
              저장(변경분 일괄)
            </MDButton>

            <MDButton
              variant="contained"
              color="warning"
              onClick={() => {
                Object.values(cardRowFiles || {}).forEach((v) => {
                  if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
                    URL.revokeObjectURL(v.previewUrl);
                });
                setCardRowFiles({});
                closeModalWithPreview(setCardListOpen);
              }}
            >
              닫기
            </MDButton>
          </Box>

          <Box mt={1} sx={{ fontSize: 11, color: "#777", textAlign: "center" }}>
            ※ 목록 전체를 직접 수정하고, 변경된 행만 저장됩니다.
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (1008) 현금/카드 목록 모달: 테이블에서 직접 수정 + 저장 ======================== */}
      <Modal
        open={cashListOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => {
          Object.values(cashRowFiles || {}).forEach((v) => {
            if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
              URL.revokeObjectURL(v.previewUrl);
          });
          setCashRowFiles({});
          closeModalWithPreview(setCashListOpen);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 980,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            fontSize: 12,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            결제 목록 (개인결제, 전체 편집)
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            날짜: {cashContext.dateStr}
          </Typography>

          <Box sx={{ maxHeight: 360, overflow: "auto", border: "1px solid #ddd" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f0f0f0" }}>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>사용처</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 110 }}>금액</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 120 }}>결제수단</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 140 }}>현금영수증</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 140 }}>구매자</th>
                  <th style={{ border: "1px solid #ddd", padding: 6, width: 220 }}>영수증</th>
                </tr>
              </thead>

              <tbody>
                {(cashEditRows || []).map((r, idx) => {
                  const rowKey = String(r.id ?? r.sale_id ?? idx);
                  const orig = cashOrigRowsForDiff?.[idx] || {};
                  const fileInfo = cashRowFiles?.[rowKey];
                  const previewSrc = fileInfo?.previewUrl || toPreviewUrl(r.receipt_image);

                  const payType = String(r.payType ?? "1");

                  return (
                    <tr key={rowKey}>
                      <td style={{ border: "1px solid #ddd", padding: 6 }}>
                        <TextField
                          size="small"
                          value={r.use_name || ""}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) =>
                            setCashEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, use_name: e.target.value } : x
                              )
                            )
                          }
                          fullWidth
                          sx={{ "& input": getCellStyleByCompare(orig.use_name, r.use_name) }}
                        />
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "right" }}>
                        <TextField
                          size="small"
                          value={r.total ?? ""}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9-]/g, "");
                            setCashEditRows((prev) =>
                              prev.map((x, i) => (i === idx ? { ...x, total: v } : x))
                            );
                          }}
                          fullWidth
                          sx={{
                            "& input": getCellStyleByCompare(
                              parseNumber(orig.total),
                              parseNumber(r.total)
                            ),
                          }}
                        />
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Select
                          size="small"
                          value={payType}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) => {
                            const v = String(e.target.value);
                            setCashEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx
                                  ? {
                                    ...x,
                                    payType: v,
                                    // 카드로 바꾸면 cash_receipt_type 의미 없으니 기본값만 유지
                                    cash_receipt_type:
                                      v === "1" ? String(x.cash_receipt_type ?? "3") : "3",
                                  }
                                  : x
                              )
                            );
                          }}
                          fullWidth
                          sx={{
                            height: "39px",
                            "& .MuiSelect-select": getCellStyleByCompare(
                              String(orig.payType ?? "1"),
                              payType
                            ),
                          }}
                        >
                          <MenuItem value="1">현금</MenuItem>
                          <MenuItem value="2">카드</MenuItem>
                        </Select>
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Select
                          size="small"
                          value={String(r.cash_receipt_type ?? "3")}
                          disabled={payType !== "1"}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) =>
                            setCashEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, cash_receipt_type: String(e.target.value) } : x
                              )
                            )
                          }
                          fullWidth
                          sx={{
                            height: "39px",
                            "& .MuiSelect-select": getCellStyleByCompare(
                              String(orig.cash_receipt_type ?? "3"),
                              String(r.cash_receipt_type ?? "3")
                            ),
                          }}
                        >
                          <MenuItem value="1">개인소득공제</MenuItem>
                          <MenuItem value="2">사업자지출증빙</MenuItem>
                          <MenuItem value="3">미발급</MenuItem>
                        </Select>
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6 }}>
                        <TextField
                          size="small"
                          value={r.buyer || ""}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) =>
                            setCashEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, buyer: e.target.value } : x
                              )
                            )
                          }
                          fullWidth
                          sx={{ "& input": getCellStyleByCompare(orig.buyer, r.buyer) }}
                        />
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <MDButton
                            component="label"
                            variant="contained"
                            color="info"
                            size="small"
                            sx={{ color: "#fff !important" }}
                          >
                            파일
                            <input
                              type="file"
                              hidden
                              accept="image/*,application/pdf"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                closeFloatingPreview();
                                setCashRowFiles((prev) => {
                                  const old = prev?.[rowKey];
                                  if (
                                    old?.previewUrl &&
                                    String(old.previewUrl).startsWith("blob:")
                                  ) {
                                    URL.revokeObjectURL(old.previewUrl);
                                  }
                                  return {
                                    ...prev,
                                    [rowKey]: { file, previewUrl: URL.createObjectURL(file) },
                                  };
                                });
                                e.target.value = "";
                              }}
                            />
                          </MDButton>

                          {previewSrc && (
                            <MDButton
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                openFloatingPreview(
                                  fileInfo?.file || previewSrc,
                                  "(1008) 영수증 미리보기"
                                );
                              }}
                            >
                              미리보기
                            </MDButton>
                          )}
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={async () => {
                const changed = (cashEditRows || [])
                  .map((r, idx) => {
                    const orig = cashOrigRowsForDiff?.[idx] || {};
                    const rowKey = String(r.id ?? r.sale_id ?? idx);

                    const fieldChanged =
                      normalizeText(r.use_name) !== normalizeText(orig.use_name) ||
                      parseNumber(r.total) !== parseNumber(orig.total) ||
                      String(r.payType ?? "1") !== String(orig.payType ?? "1") ||
                      String(r.cash_receipt_type ?? "3") !==
                      String(orig.cash_receipt_type ?? "3") ||
                      normalizeText(r.buyer) !== normalizeText(orig.buyer);

                    const hasFile = !!cashRowFiles?.[rowKey]?.file;
                    return fieldChanged || hasFile ? { r, idx, rowKey } : null;
                  })
                  .filter(Boolean);

                if (!changed.length) {
                  Swal.fire("정보", "변경된 내용이 없습니다.", "info");
                  return;
                }

                try {
                  Swal.fire({
                    title: "저장 중 입니다.",
                    text: `0 / ${changed.length}`,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    customClass: SWAL_LOADING_CUSTOM_CLASS,
                    willOpen: ensureSwalLoadingStyle,
                    didOpen: showSwalLoadingWithoutConfirm,
                  });

                  for (let i = 0; i < changed.length; i++) {
                    const { r, rowKey, idx } = changed[i];
                    const orig = cashOrigRowsForDiff?.[idx] || {};

                    const y = cashContext.isSecond ? prevYear : year;
                    const m = cashContext.isSecond ? prevMonth : month;
                    const fixedCellDate = buildCellDate(y, m, cashContext.dayIndex ?? 0);

                    const fd = new FormData();
                    fd.append("user_id", localUserId);

                    const submitAccountId = String(
                      r.account_id ?? orig.account_id ?? selectedAccountId ?? ""
                    );
                    fd.append("account_id", submitAccountId);
                    fd.append("row_account_id", submitAccountId);

                    fd.append("cell_day", String((cashContext.dayIndex ?? 0) + 1));
                    fd.append("saleDate", fixedCellDate);

                    fd.append("type", 1008);

                    fd.append("payType", String(r.payType ?? "1"));
                    fd.append("cash_receipt_type", String(r.cash_receipt_type ?? "3"));

                    fd.append("use_name", r.use_name || "");
                    fd.append("receipt_type", r.receipt_type || "UNKNOWN");
                    fd.append("buyer", r.buyer || "");
                    fd.append("total", parseNumber(r.total));

                    if (r.id != null) fd.append("id", r.id);
                    if (r.sale_id || orig.sale_id)
                      fd.append("sale_id", String(r.sale_id || orig.sale_id));

                    const file = cashRowFiles?.[rowKey]?.file;
                    if (file) fd.append("file", file);
                    const receiptImage = r.receipt_image || orig.receipt_image || "";
                    if (receiptImage) fd.append("receipt_image", receiptImage);

                    const res = await api.post("/Account/AccountTallyToPurchaseSave", fd, {
                      headers: {
                        "Content-Type": "multipart/form-data",
                        Accept: "application/json",
                      },
                      validateStatus: () => true,
                    });

                    if (res.status !== 200) {
                      throw new Error(res.data?.message || `저장 실패(code: ${res.status})`);
                    }

                    Swal.update?.({ text: `${i + 1} / ${changed.length}` });
                  }

                  Swal.close();
                  Swal.fire("완료", "저장되었습니다.", "success");
                  // 소모품 예산 누계 갱신 (백그라운드 호출)

                  await fetchDataRows?.(selectedAccountId, year, month);
                  await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
                  await fetchBudgetGrant?.(selectedAccountId, year, month);
                  await fetchBudget2Grant?.(selectedAccountId, year, month);
                  setOriginalRows([]);
                  setOriginal2Rows([]);

                  Object.values(cashRowFiles || {}).forEach((v) => {
                    if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
                      URL.revokeObjectURL(v.previewUrl);
                  });
                  setCashRowFiles({});
                  closeModalWithPreview(setCashListOpen);
                } catch (e) {
                  Swal.close();
                  Swal.fire("오류", e.message || "저장 중 오류", "error");
                }
              }}
            >
              저장(변경분 일괄)
            </MDButton>

            <MDButton
              variant="contained"
              color="warning"
              onClick={() => {
                Object.values(cashRowFiles || {}).forEach((v) => {
                  if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
                    URL.revokeObjectURL(v.previewUrl);
                });
                setCashRowFiles({});
                closeModalWithPreview(setCashListOpen);
              }}
            >
              닫기
            </MDButton>
          </Box>

          <Box mt={1} sx={{ fontSize: 11, color: "#777", textAlign: "center" }}>
            ※ 목록 전체를 직접 수정하고, 변경된 행만 저장됩니다. (등록 endpoint 사용):{" "}
            {ENDPOINT_CASH_SAVE}
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (기타 type) 목록 모달: 테이블에서 직접 수정 + 저장 ======================== */}
      <Modal
        open={otherListOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => {
          Object.values(otherRowFiles || {}).forEach((v) => {
            if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
              URL.revokeObjectURL(v.previewUrl);
          });
          setOtherRowFiles({});
          closeModalWithPreview(setOtherListOpen);
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 980,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            fontSize: 12,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            결제 목록 ({otherContext.rowName}, 전체 편집)
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            날짜: {otherContext.dateStr}
          </Typography>

          <Box sx={{ maxHeight: 360, overflow: "auto", border: "1px solid #ddd" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "27%" }} />
                <col style={{ width: "12%" }} />
                {(String(otherContext.type) === "1002" || String(otherContext.type) === "1003") && (
                  <col style={{ width: "25%" }} />
                )}
                <col style={{ width: "17%" }} />
                <col style={{ width: String(otherContext.type) === "1002" || String(otherContext.type) === "1003" ? "19%" : "44%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#f0f0f0" }}>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>사용처</th>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>금액</th>
                  {(String(otherContext.type) === "1002" || String(otherContext.type) === "1003") && (
                    <th style={{ border: "1px solid #ddd", padding: 6 }}>카드번호</th>
                  )}
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>영수증 타입</th>
                  <th style={{ border: "1px solid #ddd", padding: 6 }}>영수증</th>
                </tr>
              </thead>

              <tbody>
                {(otherEditRows || []).map((r, idx) => {
                  const rowKey = String(r.id ?? r.sale_id ?? idx);
                  const orig = otherOrigRowsForDiff?.[idx] || {};
                  const fileInfo = otherRowFiles?.[rowKey];
                  const isCorpCardPreviewRow =
                    String(otherContext.type || r.type || "") === "1002" ||
                    String(otherContext.type || r.type || "") === "1003";
                  // 1002/1003은 영수증 이미지 1장만 사용하는 목록 미리보기
                  const rowPreviewSources =
                    fileInfo?.file
                      ? [fileInfo.file]
                      : (isCorpCardPreviewRow
                        ? [
                          r.receipt_image,
                          r.receiptImage,
                          orig.receipt_image,
                          orig.receiptImage,
                        ]
                        : [
                          r.receipt_image,
                          r.receiptImage,
                          r.receipt_image2,
                          r.receiptImage2,
                          r.receipt_image_2,
                          r.receipt_image3,
                          r.receiptImage3,
                          r.receipt_image_3,
                          orig.receipt_image,
                          orig.receiptImage,
                          orig.receipt_image2,
                          orig.receiptImage2,
                          orig.receipt_image_2,
                          orig.receipt_image3,
                          orig.receiptImage3,
                          orig.receipt_image_3,
                        ])
                        .map((v) => String(v || "").trim())
                        .filter(Boolean)
                        .filter((v, i, arr) => arr.indexOf(v) === i);

                  return (
                    <tr key={rowKey}>
                      <td style={{ border: "1px solid #ddd", padding: 6 }}>
                        <TextField
                          size="small"
                          value={r.use_name || ""}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) =>
                            setOtherEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, use_name: e.target.value } : x
                              )
                            )
                          }
                          fullWidth
                          sx={{ "& input": getCellStyleByCompare(orig.use_name, r.use_name) }}
                        />
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "right" }}>
                        <TextField
                          size="small"
                          value={r.total ?? ""}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9-]/g, "");
                            setOtherEditRows((prev) =>
                              prev.map((x, i) => (i === idx ? { ...x, total: v } : x))
                            );
                          }}
                          fullWidth
                          sx={{
                            "& input": getCellStyleByCompare(
                              parseNumber(orig.total),
                              parseNumber(r.total)
                            ),
                          }}
                        />
                      </td>

                      {(String(otherContext.type) === "1002" || String(otherContext.type) === "1003") && (
                        <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                          <Select
                            size="small"
                            value={String(r.card_idx ?? r.corp_card_idx ?? "")}
                            onClick={stopRowClick}
                            onMouseDown={stopRowClick}
                            onChange={(e) => {
                              const v = e.target.value;
                              const picked = (headOfficeCardList || []).find((c) => String(c.idx) === v);
                              setOtherEditRows((prev) =>
                                prev.map((x, i) =>
                                  i === idx ? { ...x, card_idx: v, card_no: picked?.card_no || x.card_no || "", card_brand: picked?.card_brand || x.card_brand || "" } : x
                                )
                              );
                            }}
                            fullWidth
                            displayEmpty
                            sx={{ height: 40 }}
                          >
                            <MenuItem value=""><em>카드 선택</em></MenuItem>
                            {(headOfficeCardList || []).map((c) => (
                              <MenuItem key={String(c.idx)} value={String(c.idx)}>
                                {c.card_brand || "카드"} / {maskCardNoFull(c.card_no)}
                              </MenuItem>
                            ))}
                          </Select>
                        </td>
                      )}

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Select
                          size="small"
                          value={r.receipt_type || "UNKNOWN"}
                          onClick={stopRowClick}
                          onMouseDown={stopRowClick}
                          onChange={(e) =>
                            setOtherEditRows((prev) =>
                              prev.map((x, i) =>
                                i === idx ? { ...x, receipt_type: e.target.value } : x
                              )
                            )
                          }
                          fullWidth
                          sx={{
                            height: "39px",
                            "& .MuiSelect-select": getCellStyleByCompare(
                              orig.receipt_type,
                              r.receipt_type
                            ),
                          }}
                        >
                          {(String(otherContext.type) === "1002" || String(otherContext.type) === "1003") ? [
                            <MenuItem key="UNKNOWN" value="UNKNOWN" disabled><em>영수증 타입 선택</em></MenuItem>,
                            <MenuItem key="coupang" value="coupang">쿠팡</MenuItem>,
                            <MenuItem key="gmarket" value="gmarket">G마켓</MenuItem>,
                            <MenuItem key="11post" value="11post">11번가</MenuItem>,
                            <MenuItem key="naver" value="naver">네이버</MenuItem>,
                            <MenuItem key="homeplus" value="homeplus">홈플러스</MenuItem>,
                            <MenuItem key="auction" value="auction">옥션</MenuItem>,
                            <MenuItem key="daiso" value="daiso">다이소</MenuItem>,
                            <MenuItem key="MART_ITEMIZED" value="MART_ITEMIZED">마트</MenuItem>,
                            <MenuItem key="CONVENIENCE" value="CONVENIENCE">편의점</MenuItem>,
                          ] : [
                            <MenuItem key="UNKNOWN" value="UNKNOWN" disabled><em>영수증 타입 선택</em></MenuItem>,
                            <MenuItem key="TRANSACTION" value="TRANSACTION">거래명세표(서)</MenuItem>,
                            <MenuItem key="MART_ITEMIZED" value="MART_ITEMIZED">마트</MenuItem>,
                          ]}
                        </Select>
                      </td>

                      <td style={{ border: "1px solid #ddd", padding: 6, textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                            flexWrap: "nowrap",
                          }}
                        >
                          <Button
                            component="label"
                            size="small"
                            variant="contained"
                            sx={{ color: "#fff !important", whiteSpace: "nowrap" }}
                          >
                            파일
                            <input
                              type="file"
                              hidden
                              accept="image/*,application/pdf"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                closeFloatingPreview();
                                setOtherRowFiles((prev) => {
                                  const old = prev?.[rowKey];
                                  if (
                                    old?.previewUrl &&
                                    String(old.previewUrl).startsWith("blob:")
                                  ) {
                                    URL.revokeObjectURL(old.previewUrl);
                                  }
                                  return {
                                    ...prev,
                                    [rowKey]: { file, previewUrl: URL.createObjectURL(file) },
                                  };
                                });
                                e.target.value = "";
                              }}
                            />
                          </Button>

                          {rowPreviewSources.length > 0 && (
                            <MDButton
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                openFloatingPreview(
                                  rowPreviewSources,
                                  "(기타) 영수증 미리보기"
                                );
                              }}
                            >
                              미리보기
                            </MDButton>
                          )}
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>

          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={async () => {
                const changed = (otherEditRows || [])
                  .map((r, idx) => {
                    const orig = otherOrigRowsForDiff?.[idx] || {};
                    const rowKey = String(r.id ?? r.sale_id ?? idx);

                    const fieldChanged =
                      normalizeText(r.use_name) !== normalizeText(orig.use_name) ||
                      parseNumber(r.total) !== parseNumber(orig.total) ||
                      String(r.receipt_type ?? "UNKNOWN") !==
                      String(orig.receipt_type ?? "UNKNOWN");

                    const hasFile = !!otherRowFiles?.[rowKey]?.file;

                    return fieldChanged || hasFile ? { r, idx, rowKey } : null;
                  })
                  .filter(Boolean);

                if (!changed.length) {
                  Swal.fire("정보", "변경된 내용이 없습니다.", "info");
                  return;
                }

                try {
                  Swal.fire({
                    title: "저장 중 입니다.",
                    text: `0 / ${changed.length}`,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    customClass: SWAL_LOADING_CUSTOM_CLASS,
                    willOpen: ensureSwalLoadingStyle,
                    didOpen: showSwalLoadingWithoutConfirm,
                  });

                  for (let i = 0; i < changed.length; i++) {
                    const { r, rowKey, idx } = changed[i];
                    const orig = otherOrigRowsForDiff?.[idx] || {};

                    const y = otherContext.isSecond ? prevYear : year;
                    const m = otherContext.isSecond ? prevMonth : month;
                    const fixedCellDate = buildCellDate(y, m, otherContext.dayIndex ?? 0);

                    const fd = new FormData();
                    fd.append("user_id", localUserId);

                    const submitAccountId = String(
                      r.account_id ?? orig.account_id ?? selectedAccountId ?? ""
                    );
                    fd.append("account_id", submitAccountId);
                    fd.append("row_account_id", submitAccountId);

                    fd.append("cell_day", String((otherContext.dayIndex ?? 0) + 1));
                    fd.append("saleDate", fixedCellDate);

                    fd.append("type", String(otherContext.type || r.type || ""));
                    fd.append("use_name", r.use_name || "");
                    fd.append("receipt_type", r.receipt_type || "UNKNOWN");
                    fd.append("total", parseNumber(r.total));

                    const rowType = String(otherContext.type || r.type || "");
                    const isCorpCardRow = rowType === "1002" || rowType === "1003";
                    const corpCardSaleId = isCorpCardRow ? getCorpCardSaleId(r, orig) : "";

                    if (r.id != null) fd.append("id", r.id);
                    if (isCorpCardRow && corpCardSaleId) {
                      // 법인카드 영수증은 /image/acnCorporate/{sale_id}/... 경로를 유지
                      fd.append("sale_id", corpCardSaleId);
                    } else if (r.sale_id || orig.sale_id) {
                      fd.append("sale_id", String(r.sale_id || orig.sale_id));
                    }

                    const file = otherRowFiles?.[rowKey]?.file;
                    if (file) fd.append("file", file);
                    const receiptImage = r.receipt_image || orig.receipt_image || "";
                    const receiptImage2 =
                      r.receipt_image2 ||
                      r.receiptImage2 ||
                      r.receipt_image_2 ||
                      orig.receipt_image2 ||
                      orig.receiptImage2 ||
                      orig.receipt_image_2 ||
                      "";
                    const receiptImage3 =
                      r.receipt_image3 ||
                      r.receiptImage3 ||
                      r.receipt_image_3 ||
                      orig.receipt_image3 ||
                      orig.receiptImage3 ||
                      orig.receipt_image_3 ||
                      "";
                    if (receiptImage) fd.append("receipt_image", receiptImage);
                    if (receiptImage2) fd.append("receipt_image2", receiptImage2);
                    if (receiptImage3) fd.append("receipt_image3", receiptImage3);

                    if (isCorpCardRow) {
                      const saleId = corpCardSaleId;

                      if (file) {
                        const rowReceiptType = String(r.receipt_type || "UNKNOWN");
                        if (rowReceiptType === "UNKNOWN") {
                          throw new Error("영수증 타입을 선택해주세요.");
                        }
                        // 1002/1003 목록 수정에서 선택한 영수증 파일을 실제 저장 경로로 업로드
                        fd.set("type", rowReceiptType);
                        fd.set("receipt_type", rowReceiptType);
                        fd.append("tallyType", rowType);
                        fd.append("receiptType", rowReceiptType);
                        fd.append("objectValue", submitAccountId);
                        fd.append("folderValue", "acnCorporate");
                        fd.append("cardNo", r.card_no || orig.card_no || r.cardNo || orig.cardNo || "");
                        fd.append("cardBrand", r.card_brand || orig.card_brand || r.cardBrand || orig.cardBrand || "");
                        fd.append("saveType", "headoffice");
                        fd.append("cell_date", fixedCellDate);

                        const uploadRes = await api.post(ENDPOINT_CORP_CARD_SAVE, fd, {
                          headers: {
                            "Content-Type": "multipart/form-data",
                            Accept: "application/json",
                          },
                          validateStatus: () => true,
                        });
                        if (uploadRes.status !== 200) {
                          throw new Error(uploadRes.data?.message || `저장 실패(code: ${uploadRes.status})`);
                        }
                      } else {
                        // 파일 변경이 없을 때는 기존 상품명은 유지하고 금액 기준으로 수량과 단가를 맞춤
                        const detailRes = await api.get("/Account/HeadOfficeCorporateCardPaymentDetailList", {
                          params: { sale_id: saleId },
                          validateStatus: () => true,
                        });
                        const detailList = Array.isArray(detailRes.data) ? detailRes.data : [];
                        if (detailList.length > 1) {
                          throw new Error("상세 내역이 2건 이상입니다. 본사에 문의해 주세요.");
                        }
                        const existingDetail = detailList[0] || null;
                        const rowAmount = parseNumber(r.total);
                        const res = await api.post("/Account/HeadOfficeCorporateCardPaymentAllSave", {
                          main: [{
                            sale_id: saleId,
                            account_id: submitAccountId,
                            payment_dt: fixedCellDate,
                            use_name: r.use_name || "",
                            user_id: localUserId,
                          }],
                          item: [{
                            idx: existingDetail?.idx ?? null,
                            sale_id: saleId,
                            account_id: submitAccountId,
                            name: existingDetail?.name ?? "",
                            qty: 1,
                            unitPrice: rowAmount,
                            amount: rowAmount,
                            taxType: existingDetail?.taxType ?? 3,
                            itemType: rowType === "1003" ? 1 : 2,
                            type: Number(rowType),
                            payment_dt: fixedCellDate,
                          }],
                        });
                        if (res.status !== 200) {
                          throw new Error(res.data?.message || `저장 실패(code: ${res.status})`);
                        }
                      }
                    } else {
                      const res = await api.post("/Account/AccountTallyToPurchaseSave", fd, {
                        headers: {
                          "Content-Type": "multipart/form-data",
                          Accept: "application/json",
                        },
                        validateStatus: () => true,
                      });
                      if (res.status !== 200) {
                        throw new Error(res.data?.message || `저장 실패(code: ${res.status})`);
                      }
                    }

                    Swal.update?.({ text: `${i + 1} / ${changed.length}` });
                  }

                  Swal.close();
                  Swal.fire("완료", "저장되었습니다.", "success");
                  // 소모품 예산 누계 갱신 (백그라운드 호출)

                  await fetchDataRows?.(selectedAccountId, year, month);
                  await fetchData2Rows?.(selectedAccountId, prevYear, prevMonth);
                  await fetchBudgetGrant?.(selectedAccountId, year, month);
                  await fetchBudget2Grant?.(selectedAccountId, year, month);
                  setOriginalRows([]);
                  setOriginal2Rows([]);

                  Object.values(otherRowFiles || {}).forEach((v) => {
                    if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
                      URL.revokeObjectURL(v.previewUrl);
                  });
                  setOtherRowFiles({});
                  closeModalWithPreview(setOtherListOpen);
                } catch (e) {
                  Swal.close();
                  Swal.fire("오류", e.message || "저장 중 오류", "error");
                }
              }}
            >
              저장(변경분 일괄)
            </MDButton>

            <MDButton
              variant="contained"
              color="warning"
              onClick={() => {
                Object.values(otherRowFiles || {}).forEach((v) => {
                  if (v?.previewUrl && String(v.previewUrl).startsWith("blob:"))
                    URL.revokeObjectURL(v.previewUrl);
                });
                setOtherRowFiles({});
                closeModalWithPreview(setOtherListOpen);
              }}
            >
              닫기
            </MDButton>
          </Box>

          <Box mt={1} sx={{ fontSize: 11, color: "#777", textAlign: "center" }}>
            ※ 목록 전체를 직접 수정하고, 변경된 행만 저장됩니다.
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (1000) 법인카드 등록 모달 ======================== */}
      <Modal
        open={cardCreateOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => closeModalWithPreview(setCardCreateOpen)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 520,
            maxWidth: 520,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            (법인카드) 결제 등록 (type=1000)
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            거래처명: {selectedAccountOption?.account_name || "-"} / 날짜: {cardContext.dateStr}
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                label="사용처"
                size="small"
                value={cardForm.use_name || ""}
                onChange={(e) => setCardForm((p) => ({ ...p, use_name: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="금액"
                size="small"
                value={cardForm.total || ""}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9-]/g, "");
                  setCardForm((p) => ({ ...p, total: v }));
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Select
                size="small"
                value={cardForm.card_idx || ""}
                onChange={(e) => {
                  const v = String(e.target.value || "");
                  const picked = getCorpCardByIdx(v);
                  setCardForm((p) => ({
                    ...p,
                    card_idx: v,
                    card_brand: picked?.card_brand || p.card_brand || "",
                    card_no: picked?.card_no || p.card_no || "",
                  }));
                }}
                fullWidth
                displayEmpty
                sx={{ height: 40 }}
              >
                <MenuItem value="">
                  <em>카드 선택</em>
                </MenuItem>
                {(corpCardList || []).map((c) => (
                  <MenuItem key={String(c.idx)} value={String(c.idx)}>
                    {c.card_brand || "카드"} / {maskCardNo(c.card_no)} / idx={String(c.idx)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12}>
              <Select
                size="small"
                value={cardForm.receipt_type || "UNKNOWN"}
                onChange={(e) => setCardForm((p) => ({ ...p, receipt_type: e.target.value }))}
                fullWidth
                sx={{ height: 40 }}
              >
                <MenuItem value="UNKNOWN" disabled>
                  <em>영수증 타입 선택</em>
                </MenuItem>
                <MenuItem value="CARD_SLIP_GENERIC">카드전표</MenuItem>
                <MenuItem value="MART_ITEMIZED">마트</MenuItem>
                <MenuItem value="CONVENIENCE">편의점</MenuItem>
                {/*<MenuItem value="COUPANG_CARD">쿠팡</MenuItem>*/}
                <MenuItem value="COUPANG_APP">배달앱</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <MDButton component="label" variant="contained" color="info" size="small" sx={{ color: "#fff !important" }}>
                  영수증 파일 선택
                  <input
                    ref={cardFileRef}
                    type="file"
                    hidden
                    accept="image/*,application/pdf"
                    onClick={(e) => {
                      e.currentTarget.value = "";
                    }}
                    onChange={handleCardReceiptFileChange}
                  />
                </MDButton>

                {(cardReceiptPreview || cardForm.receipt_image) && (
                  <MDButton
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() =>
                      openFloatingPreview(
                        cardForm.receipt_image || cardReceiptPreview,
                        "(법인카드) 영수증 미리보기"
                      )
                    }
                  >
                    미리보기
                  </MDButton>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={async () => {
                const ok = await saveCorpCardPayment("create");
                if (ok) closeModalWithPreview(setCardCreateOpen);
              }}
            >
              등록
            </MDButton>
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => closeModalWithPreview(setCardCreateOpen)}
            >
              취소
            </MDButton>
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (1008) 선택 모달 (0원 아니면 뜨는 모달) ======================== */}
      <Modal
        open={cashChoiceOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => closeModalWithPreview(setCashChoiceOpen)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 420,
            maxWidth: 420,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            개인 결제 입력
          </Typography>
          <Typography sx={{ fontSize: 13, mb: 2 }}>
            이미 입력된 금액이 있습니다.
            <br />
            등록 / 수정을 선택하세요.
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            거래처명: {selectedAccountOption?.account_name || "-"} / 날짜: {cashContext.dateStr}
          </Typography>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="contained" color="info" onClick={openCashCreateFromChoice}>
              등록
            </MDButton>
            <MDButton variant="contained" color="primary" onClick={openCashListFromChoice}>
              수정
            </MDButton>
            <MDButton variant="outlined" onClick={() => closeModalWithPreview(setCashChoiceOpen)}>
              취소
            </MDButton>
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (1008) 등록 모달 ======================== */}
      <Modal
        open={cashCreateOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => closeModalWithPreview(setCashCreateOpen)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 520,
            maxWidth: 520,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            개인 결제 등록 (type=1008)
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            거래처명: {selectedAccountOption?.account_name || "-"} / 날짜: {cashContext.dateStr}
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                label="사용처"
                size="small"
                value={cashForm.use_name || ""}
                onChange={(e) => setCashForm((p) => ({ ...p, use_name: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="금액"
                size="small"
                value={cashForm.total || ""}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9-]/g, "");
                  setCashForm((p) => ({ ...p, total: v }));
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Select
                size="small"
                value={cashForm.payType || "1"}
                onChange={(e) =>
                  setCashForm((p) => ({
                    ...p,
                    payType: String(e.target.value),
                    cash_receipt_type:
                      String(e.target.value) === "1" ? String(p.cash_receipt_type ?? "3") : "3",
                  }))
                }
                fullWidth
                sx={{ height: "40px" }}
              >
                <MenuItem value="1">현금</MenuItem>
                <MenuItem value="2">카드</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12}>
              <Select
                size="small"
                value={cashForm.cash_receipt_type || "3"}
                disabled={String(cashForm.payType) !== "1"}
                onChange={(e) =>
                  setCashForm((p) => ({
                    ...p,
                    cash_receipt_type: String(e.target.value),
                  }))
                }
                fullWidth
                sx={{ height: "40px" }}
              >
                <MenuItem value="1">개인소득공제</MenuItem>
                <MenuItem value="2">사업자지출증빙</MenuItem>
                <MenuItem value="3">미발급</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="구매자"
                size="small"
                value={cashForm.buyer || ""}
                onChange={(e) => setCashForm((p) => ({ ...p, buyer: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <MDButton component="label" variant="contained" color="info" size="small" sx={{ color: "#fff !important" }}>
                  영수증 파일 선택
                  <input
                    ref={cashFileRef}
                    type="file"
                    hidden
                    accept="image/*,application/pdf"
                    onClick={(e) => {
                      e.currentTarget.value = "";
                    }}
                    onChange={handleCashReceiptFileChange}
                  />
                </MDButton>

                {cashReceiptPreview && (
                  <MDButton
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() =>
                      openFloatingPreview(
                        cashForm.receipt_image || cashReceiptPreview,
                        "(1008) 영수증 미리보기"
                      )
                    }
                  >
                    미리보기
                  </MDButton>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={async () => {
                const ok = await saveCashPayment("create");
                if (ok) closeModalWithPreview(setCashCreateOpen);
              }}
            >
              등록
            </MDButton>
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => closeModalWithPreview(setCashCreateOpen)}
            >
              취소
            </MDButton>
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (기타 type) 선택 모달 ======================== */}
      <Modal
        open={otherChoiceOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => closeModalWithPreview(setOtherChoiceOpen)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 420,
            maxWidth: 420,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            (기타) 결제 입력 (type={otherContext.type})
          </Typography>
          <Typography sx={{ fontSize: 13, mb: 2 }}>
            이미 입력된 금액이 있습니다.
            <br />
            등록 / 수정을 선택하세요.
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            거래처명: {selectedAccountOption?.account_name || "-"} / 날짜: {otherContext.dateStr}
          </Typography>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="contained" color="info" onClick={openOtherCreateFromChoice}>
              등록
            </MDButton>
            <MDButton variant="contained" color="primary" onClick={openOtherListFromChoice}>
              수정
            </MDButton>
            <MDButton variant="outlined" onClick={() => closeModalWithPreview(setOtherChoiceOpen)}>
              취소
            </MDButton>
          </Box>
        </Box>
      </Modal>

      {/* ======================== ✅ (기타 type) 등록 모달 ======================== */}
      <Modal
        open={otherCreateOpen}
        disableEscapeKeyDown={floatingPreview.open}
        onClose={() => closeModalWithPreview(setOtherCreateOpen)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobileOrTablet ? "92vw" : 520,
            maxWidth: 520,
            maxHeight: isMobileOrTablet ? "88vh" : "none",
            overflowY: isMobileOrTablet ? "auto" : "visible",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            (기타) 결제 등록 (type={otherForm.type || otherContext.type})
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#666", mb: 2 }}>
            거래처명: {selectedAccountOption?.account_name || "-"} / 날짜: {otherContext.dateStr}
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                label="사용처"
                size="small"
                value={otherForm.use_name || ""}
                onChange={(e) => setOtherForm((p) => ({ ...p, use_name: e.target.value }))}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="금액"
                size="small"
                value={otherForm.total || ""}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9-]/g, "");
                  setOtherForm((p) => ({ ...p, total: v }));
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Select
                size="small"
                value={otherForm.receipt_type || "UNKNOWN"}
                onChange={(e) => setOtherForm((p) => ({ ...p, receipt_type: e.target.value }))}
                fullWidth
                sx={{ height: "40px" }}
              >
                {(String(otherForm.type || otherContext.type) === "1002" || String(otherForm.type || otherContext.type) === "1003") ? [
                  <MenuItem key="UNKNOWN" value="UNKNOWN" disabled><em>영수증 타입 선택</em></MenuItem>,
                  <MenuItem key="coupang" value="coupang">쿠팡</MenuItem>,
                  <MenuItem key="gmarket" value="gmarket">G마켓</MenuItem>,
                  <MenuItem key="11post" value="11post">11번가</MenuItem>,
                  <MenuItem key="naver" value="naver">네이버</MenuItem>,
                  <MenuItem key="homeplus" value="homeplus">홈플러스</MenuItem>,
                  <MenuItem key="auction" value="auction">옥션</MenuItem>,
                  <MenuItem key="daiso" value="daiso">다이소</MenuItem>,
                  <MenuItem key="MART_ITEMIZED" value="MART_ITEMIZED">마트</MenuItem>,
                  <MenuItem key="CONVENIENCE" value="CONVENIENCE">편의점</MenuItem>,
                ] : [
                  <MenuItem key="UNKNOWN" value="UNKNOWN" disabled><em>영수증 타입 선택</em></MenuItem>,
                  <MenuItem key="TRANSACTION" value="TRANSACTION">거래명세표(서)</MenuItem>,
                  <MenuItem key="MART_ITEMIZED" value="MART_ITEMIZED">마트</MenuItem>,
                ]}
              </Select>
            </Grid>

            {(String(otherForm.type || otherContext.type) === "1002" || String(otherForm.type || otherContext.type) === "1003") && (
              <Grid item xs={12}>
                <Select
                  size="small"
                  value={otherForm.card_idx || ""}
                  onChange={(e) => setOtherForm((p) => ({ ...p, card_idx: e.target.value }))}
                  fullWidth
                  displayEmpty
                  sx={{ height: 40 }}
                  disabled={headOfficeCardLoading}
                >
                  <MenuItem value="">
                    <em>카드 선택</em>
                  </MenuItem>
                  {(headOfficeCardList || []).map((c) => (
                    <MenuItem key={String(c.idx)} value={String(c.idx)}>
                      {c.card_brand || "카드"} / {maskCardNoFull(c.card_no)}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <MDButton component="label" variant="contained" color="info" size="small" sx={{ color: "#fff !important" }}>
                  영수증 파일 선택
                  <input
                    ref={otherFileRef}
                    type="file"
                    hidden
                    {...(String(otherForm.type || otherContext.type) === "1002" || String(otherForm.type || otherContext.type) === "1003" ? {} : { multiple: true })}
                    accept="image/*,application/pdf"
                    onClick={(e) => {
                      e.currentTarget.value = "";
                    }}
                    onChange={handleOtherReceiptFileChange}
                  />
                </MDButton>

                {otherReceiptPreview && (
                  <MDButton
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => {
                      const selectedFiles = Array.isArray(otherForm.receipt_files)
                        ? otherForm.receipt_files.filter(Boolean).slice(0, 3)
                        : [];
                      const previewSources = selectedFiles.length
                        ? selectedFiles
                        : [otherForm.receipt_image || otherReceiptPreview].filter(Boolean);
                      openFloatingPreview(previewSources, "(기타) 영수증 미리보기");
                    }}
                  >
                    미리보기
                  </MDButton>
                )}
              </Box>
              <Typography sx={{ mt: 0.75, fontSize: 11, color: "#666" }}>
                {(String(otherForm.type || otherContext.type) === "1002" || String(otherForm.type || otherContext.type) === "1003") ? "최대 1장 업로드 가능" : "최대 3장 업로드 가능"}
                {Array.isArray(otherForm.receipt_files) && otherForm.receipt_files.length > 0
                  ? ` / 선택 ${otherForm.receipt_files.length}장`
                  : ""}
              </Typography>
            </Grid>
          </Grid>

          <Box mt={2} display="flex" justifyContent="center" gap={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={async () => {
                const ok = await saveOtherPayment("create");
                if (ok) closeModalWithPreview(setOtherCreateOpen);
              }}
            >
              등록
            </MDButton>
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => closeModalWithPreview(setOtherCreateOpen)}
            >
              취소
            </MDButton>
          </Box>
        </Box>
      </Modal>
    </DashboardLayout>
  );
}

export default TallySheet;
